import './ClientConsole.css';
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ClientInstanceService from '../Services/InstanceService';
import ClientUserService from '../Services/UserService';
import EnterInstanceName from './Components/EnterInstanceName';
import LoadingInstanceData from './Components/LoadingInstanceData';
import IntegrationsHome from './ConsoleComponents/IntegrationsHome';
import IntegrationSymbol from './ConsoleComponents/IntegrationSymbol';
import {API_URL, SERVER_URL, GOOGLE_CLIENT_ID} from '../Constants';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import LogoAnimated from '../Refs/LogoAminated.gif';
import HeroBackground from '../Refs/HeroBackground.jpg';

import { FaArrowRight, FaEye, FaEyeSlash, FaHouse, FaBook, FaClockRotateLeft, FaClock, FaPhone, FaPlug, FaCreditCard, FaUsers, FaChevronLeft, FaChevronRight, FaGear, FaRightFromBracket, FaXmark, FaEnvelope, FaPencil, FaFloppyDisk, FaTrash, FaPlus, FaVialCircleCheck, FaCircleQuestion, FaCircleExclamation, FaCircleCheck, FaFileLines, FaLocationDot, FaUser, FaComment, FaCalendarDay, FaRotate, FaMinus, FaCheck } from "react-icons/fa6";
import { MdQueue } from "react-icons/md";

// - - - - - - - - - - - UTILITY FUNCTIONS - - - - - - - - - - - - 

// Phone number formatting utilities
const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Handle both 10-digit (##########) and 11-digit (+1##########) formats
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        // Strip country code and format the 10 digits
        const digits = cleaned.slice(1);
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (cleaned.length === 10) {
        // Format 10 digits
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
    return phoneNumber; // Return as-is if not recognized format
};

const parsePhoneNumber = (formattedPhone) => {
    if (!formattedPhone) return '';
    // Remove all non-digit characters
    const cleaned = formattedPhone.replace(/\D/g, '');
    
    // If it's 10 digits, add +1 country code for backend
    if (cleaned.length === 10) {
        return `+1${cleaned}`;
    }
    // If it already has country code (11 digits starting with 1), add +
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        return `+${cleaned}`;
    }
    // Return as-is for other cases
    return cleaned;
};

const formatPhoneInput = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');
    
    // Strip country code if present (1 at start for 11 digits)
    let digits = cleaned;
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        digits = cleaned.slice(1);
    }
    
    // Limit to 10 digits
    const limited = digits.slice(0, 10);
    
    // Format as user types
    if (limited.length <= 3) {
        return limited;
    } else if (limited.length <= 6) {
        return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    } else {
        return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
};

// - - - - - - - - - - - API INTERACTIONS - - - - - - - - - - - - 

// USERS
const loginUser = async (credentials, setSuccess, setFailure) => {
    try{
        const response = await ClientUserService.loginUser(credentials);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setFailure(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setFailure(`Error: ${error.message}`);
    };
};

const autoLoginUser = async (userID, accessToken, setSuccess, setFailure) => {
    try{
        const response = await ClientUserService.autoLoginUser(userID, accessToken);
        if(response.status >= 200 && response.status < 300) {
            console.log('Auto-login successful:', response.data);
            setSuccess(response.data);
        } else {
            setFailure(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setFailure(`Error: ${error.message}`);
    };
};

const checkUserEmail = async (email, accessToken, setSuccess, setFailure) => {
    try{
        const response = await ClientUserService.checkUserEmail(accessToken, email);
        if(response.status >= 200 && response.status < 300) {
            console.log('User email check successful:', response.status);
            setSuccess(response.data);
        } else {
            setFailure(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setFailure(`Error: ${error.message}`);
    };
};

const getTenantUsers = async (tenantID, accessToken, setSuccess, setFailure) => {
    try{
        const response = await ClientUserService.getTenantUsers(tenantID, accessToken);
        if(response.status >= 200 && response.status < 300) {
            console.log('Tenant users fetched:', response.data);
            setSuccess(response.data);
        } else {
            setFailure(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setFailure(`Error: ${error.message}`);
    };
};

const createUser = async (token, tenant, userData, setSuccess, setFailure) => {
    try{
        const response = await ClientUserService.createUser(token, tenant, userData);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setFailure(`Failed to add user. Try again and if the problem persists, contact support.`);
        };
    } catch(error) {
        setFailure(`Failed to add user. Try again and if the problem persists, contact support.`);
    };
};

const updateUserPermissions = async (token, tenant, permissions, setSuccess, setFailure) => {
    try{
        const response = await ClientUserService.updateUserPermissions(token, tenant, permissions);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setFailure(`Failed to update user permissions. Try again and if the problem persists, contact support.`);
        };
    } catch(error) {
        setFailure(`Failed to update user permissions. Try again and if the problem persists, contact support.`);
    };
};

const removeUser = async (token, tenant, userID, setSuccess, setFailure) => {
    console.log(tenant)
    try{
        const response = await ClientUserService.removeUser(token, tenant, userID);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setFailure(`Failed to remove user. Try again and if the problem persists, contact support.`);
        };
    } catch(error) {
        setFailure(`Failed to remove user. Try again and if the problem persists, contact support.`);
    };
};

// COMMUNICATION CHANNELS
const getTenantsCommunicationChannels = async (tenantID, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.getCommunicationChannels(tenantID, accessToken);
        if(response.status >= 200 && response.status < 300) {
            console.log('Communication channels fetched:', response.data);
            setSuccess(response.data);
            return response.data;
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const addCommunicationChannel = async (tenantID, channelData, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.addCommunicationChannel(tenantID, channelData, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const deleteCommunicationChannel = async (tenantID, channelID, type, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.deleteCommunicationChannel(tenantID, channelID, type, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const updateCommunicationChannel = async (tenantID, channelID, type, channelData, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.updateCommunicationChannel(tenantID, channelID, type, channelData, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const testCommunicationChannel = async (tenantID, channelID, type, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.testCommunicationChannel(tenantID, channelID, type, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            console.log(response.data)
            setError(`Error: TEST FAILED... This communication channel is not working properly. Please check the configuration, click Edit, make the required changes, and Save to retry.`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

// HISTORY
const getHistoryLogs = async (tenantID, filterObject, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.getHistoryLogs(tenantID, accessToken, filterObject);
        if(response.status >= 200 && response.status < 300) {
            console.log('History logs fetched:', response.data);
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const markHistoryFollowedUp = async (tenantID, historyId, userId, notes, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.markFollowedUp(tenantID, accessToken, historyId, userId, notes);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const markHistoryRequiringFollowUp = async (tenantID, historyId, userId, notes, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.markRequiringFollowUp(tenantID, accessToken, historyId, userId, notes);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

// CONTEXT
const getAllContext = async (tenantID, accessToken, setSuccess, setError) => {
    console.log('Fetching all context for tenant:', tenantID);
    try {
        const response = await ClientInstanceService.getTenantContext(tenantID, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const createContext = async (tenantID, contextData, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.createTenantContext(tenantID, contextData, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const updateContext = async (tenantID, contextID, contextData, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.updateTenantContext(tenantID, contextID, contextData, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const deleteContext = async (tenantID, contextID, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.deleteTenantContext(tenantID, contextID, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const checkContextLocation = async (tenantID, locationId, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.checkTenantContextLocation(tenantID, locationId, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const getContextLocation = async (tenantID, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.getTenantContextLocation(tenantID, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const createContextLocation = async (tenantID, locationData, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.createTenantContextLocation(tenantID, locationData, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const checkContextClient = async (tenantID, clientId, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.checkTenantContextClient(tenantID, clientId, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const getContextClient = async (tenantID, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.getTenantContextClient(tenantID, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const createContextClient = async (tenantID, clientData, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.createTenantContextClient(tenantID, clientData, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

// COMMUNICATION QUEUE
const getCommunicationQueue = async (tenantID, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.getCommunicationQueue(tenantID, accessToken);
        if(response.status >= 200 && response.status < 300) {
            console.log('Communication queue fetched:', response.data);
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const formatBulkQueueData = async (tenantID, accessToken, queueDataFile, setSuccess, setError) => {
    try{
        const response = await ClientInstanceService.formatBulkQueueData(tenantID, accessToken, queueDataFile);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const addToCommunicationQueue = async (tenantID, userId, queueArray, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.addToCommunicationQueue(tenantID, userId, queueArray, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const stopCommunicationQueue = async (tenantID, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.stopCommunicationQueue(tenantID, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const removeFromCommunicationQueue = async (tenantID, queue, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.removeFromCommunicationQueue(tenantID, queue, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const getCommunicationQueueHistory = async (tenantID, queueId, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.getCommunicationQueueHistory(tenantID, queueId, accessToken);
        if(response.status >= 200 && response.status < 300) {
            console.log('Communication queue history fetched:', response.data);
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

// INTEGRATIONS
const getAllIntegrations = async (accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.getAllIntegrations(accessToken);
        if(response.status >= 200 && response.status < 300) {
            console.log('All integrations fetched:', response.data);
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const getIntegrations = async (tenantID, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.getIntegrations(tenantID, accessToken);
        if(response.status >= 200 && response.status < 300) {
            console.log('Tenant integrations fetched:', response.data);
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const addIntegration = async (tenantID, integrationData, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.addIntegration(tenantID, integrationData, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

const updateIntegrations = async (tenantID, integrationData, accessToken, setSuccess, setError) => {
    try {
        const response = await ClientInstanceService.updateIntegrations(tenantID, integrationData, accessToken);
        if(response.status >= 200 && response.status < 300) {
            setSuccess(response.data);
        } else {
            setError(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        setError(`Error: ${error.message}`);
    };
};

// - - - - - - - - - - - COMPONENTS - - - - - - - - - - - - 

const ClientLogin = ({handleUserLogin, handleGoogleLogin, userError}) => {
    const [credentials, setCredentials] = useState({ email: '', password: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        await handleUserLogin(credentials);
        setIsLoading(false);
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        await handleGoogleLogin(credentialResponse.credential);
    };

    const handleGoogleError = () => {
        console.error('Google login failed');
    };

    const checkCredentialValidity = (credential) => {
        let ready = true;

        //check valid email format if not valid set ready to false
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(credential.email)) {
            ready = false;
        };

        //check valid password format if not valid set ready to false
        if (credential.password.length < 6) {
            ready = false;
        };

        return ready;
    }

    return (
        <div className="client-instance-login" style={{ backgroundImage: `url(${HeroBackground})`, width: '100vw', height: '100vh', backgroundPosition: 'center', backgroundSize: 'cover'}}>
            <div className="client-instance-login-overlay">
                <div className="login-card">
                    <div className="login-header">
                        <img src={LogoAnimated} alt="Sofie GIF" className="login-header-gif"/>
                        <h1>Console Access</h1>
                        <div className='login-header-link'>
                            <p>Need an account?</p>
                            <a href="https://meetsofie.com/signup" target="_blank" rel="noopener noreferrer" className='login-header-link-text'>Sign up</a>
                            <FaArrowRight className='login-header-link-icon'/>
                        </div>
                        
                    </div>
                    <div className='login-externals'>
                        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={handleGoogleError}
                                theme="outline"
                                size="large"
                                text="signin_with"
                                shape="rectangular"
                            />
                        </GoogleOAuthProvider>
                    </div>
                    <div className='login-divider'>
                        <hr className="short-divider"/>
                        <span className='login-divider-text'>OR</span>
                        <hr className="long-divider"/>
                    </div>
                    <form onSubmit={handleSubmit} className="login-form">
                        <div className="form-group">
                            <label htmlFor="username">Email Address</label>
                            <input
                                type="text"
                                id="email"
                                value={credentials.email}
                                onChange={(e) => setCredentials({...credentials, email: e.target.value})}
                                placeholder="Enter your email address"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    value={credentials.password}
                                    onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                                    placeholder="Enter your password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                        </div>
                        {userError && <div className="error-message">{userError}</div>}
                        <button type="submit" className="btn-primary sign-in-btn" disabled={isLoading || !checkCredentialValidity(credentials)}>
                            {isLoading ? 'Signing In...' : 'Sign In'}
                        </button>
                        <a href="https://meetsofie.com/forgotPassword" target="_blank" rel="noopener noreferrer" className="forgot-password-link">
                            Forgot Password?
                        </a>
                    </form>
                </div>
            </div>
        </div>
    );
};

const AccountSettingsModal = ({ userData, onClose }) => {
    const [formData, setFormData] = useState({
        firstName: userData?.user?.firstName || '',
        lastName: userData?.user?.lastName || '',
        email: userData?.user?.email || '',
        phone: userData?.user?.phone || ''
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        // TODO: Implement account update logic
        console.log('Updating account:', formData);
        onClose();
    };

    const handleOverlayClick = (e) => {
        if (e.target.className === 'modal-overlay') {
            onClose();
        }
    };

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal-container">
                <div className="modal-header">
                    <h2>Account Settings</h2>
                    <button className="modal-close" onClick={onClose}>
                        <FaXmark />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label>First Name</label>
                            <input
                                type="text"
                                value={formData.firstName}
                                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Last Name</label>
                            <input
                                type="text"
                                value={formData.lastName}
                                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                                required
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Phone</label>
                        <input
                            type="tel"
                            value={formatPhoneInput(formData.phone)}
                            onChange={(e) => {
                                const formatted = formatPhoneInput(e.target.value);
                                const parsed = parsePhoneNumber(formatted);
                                setFormData({...formData, phone: parsed});
                            }}
                            placeholder="(###) ###-####"
                            required
                        />
                    </div>
                    <div className="modal-actions">
                        <button type="button" className="btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary">
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ClientConsoleHeader = ({ userData, selectedTenant, setSelectedTenant, onOpenAccountSettings, logoutUser }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    
    const getInitials = () => {
        if (!userData?.user?.firstName || !userData?.user?.lastName) return '';
        return `${userData.user.firstName[0]}${userData.user.lastName[0]}`.toUpperCase();
    };

    const handleLogout = () => {
        logoutUser();
        setShowDropdown(false);
    };

    const handleAccountSettings = () => {
        onOpenAccountSettings();
        setShowDropdown(false);
    };

    return (
        <div className='console-header'>
            <div className='console-header-left'>
                <img src={LogoAnimated} alt="Sofie AI Logo" className='console-logo' />
                <div className='logo-text-container'>
                    <div className='logo-text-line-1'>Sofie</div>
                    <div className='logo-text-line-2'>AI Agent</div>
                </div>
            </div>
            <div className='console-header-right'>
                {userData?.tenants?.length > 1 ? (
                    <select 
                        className='tenant-selector'
                        value={selectedTenant}
                        onChange={(e) => setSelectedTenant(e.target.value)}
                    >
                        {userData.tenants.map((tenant) => (
                            <option key={tenant.id} value={tenant.id}>
                                {tenant.name}
                            </option>
                        ))}
                    </select>
                ) : (
                    <div className='tenant-name'>{userData?.tenants?.[0]?.name || ''}</div>
                )}
                
                <div className='user-avatar-container'>
                    <div 
                        className='user-avatar'
                        onClick={() => setShowDropdown(!showDropdown)}
                    >
                        {getInitials()}
                    </div>
                    {showDropdown && (
                        <div className='user-dropdown'>
                            <button 
                                className='dropdown-item'
                                onClick={handleAccountSettings}
                            >
                                <FaGear className='dropdown-icon' />
                                <span>Account Settings</span>
                            </button>
                            <button 
                                className='dropdown-item'
                                onClick={handleLogout}
                            >
                                <FaRightFromBracket className='dropdown-icon' />
                                <span>Log Out</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ClientConsoleMenubar = ({ selectedTenant, menuOptions, menuSelection, setMenuSelection }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);

    const getMenuIcon = (selection) => {
        switch(selection) {
            case 'home': return <FaHouse />;
            case 'context': return <FaBook />;
            case 'history': return <FaClockRotateLeft />;
            case 'communicationChannels': return <FaPhone />;
            case 'communicationQueue': return <MdQueue />;
            case 'integrations': return <FaPlug />;
            case 'billing': return <FaCreditCard />;
            case 'userManagement': return <FaUsers />;
            case 'support': return <FaCircleQuestion />;
            default: return <FaHouse />;
        }
    };

    return (
        <div className={`console-menubar ${isCollapsed ? 'collapsed' : 'expanded'}`}>
            <div className="menubar-options">
                {menuOptions.map((option) => (
                    <button
                        key={option.selection}
                        className={`menubar-option ${menuSelection === option.selection ? 'active' : ''}`}
                        onClick={() => setMenuSelection(option.selection)}
                        title={isCollapsed ? option.displayName : ''}
                    >
                        <span className="menubar-icon">{getMenuIcon(option.selection)}</span>
                        {!isCollapsed && <span className="menubar-label">{option.displayName}</span>}
                    </button>
                ))}
            </div>
            
            <button 
                className="menubar-toggle"
                onClick={() => setIsCollapsed(!isCollapsed)}
                aria-label={isCollapsed ? 'Expand menu' : 'Collapse menu'}
            >
                {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
            </button>
        </div>
    );
};

const ClientHome = () => {
    return (
        <div className="home-content">
            <h2>Welcome to the Sofie AI Console</h2>
            <p>Select a tenant and navigate through the menu to manage your settings.</p>
        </div>
    );
};

const ContextHome = ({ contexts, locations, clients, onCreateLocation, onCreateClient, filters, onFilterChange, onClearFilters, selectedTenant, accessToken }) => {
    const [activeTab, setActiveTab] = useState('overview');

    return (
        <div className="context-home-container">
            {/* Filter Section */}
            <div className="console-form-section-large">
                <div className="filter-header">
                    <label>Filter Context Records</label>
                    <button 
                        className="btn-clear-filters"
                        onClick={onClearFilters}
                    >
                        Clear All Filters
                    </button>
                </div>
                <div className="filter-grid-5col">
                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            placeholder="Search by name..."
                            value={filters.name}
                            onChange={(e) => onFilterChange('name', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Account Type</label>
                        <select
                            value={filters.accountType}
                            onChange={(e) => onFilterChange('accountType', e.target.value)}
                        >
                            <option value="">All Types</option>
                            <option value="call">Call</option>
                            <option value="text">Text</option>
                            <option value="email">Email</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Account Direction</label>
                        <select
                            value={filters.accountDirection}
                            onChange={(e) => onFilterChange('accountDirection', e.target.value)}
                        >
                            <option value="">All Directions</option>
                            <option value="Inbound">Inbound</option>
                            <option value="Outbound">Outbound</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Client</label>
                        <select
                            value={filters.client}
                            onChange={(e) => onFilterChange('client', e.target.value)}
                        >
                            <option value="">All Clients</option>
                            {clients && clients.map((client) => (
                                <option key={client.id} value={client.id}>
                                    {client.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Location</label>
                        <select
                            value={filters.location}
                            onChange={(e) => onFilterChange('location', e.target.value)}
                        >
                            <option value="">All Locations</option>
                            {locations && locations.map((location) => (
                                <option key={location.id} value={location.id}>
                                    {location.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="tab-navigation">
                <div className="tab-buttons">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('locations')}
                        className={`tab-button ${activeTab === 'locations' ? 'active' : ''}`}
                    >
                        <FaLocationDot className="icon-margin-right" />
                        Locations ({locations?.length || 0})
                    </button>
                    <button
                        onClick={() => setActiveTab('clients')}
                        className={`tab-button ${activeTab === 'clients' ? 'active' : ''}`}
                    >
                        <FaUser className="icon-margin-right" />
                        Clients ({clients?.length || 0})
                    </button>
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <div>
                    <div className="overview-welcome">
                        <FaFileLines className="welcome-icon" />
                        <h3>Welcome to Context Management</h3>
                        <p>
                            Select a context record from the left sidebar to view and edit its details, or create a new context record using the button above.
                        </p>
                        <p>
                            Use the tabs above to manage your locations and clients, which are used when creating context records.
                        </p>
                        <div className="overview-stats">
                            <div className="stat-card">
                                <FaBook className="stat-icon" />
                                <h4>Context Records</h4>
                                <p className="stat-number">
                                    {contexts?.length || 0}
                                </p>
                                <p className="stat-label">Total contexts</p>
                            </div>
                            <div className="stat-card">
                                <FaLocationDot className="stat-icon" />
                                <h4>Locations</h4>
                                <p className="stat-number">
                                    {locations?.length || 0}
                                </p>
                                <p className="stat-label">Total locations</p>
                            </div>
                            <div className="stat-card">
                                <FaUser className="stat-icon" />
                                <h4>Clients</h4>
                                <p className="stat-number">
                                    {clients?.length || 0}
                                </p>
                                <p className="stat-label">Total clients</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'locations' && (
                <LocationManager 
                    locations={locations}
                    onCreateLocation={onCreateLocation}
                    selectedTenant={selectedTenant}
                    accessToken={accessToken}
                />
            )}

            {activeTab === 'clients' && (
                <ClientManager 
                    clients={clients}
                    onCreateClient={onCreateClient}
                    selectedTenant={selectedTenant}
                    accessToken={accessToken}
                />
            )}
        </div>
    );
};

const ClientContext = ({selectedTenant, accessToken}) => {
    const [contexts, setContexts] = useState([]);
    const [locations, setLocations] = useState([]);
    const [clients, setClients] = useState([]);
    const [communicationChannels, setCommunicationChannels] = useState({ emailAccounts: [], phoneAccounts: [] });
    const [selectedContext, setSelectedContext] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showLocationManager, setShowLocationManager] = useState(false);
    const [showClientManager, setShowClientManager] = useState(false);
    const [showCreateContext, setShowCreateContext] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [showClientModal, setShowClientModal] = useState(false);
    const [isEditingContext, setIsEditingContext] = useState(false);
    const [updateNotification, setUpdateNotification] = useState({
        visible: false,
        message: '',
        type: '' // 'success' or 'error'
    });
    const [editContextFormData, setEditContextFormData] = useState({
        id: '',
        accountType: '',
        accountDirection: '',
        account: '',
        name: '',
        greeting: '',
        purpose: '',
        generalContext: '',
        specificContext: '',
        flow: [''],
        location: '',
        client: ''
    });
    const [filters, setFilters] = useState({
        name: '',
        accountType: '',
        accountDirection: '',
        client: '',
        location: ''
    });
    const [contextFormData, setContextFormData] = useState({
        id: '',
        accountType: '',
        accountDirection: '',
        account: '',
        name: '',
        greeting: '',
        purpose: '',
        generalContext: '',
        specificContext: '',
        flow: [''],
        location: '',
        client: ''
    });

    useEffect(() => {
        if (selectedTenant && accessToken) {
            loadAllContext();
            loadCommunicationChannels();
        }
    }, [selectedTenant, accessToken]);

    const loadCommunicationChannels = async () => {
        await getTenantsCommunicationChannels(
            selectedTenant.id,
            accessToken,
            (data) => {
                console.log('Communication channels loaded:', data);
                setCommunicationChannels(data);
            },
            (err) => {
                console.error('Error loading communication channels:', err);
            }
        );
    };

    const loadAllContext = async () => {
        setIsLoading(true);
        setError(null);
        
        console.log('Loading context for tenant:', selectedTenant.id);
        
        return new Promise((resolve, reject) => {
            getAllContext(
                selectedTenant.id,
                accessToken,
                (data) => {
                    console.log('Context data received:', data);
                    setContexts(data.context || []);
                    setLocations(data.locations || []);
                    setClients(data.clients || []);
                    setIsLoading(false);
                    resolve(data);
                },
                (err) => {
                    console.error('Error loading context:', err);
                    setError(err);
                    setIsLoading(false);
                    reject(err);
                }
            );
        });
    };

    const handleSelectContext = (context) => {
        setSelectedContext(context);
        setShowLocationManager(false);
        setShowClientManager(false);
        setShowCreateContext(false);
    };

    const getFilteredContexts = () => {
        if (!contexts) return [];
        
        return contexts.filter(context => {
            // Name filter
            if (filters.name && !context.name?.toLowerCase().includes(filters.name.toLowerCase())) {
                return false;
            }
            
            // Account Type filter - case insensitive comparison
            if (filters.accountType && context.accountType?.toLowerCase() !== filters.accountType.toLowerCase()) {
                return false;
            }
            
            // Account Direction filter
            if (filters.accountDirection && context.direction !== filters.accountDirection) {
                return false;
            }
            
            // Client filter - compare as strings using clientInformation.id
            if (filters.client && String(context.clientInformation?.id) !== String(filters.client)) {
                return false;
            }
            
            // Location filter - compare as strings using locationInformation.id
            if (filters.location && String(context.locationInformation?.id) !== String(filters.location)) {
                return false;
            }
            
            return true;
        });
    };

    // Group contexts by account type
    const getGroupedContexts = () => {
        const filtered = getFilteredContexts();
        return {
            call: filtered.filter(ctx => ctx.accountType?.toLowerCase() === 'call'),
            text: filtered.filter(ctx => ctx.accountType?.toLowerCase() === 'text'),
            email: filtered.filter(ctx => ctx.accountType?.toLowerCase() === 'email')
        };
    };

    // Check if a communication channel has an inbound context
    const hasInboundContext = (accountId, accountType) => {
        return contexts.some(ctx => 
            ctx.direction === 'Inbound' && 
            ctx.accountType?.toLowerCase() === accountType.toLowerCase() &&
            String(ctx.account?.id || ctx.account) === String(accountId)
        );
    };

    // Get channels without inbound contexts
    const getChannelsWithoutInbound = () => {
        const warnings = [];
        
        // Check phone accounts for call
        communicationChannels.phoneAccounts?.forEach(phone => {
            if (!hasInboundContext(phone.id, 'call')) {
                warnings.push({
                    id: phone.id,
                    type: 'call',
                    account: phone.number || phone.name,
                    accountObj: phone
                });
            }
        });
        
        // Check phone accounts for text
        communicationChannels.phoneAccounts?.forEach(phone => {
            if (!hasInboundContext(phone.id, 'text')) {
                warnings.push({
                    id: phone.id,
                    type: 'text',
                    account: phone.number || phone.name,
                    accountObj: phone
                });
            }
        });
        
        // Check email accounts
        communicationChannels.emailAccounts?.forEach(email => {
            if (!hasInboundContext(email.id, 'email')) {
                warnings.push({
                    id: email.id,
                    type: 'email',
                    account: email.username || email.name,
                    accountObj: email
                });
            }
        });
        
        return warnings;
    };

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };

    const handleClearFilters = () => {
        setFilters({
            name: '',
            accountType: '',
            accountDirection: '',
            client: '',
            location: ''
        });
    };

    const handleCreateContext = async () => {
        const submissionData = {
            ...contextFormData,
            greeting: 'Hi, this is Sofie ' + contextFormData.greeting,
            flow: contextFormData.flow.filter(f => f.trim() !== '')
        };
        await createContext(
            selectedTenant.id,
            submissionData,
            accessToken,
            (data) => {
                loadAllContext();
                setShowCreateContext(false);
                setContextFormData({
                    id: '',
                    accountType: '',
                    accountDirection: '',
                    account: '',
                    name: '',
                    greeting: '',
                    purpose: '',
                    generalContext: '',
                    specificContext: '',
                    flow: [''],
                    location: '',
                    client: ''
                });
                alert('Context created successfully!');
            },
            (err) => {
                console.error('Error creating context:', err);
                alert('Error creating context: ' + err);
            }
        );
    };

    const handleUpdateContext = async () => {
        if (!selectedContext) return;
        
        const submissionData = {
            ...editContextFormData,
            greeting: 'Hi, this is Sofie ' + editContextFormData.greeting,
            flow: editContextFormData.flow.filter(f => f.trim() !== '')
        };
        
        await updateContext(
            selectedTenant.id,
            selectedContext.id,
            submissionData,
            accessToken,
            async (data) => {
                const reloadedData = await loadAllContext();
                setIsEditingContext(false);
                // Find and select the updated context from the refreshed data
                const updatedContext = reloadedData.context?.find(ctx => ctx.id === selectedContext.id);
                if (updatedContext) {
                    setSelectedContext(updatedContext);
                }
                setUpdateNotification({
                    visible: true,
                    message: 'Context updated successfully',
                    type: 'success'
                });
                setTimeout(() => {
                    setUpdateNotification({ visible: false, message: '', type: '' });
                }, 3000);
            },
            (err) => {
                console.error('Error updating context:', err);
                setUpdateNotification({
                    visible: true,
                    message: 'Failed to update context. Check your input and contact support if this issue persists',
                    type: 'error'
                });
                setTimeout(() => {
                    setUpdateNotification({ visible: false, message: '', type: '' });
                }, 5000);
            }
        );
    };

    const handleDeleteContext = async () => {
        if (!selectedContext) return;
        
        const confirmDelete = window.confirm(`Are you sure you want to delete the context "${selectedContext.name}"? This action cannot be undone.`);
        if (!confirmDelete) return;

        await deleteContext(
            selectedTenant.id,
            selectedContext.id,
            accessToken,
            (data) => {
                loadAllContext();
                setSelectedContext(null);
                setIsEditingContext(false);
                alert('Context deleted successfully!');
            },
            (err) => {
                console.error('Error deleting context:', err);
                alert('Error deleting context: ' + err);
            }
        );
    };

    const isContextFormValid = () => {
        // Check if there's already an Inbound context with the same accountType and account
        const hasDuplicateInbound = contextFormData.accountDirection === 'Inbound' && 
            contexts.some(ctx => 
                ctx.direction === 'Inbound' && 
                ctx.accountType?.toLowerCase() === contextFormData.accountType?.toLowerCase() &&
                String(ctx.account?.id || ctx.account) === String(contextFormData.account) &&
                ctx.id !== contextFormData.id // Exclude current context when editing
            );

        return (
            contextFormData.accountType.trim() !== '' &&
            contextFormData.accountDirection.trim() !== '' &&
            contextFormData.account.trim() !== '' &&
            contextFormData.name.trim() !== '' &&
            contextFormData.greeting.trim() !== '' &&
            contextFormData.purpose.trim() !== '' &&
            contextFormData.generalContext.trim() !== '' &&
            contextFormData.flow.some(f => f.trim() !== '') &&
            contextFormData.location !== '' &&
            contextFormData.client !== '' &&
            !hasDuplicateInbound // Prevent submission if duplicate exists
        );
    };

    const checkDuplicateInbound = () => {
        if (contextFormData.accountDirection !== 'Inbound' || !contextFormData.accountType || !contextFormData.account) {
            return null;
        }

        const duplicate = contexts.find(ctx => 
            ctx.direction === 'Inbound' && 
            ctx.accountType?.toLowerCase() === contextFormData.accountType?.toLowerCase() &&
            String(ctx.account?.id || ctx.account) === String(contextFormData.account) &&
            ctx.id !== contextFormData.id
        );

        return duplicate;
    };

    const handleCancelCreateContext = () => {
        setShowCreateContext(false);
        setContextFormData({
            id: '',
            accountType: '',
            accountDirection: '',
            account: '',
            name: '',
            greeting: '',
            purpose: '',
            generalContext: '',
            specificContext: '',
            flow: [''],
            location: '',
            client: ''
        });
    };

    const handleCreateLocation = async (locationData, fromModal = false) => {
        await createContextLocation(
            selectedTenant.id,
            locationData,
            accessToken,
            (data) => {
                loadAllContext();
                if (fromModal) {
                    // Auto-select the newly created location
                    setTimeout(() => {
                        setContextFormData(prev => ({...prev, location: data.id}));
                        setShowLocationModal(false);
                    }, 100);
                } else {
                    alert('Location created successfully!');
                }
            },
            (err) => {
                console.error('Error creating location:', err);
                alert('Error creating location: ' + err);
            }
        );
    };

    const handleCreateClient = async (clientData, fromModal = false) => {
        await createContextClient(
            selectedTenant.id,
            clientData,
            accessToken,
            (data) => {
                loadAllContext();
                if (fromModal) {
                    // Auto-select the newly created client
                    setTimeout(() => {
                        setContextFormData(prev => ({...prev, client: data.id}));
                        setShowClientModal(false);
                    }, 100);
                } else {
                    alert('Client created successfully!');
                }
            },
            (err) => {
                console.error('Error creating client:', err);
                alert('Error creating client: ' + err);
            }
        );
    };

    if (error && !isLoading) {
        return (
            <div className="communication-channels">
                <div className="error-container">
                    <p className="error-text">Error loading contexts: {error}</p>
                    <button 
                        onClick={loadAllContext}
                        className="retry-button"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="communication-channels">
            {/* Left Sidebar */}
            <div className="channels-sidebar">
                <div className="sidebar-header">
                    <h3>Context Records</h3>
                </div>
                <div className="sidebar-content">
                    {isLoading ? (
                        <div className="loading-container">
                            <p>Loading contexts...</p>
                        </div>
                    ) : (() => {
                        const groupedContexts = getGroupedContexts();
                        const warnings = getChannelsWithoutInbound();
                        const hasActiveFilters = filters.name || filters.accountType || filters.accountDirection || filters.client || filters.location;
                        
                        return (
                            <>
                                {/* Call Section */}
                                {(!hasActiveFilters || groupedContexts.call.length > 0) && (
                                    <div className="channel-section">
                                        <div className="justified-section-header">
                                            <FaPhone className="section-header-icon" />
                                            <h4>Call</h4>
                                        </div>
                                        <div className="channel-list">
                                            {!hasActiveFilters && warnings.filter(w => w.type === 'call').map((warning) => (
                                                <div key={`warning-call-${warning.id}`} className="channel-item warning-item">
                                                    <FaCircleExclamation className="channel-icon warning-icon" />
                                                    <div className="channel-info">
                                                        <p className="channel-name warning-text">
                                                            No Inbound Context
                                                        </p>
                                                        <p className="channel-detail warning-message">
                                                            Calls inbound to {formatPhoneNumber(warning.account)} won't have a response. Create an inbound context for this account to enable Sofie to respond to these calls.
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                            {groupedContexts.call.map((context) => (
                                                <div
                                                    key={context.id}
                                                    className={`channel-item ${selectedContext?.id === context.id ? 'selected' : ''}`}
                                                    onClick={() => handleSelectContext(context)}
                                                >
                                                    <FaFileLines className="channel-icon" />
                                                    <div className="channel-info">
                                                        <p className="channel-name">
                                                            {context.name || 'Unnamed Context'}
                                                        </p>
                                                        <p className="channel-detail">
                                                            {context.direction || 'No Direction'}
                                                        </p>
                                                        <p className="channel-detail">
                                                            {context.account?.name || (context.account?.number ? formatPhoneNumber(context.account.number) : null) || context.account?.username || 'No Account'}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                            {!hasActiveFilters && groupedContexts.call.length === 0 && warnings.filter(w => w.type === 'call').length === 0 && (
                                                <div className="empty-state-small">
                                                    <p>No call contexts</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Text Section */}
                                {(!hasActiveFilters || groupedContexts.text.length > 0) && (
                                    <div className="channel-section">
                                        <div className="justified-section-header">
                                            <FaComment className="section-header-icon" />
                                            <h4>Text</h4>
                                        </div>
                                        <div className="channel-list">
                                            {!hasActiveFilters && warnings.filter(w => w.type === 'text').map((warning) => (
                                                <div key={`warning-text-${warning.id}`} className="channel-item warning-item">
                                                    <FaCircleExclamation className="channel-icon warning-icon" />
                                                    <div className="channel-info">
                                                        <p className="channel-name warning-text">
                                                            No Inbound Context
                                                        </p>
                                                        <p className="channel-detail warning-message">
                                                            Texts inbound to {formatPhoneNumber(warning.account)} won't have a response. Create an inbound context for this account to enable Sofie to respond to these texts.
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                            {groupedContexts.text.map((context) => (
                                                <div
                                                    key={context.id}
                                                    className={`channel-item ${selectedContext?.id === context.id ? 'selected' : ''}`}
                                                    onClick={() => handleSelectContext(context)}
                                                >
                                                    <FaFileLines className="channel-icon" />
                                                    <div className="channel-info">
                                                        <p className="channel-name">
                                                            {context.name || 'Unnamed Context'}
                                                        </p>
                                                        <p className="channel-detail">
                                                            {context.direction || 'No Direction'}
                                                        </p>
                                                        <p className="channel-detail">
                                                            {context.account?.name || (context.account?.number ? formatPhoneNumber(context.account.number) : null) || context.account?.username || 'No Account'}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                            {!hasActiveFilters && groupedContexts.text.length === 0 && warnings.filter(w => w.type === 'text').length === 0 && (
                                                <div className="empty-state-small">
                                                    <p>No text contexts</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Email Section */}
                                {(!hasActiveFilters || groupedContexts.email.length > 0) && (
                                    <div className="channel-section">
                                        <div className="justified-section-header">
                                            <FaEnvelope className="section-header-icon" />
                                            <h4>Email</h4>
                                        </div>
                                        <div className="channel-list">
                                            {!hasActiveFilters && warnings.filter(w => w.type === 'email').map((warning) => (
                                                <div key={`warning-email-${warning.id}`} className="channel-item warning-item">
                                                    <FaCircleExclamation className="channel-icon warning-icon" />
                                                    <div className="channel-info">
                                                        <p className="channel-name warning-text">
                                                            No Inbound Context
                                                        </p>
                                                        <p className="channel-detail warning-message">
                                                            Emails inbound to {warning.account} won't have a response. Create an inbound context for this account to enable Sofie to respond to these emails.
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                            {groupedContexts.email.map((context) => (
                                                <div
                                                    key={context.id}
                                                    className={`channel-item ${selectedContext?.id === context.id ? 'selected' : ''}`}
                                                    onClick={() => handleSelectContext(context)}
                                                >
                                                    <FaFileLines className="channel-icon" />
                                                    <div className="channel-info">
                                                        <p className="channel-name">
                                                            {context.name || 'Unnamed Context'}
                                                        </p>
                                                        <p className="channel-detail">
                                                            {context.direction || 'No Direction'}
                                                        </p>
                                                        <p className="channel-detail">
                                                            {context.account?.name || context.account?.number || context.account?.username || 'No Account'}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                            {!hasActiveFilters && groupedContexts.email.length === 0 && warnings.filter(w => w.type === 'email').length === 0 && (
                                                <div className="empty-state-small">
                                                    <p>No email contexts</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </div>
            </div>

            {/* Right Detail Panel */}
            <div className="channels-detail">
                <div className="detail-toolbar">
                    <div className="detail-toolbar-left">
                        <h3 className="detail-toolbar-title">
                            {showCreateContext ? 'Create New Context' : selectedContext && !showLocationManager && !showClientManager ? 'Update Context' : 'Context Management'}
                        </h3>
                    </div>
                    <div className="detail-toolbar-right">
                        {showCreateContext ? (
                            <>
                                <button 
                                    className="toolbar-btn"
                                    onClick={handleCancelCreateContext}
                                >
                                    <FaXmark className="toolbar-btn-icon" />
                                    Cancel
                                </button>
                                <button 
                                    className="toolbar-btn toolbar-btn-save"
                                    onClick={handleCreateContext}
                                    disabled={!isContextFormValid()}
                                >
                                    <FaFloppyDisk className="toolbar-btn-icon" />
                                    Create Context Record
                                </button>
                            </>
                        ) : selectedContext && !showLocationManager && !showClientManager ? (
                            <>
                                <button 
                                    className="toolbar-btn"
                                    onClick={() => {
                                        setSelectedContext(null);
                                        setIsEditingContext(false);
                                    }}
                                >
                                    <FaXmark className="toolbar-btn-icon" />
                                    Cancel
                                </button>
                                {isEditingContext ? (
                                    <button 
                                        className="toolbar-btn toolbar-btn-save"
                                        onClick={handleUpdateContext}
                                    >
                                        <FaFloppyDisk className="toolbar-btn-icon" />
                                        Save Context Record
                                    </button>
                                ) : (
                                    <button 
                                        className="toolbar-btn toolbar-btn-highlight-edit"
                                        onClick={() => setIsEditingContext(true)}
                                    >
                                        <FaPencil className="toolbar-btn-icon" />
                                        Edit
                                    </button>
                                )}
                                <button 
                                    className="toolbar-btn toolbar-btn-delete"
                                    onClick={handleDeleteContext}
                                >
                                    <FaTrash className="toolbar-btn-icon" />
                                    Delete
                                </button>
                            </>
                        ) : (
                            <>
                                <button 
                                    className="toolbar-btn toolbar-btn-highlight-edit"
                                    onClick={() => {
                                        setShowCreateContext(true);
                                        setShowLocationManager(false);
                                        setShowClientManager(false);
                                        setSelectedContext(null);
                                    }}
                                >
                                    <FaPlus className="toolbar-btn-icon" />
                                    Create Context
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {updateNotification.visible && (
                    <div className={`update-notification ${updateNotification.type === 'success' ? 'notification-success' : 'notification-error'}`}>
                        {updateNotification.message}
                    </div>
                )}

                <div className="detail-content">
                    {showLocationManager && (
                        <LocationManager 
                            locations={locations}
                            onCreateLocation={handleCreateLocation}
                            selectedTenant={selectedTenant}
                            accessToken={accessToken}
                        />
                    )}
                    
                    {showClientManager && (
                        <ClientManager 
                            clients={clients}
                            onCreateClient={handleCreateClient}
                            selectedTenant={selectedTenant}
                            accessToken={accessToken}
                        />
                    )}
                    
                    {showCreateContext && (
                        <CreateContextForm 
                            locations={locations}
                            clients={clients}
                            communicationChannels={communicationChannels}
                            formData={contextFormData}
                            setFormData={setContextFormData}
                            onSubmit={handleCreateContext}
                            onOpenLocationModal={() => setShowLocationModal(true)}
                            onOpenClientModal={() => setShowClientModal(true)}
                            contexts={contexts}
                            checkDuplicateInbound={checkDuplicateInbound}
                        />
                    )}
                    
                    {selectedContext && !showLocationManager && !showClientManager && !showCreateContext && (
                        <ContextDetails 
                            context={selectedContext}
                            locations={locations}
                            clients={clients}
                            communicationChannels={communicationChannels}
                            isEditing={isEditingContext}
                            formData={editContextFormData}
                            setFormData={setEditContextFormData}
                            onOpenLocationModal={() => setShowLocationModal(true)}
                            onOpenClientModal={() => setShowClientModal(true)}
                        />
                    )}
                    
                    {!selectedContext && !showLocationManager && !showClientManager && !showCreateContext && (
                        <ContextHome 
                            contexts={getFilteredContexts()}
                            locations={locations}
                            clients={clients}
                            onCreateLocation={handleCreateLocation}
                            onCreateClient={handleCreateClient}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onClearFilters={handleClearFilters}
                            selectedTenant={selectedTenant}
                            accessToken={accessToken}
                        />
                    )}
                </div>
            </div>

            {/* Location Modal */}
            {showLocationModal && (
                <Modal onClose={() => setShowLocationModal(false)} title="Add New Location">
                    <LocationForm 
                        onSubmit={(data) => handleCreateLocation(data, true)}
                        onCancel={() => setShowLocationModal(false)}
                    />
                </Modal>
            )}

            {/* Client Modal */}
            {showClientModal && (
                <Modal onClose={() => setShowClientModal(false)} title="Add New Client">
                    <ClientForm 
                        onSubmit={(data) => handleCreateClient(data, true)}
                        onCancel={() => setShowClientModal(false)}
                    />
                </Modal>
            )}
        </div>
    );
};

// Modal Component
const Modal = ({ onClose, title, children }) => {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{title}</h3>
                    <button onClick={onClose} className="modal-close-btn">
                        <FaXmark />
                    </button>
                </div>
                <div className="modal-body">
                    {children}
                </div>
            </div>
        </div>
    );
};

// Location Form Component
const LocationForm = ({ onSubmit, onCancel, initialData = null, onDelete = null }) => {
    const [formData, setFormData] = useState(initialData || {
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        county: '',
        country: ''
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const handleDelete = () => {
        if (onDelete && initialData) {
            onDelete(initialData);
        }
    };

    const isFormValid = () => {
        return formData.name.trim() !== '';
    };

    const isEditMode = initialData !== null;

    return (
        <form onSubmit={handleSubmit} className="detail-form">
            <div className="modal-instructions">
                <FaCircleExclamation className="instruction-icon" />
                <p>Enter location information for the entity or organization this agent represents.</p>
            </div>
            <div className="form-group">
                <label>Location Name *</label>
                <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                />
            </div>
            <div className="form-group">
                <label>Address</label>
                <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
            </div>
            <div className="form-grid-2col">
                <div className="form-group">
                    <label>City</label>
                    <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                    />
                </div>
                <div className="form-group">
                    <label>State</label>
                    <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                    />
                </div>
            </div>
            <div className="form-grid-3col">
                <div className="form-group">
                    <label>ZIP Code</label>
                    <input
                        type="text"
                        value={formData.zip}
                        onChange={(e) => setFormData({...formData, zip: e.target.value})}
                    />
                </div>
                <div className="form-group">
                    <label>County</label>
                    <input
                        type="text"
                        value={formData.county}
                        onChange={(e) => setFormData({...formData, county: e.target.value})}
                    />
                </div>
                <div className="form-group">
                    <label>Country</label>
                    <input
                        type="text"
                        value={formData.country}
                        onChange={(e) => setFormData({...formData, country: e.target.value})}
                    />
                </div>
            </div>
            <div className="form-button-actions">
                {isEditMode && onDelete && (
                    <button 
                        type="button" 
                        onClick={handleDelete}
                        className="btn-delete context-form-btn"
                    >
                        <FaTrash className="trash-icon" />
                        Delete Location
                    </button>
                )}
                <button type="button" onClick={onCancel} className="btn-secondary context-form-btn">
                    Cancel
                </button>
                <button type="submit" className="btn-primary context-form-btn" disabled={!isFormValid()}>
                    {isEditMode ? 'Update Location' : 'Create Location'}
                </button>
            </div>
        </form>
    );
};

// Client Form Component
const ClientForm = ({ onSubmit, onCancel, initialData = null, onDelete = null }) => {
    const [formData, setFormData] = useState(initialData || {
        name: '',
        email: '',
        phone: '',
        identifier: '',
        licenseNumber: '',
        behalfOf: ''
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const handleDelete = () => {
        if (onDelete && initialData) {
            onDelete(initialData);
        }
    };

    const isFormValid = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return formData.name.trim() !== '' && 
               formData.email.trim() !== '' && 
               emailRegex.test(formData.email) &&
               formData.phone.trim() !== '';
    };

    const isEditMode = initialData !== null;

    return (
        <form onSubmit={handleSubmit} className="detail-form">
            <div className="modal-instructions">
                <FaCircleExclamation className="instruction-icon" />
                <p>Enter client information for the entity or individual this agent represents.</p>
            </div>
            <div className="form-group">
                <label>Entity Name *</label>
                <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                />
            </div>
            <div className="form-grid-2col">
                <div className="form-group">
                    <label>Email *</label>
                    <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                    />
                </div>
                <div className="form-group">
                    <label>Phone *</label>
                    <input
                        type="tel"
                        value={formatPhoneInput(formData.phone)}
                        onChange={(e) => {
                            const formatted = formatPhoneInput(e.target.value);
                            const parsed = parsePhoneNumber(formatted);
                            setFormData({...formData, phone: parsed});
                        }}
                        placeholder="(###) ###-####"
                        required
                    />
                </div>
            </div>
            <div className="form-grid-2col">
                <div className="form-group">
                    <label>Identifier</label>
                    <input
                        type="text"
                        value={formData.identifier}
                        onChange={(e) => setFormData({...formData, identifier: e.target.value})}
                    />
                </div>
                <div className="form-group">
                    <label>License Number</label>
                    <input
                        type="text"
                        value={formData.licenseNumber}
                        onChange={(e) => setFormData({...formData, licenseNumber: e.target.value})}
                    />
                </div>
            </div>
            <div className="form-group">
                <label>Behalf Of</label>
                <input
                    type="text"
                    value={formData.behalfOf}
                    onChange={(e) => setFormData({...formData, behalfOf: e.target.value})}
                />
            </div>
            <div className="form-button-actions">
                {isEditMode && onDelete && (
                    <button 
                        type="button" 
                        onClick={handleDelete}
                        className="btn-delete context-form-btn"
                    >
                        <FaTrash className="trash-icon" />
                        Delete Client
                    </button>
                )}
                <button type="button" onClick={onCancel} className="btn-secondary context-form-btn">
                    Cancel
                </button>
                <button type="submit" className="btn-primary context-form-btn" disabled={!isFormValid()}>
                    {isEditMode ? 'Update Client' : 'Create Client'}
                </button>
            </div>
        </form>
    );
};

const LocationManager = ({ locations, onCreateLocation, selectedTenant, accessToken }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);

    const handleCreateLocation = (locationData) => {
        onCreateLocation(locationData, false);
        setShowModal(false);
        setEditingLocation(null);
    };

    const handleEditLocation = (location) => {
        setEditingLocation(location);
        setShowModal(true);
    };

    const handleDeleteLocation = async (location) => {
        // Check if location is associated with any context
        await checkContextLocation(
            selectedTenant?.id,
            location.id,
            accessToken,
            (data) => {
                // Success means there IS an association - prevent deletion
                alert(`Cannot delete location "${location.name}" because it is currently assigned to one or more context records. Please reassign those contexts to a different location before deleting this one.`);
            },
            (err) => {
                // Error means no association found - safe to delete
                const confirmDelete = window.confirm(`Are you sure you want to delete the location "${location.name}"? This action cannot be undone.`);
                if (confirmDelete) {
                    // TODO: Implement delete location API call
                    alert('Delete location functionality needs to be implemented on the backend.');
                    setShowModal(false);
                    setEditingLocation(null);
                }
            }
        );
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingLocation(null);
    };

    return (
        <div>
            <div className="manager-header">
                <h3>Location Records</h3>
                <button 
                    className="toolbar-btn toolbar-btn-highlight-edit"
                    onClick={() => {
                        setEditingLocation(null);
                        setShowModal(true);
                    }}
                >
                    <FaPlus className="toolbar-btn-icon" />
                    Add Location
                </button>
            </div>

            {showModal && (
                <Modal onClose={handleCloseModal} title={editingLocation ? "Edit Location" : "Add New Location"}>
                    <LocationForm
                        onSubmit={handleCreateLocation}
                        onCancel={handleCloseModal}
                        initialData={editingLocation}
                        onDelete={editingLocation ? handleDeleteLocation : null}
                    />
                </Modal>
            )}

            <div className="card-grid">
                {locations && locations.length > 0 ? (
                    locations.map((location) => (
                        <div 
                            key={location.id} 
                            onClick={() => handleEditLocation(location)}
                            className="location-card"
                        >
                            <div className="card-header">
                                <FaLocationDot className="card-icon" />
                                <h4>{location.name}</h4>
                            </div>
                            <p className="card-detail">
                                {location.address}
                            </p>
                            <p className="card-detail">
                                {location.city}, {location.state} {location.zip}
                            </p>
                            {location.county && (
                                <p className="card-detail">
                                    County: {location.county}
                                </p>
                            )}
                            <p className="card-detail">
                                {location.country}
                            </p>
                        </div>
                    ))
                ) : (
                    <div className="empty-state">
                        <FaLocationDot className="empty-state-icon" />
                        <p className="empty-state-text">No locations found. Add one to get started.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const ClientManager = ({ clients, onCreateClient, selectedTenant, accessToken }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingClient, setEditingClient] = useState(null);

    const handleCreateClient = (clientData) => {
        onCreateClient(clientData, false);
        setShowModal(false);
        setEditingClient(null);
    };

    const handleEditClient = (client) => {
        setEditingClient(client);
        setShowModal(true);
    };

    const handleDeleteClient = async (client) => {
        // Check if client is associated with any context
        await checkContextClient(
            selectedTenant?.id,
            client.id,
            accessToken,
            (data) => {
                // Success means there IS an association - prevent deletion
                alert(`Cannot delete client "${client.name}" because it is currently assigned to one or more context records. Please reassign those contexts to a different client before deleting this one.`);
            },
            (err) => {
                // Error means no association found - safe to delete
                const confirmDelete = window.confirm(`Are you sure you want to delete the client "${client.name}"? This action cannot be undone.`);
                if (confirmDelete) {
                    // TODO: Implement delete client API call
                    alert('Delete client functionality needs to be implemented on the backend.');
                    setShowModal(false);
                    setEditingClient(null);
                }
            }
        );
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingClient(null);
    };

    return (
        <div>
            <div className="manager-header">
                <h3>Client Records</h3>
                <button 
                    className="toolbar-btn toolbar-btn-highlight-edit"
                    onClick={() => {
                        setEditingClient(null);
                        setShowModal(true);
                    }}
                >
                    <FaPlus className="toolbar-btn-icon" />
                    Add Client
                </button>
            </div>

            {showModal && (
                <Modal onClose={handleCloseModal} title={editingClient ? "Edit Client" : "Add New Client"}>
                    <ClientForm
                        onSubmit={handleCreateClient}
                        onCancel={handleCloseModal}
                        initialData={editingClient}
                        onDelete={editingClient ? handleDeleteClient : null}
                    />
                </Modal>
            )}

            <div className="card-grid">
                {clients && clients.length > 0 ? (
                    clients.map((client) => (
                        <div 
                            key={client.id} 
                            onClick={() => handleEditClient(client)}
                            className="client-card"
                        >
                            <div className="card-header">
                                <FaUser className="card-icon" />
                                <h4>{client.name}</h4>
                            </div>
                            <p className="card-detail">
                                <strong>Email:</strong> {client.email}
                            </p>
                            <p className="card-detail">
                                <strong>Phone:</strong> {formatPhoneNumber(client.phone)}
                            </p>
                            {client.identifier && (
                                <p className="card-detail">
                                    <strong>ID:</strong> {client.identifier}
                                </p>
                            )}
                            {client.license_number && (
                                <p className="card-detail">
                                    <strong>License:</strong> {client.license_number}
                                </p>
                            )}
                            {client.behalf_of && (
                                <p className="card-detail">
                                    <strong>On Behalf Of:</strong> {client.behalf_of}
                                </p>
                            )}
                        </div>
                    ))
                ) : (
                    <div className="empty-state">
                        <FaUser className="empty-state-icon" />
                        <p className="empty-state-text">No clients found. Add one to get started.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const CreateContextForm = ({ locations, clients, communicationChannels, formData, setFormData, onSubmit, onOpenLocationModal, onOpenClientModal, contexts, checkDuplicateInbound }) => {
    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit();
    };

    const duplicateContext = checkDuplicateInbound ? checkDuplicateInbound() : null;

    const isFormValid = () => {
        return (
            formData.accountType.trim() !== '' &&
            formData.accountDirection.trim() !== '' &&
            formData.account.trim() !== '' &&
            formData.greeting.trim() !== '' &&
            formData.purpose.trim() !== '' &&
            formData.generalContext.trim() !== '' &&
            formData.flow.some(f => f.trim() !== '') &&
            formData.location !== '' &&
            formData.client !== '' &&
            !duplicateContext // Prevent submission if duplicate exists
        );
    };

    const addFlowItem = () => {
        setFormData({...formData, flow: [...formData.flow, '']});
    };

    const removeFlowItem = (index) => {
        const newFlow = formData.flow.filter((_, i) => i !== index);
        setFormData({...formData, flow: newFlow.length > 0 ? newFlow : ['']});
    };

    const updateFlowItem = (index, value) => {
        const newFlow = [...formData.flow];
        newFlow[index] = value;
        setFormData({...formData, flow: newFlow});
    };

    const moveFlowItem = (index, direction) => {
        const newFlow = [...formData.flow];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex >= 0 && targetIndex < newFlow.length) {
            [newFlow[index], newFlow[targetIndex]] = [newFlow[targetIndex], newFlow[index]];
            setFormData({...formData, flow: newFlow});
        }
    };

    return (
        <div>
            <form onSubmit={handleSubmit} className="detail-form detail-form-full">
                
                {/* Communication Channel Section */}
                <div className="console-form-section">
                    <h4 className="console-form-section-title">Communication Channel</h4>
                    
                    {duplicateContext && (
                        <div className="validation-warning">
                            <FaCircleExclamation className="warning-icon" />
                            <div>
                                <strong>Duplicate Inbound Context Detected:</strong>
                                <br />
                                An Inbound context already exists for this account type and account combination: 
                                <strong> "{duplicateContext.name}"</strong>. 
                                Only one Inbound context is allowed per account. Please select a different account, 
                                change the direction to Outbound, or delete the existing Inbound context.
                            </div>
                        </div>
                    )}
                    
                    <div className="form-grid-3col">
                        <div className="form-group">
                            <label>Account Type *</label>
                            <select
                                value={formData.accountType}
                                onChange={(e) => {
                                    setFormData({...formData, accountType: e.target.value, account: ''});
                                }}
                                required
                            >
                                <option value="">
                                    {(communicationChannels.emailAccounts?.length > 0 || communicationChannels.phoneAccounts?.length > 0)
                                        ? 'Select type'
                                        : 'No channels - Click "Communication Channels" to add one'}
                                </option>
                                {communicationChannels.phoneAccounts?.length > 0 && (
                                    <option value="Call">Call</option>
                                )}
                                {communicationChannels.emailAccounts?.length > 0 && (
                                    <option value="Email">Email</option>
                                )}
                                {communicationChannels.phoneAccounts?.length > 0 && (
                                    <option value="Text">Text</option>
                                )}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Account Direction *</label>
                            <select
                                value={formData.accountDirection}
                                onChange={(e) => setFormData({...formData, accountDirection: e.target.value})}
                                required
                            >
                                <option value="">Select direction</option>
                                <option value="Inbound">Inbound</option>
                                <option value="Outbound">Outbound</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Account *</label>
                            <select
                                value={formData.account}
                                onChange={(e) => setFormData({...formData, account: e.target.value})}
                                required
                                disabled={!formData.accountType}
                            >
                                <option value="">
                                    {!formData.accountType 
                                        ? 'Select account type first' 
                                        : 'Select account'}
                                </option>
                                {formData.accountType === 'Email' && communicationChannels.emailAccounts?.map((email) => (
                                    <option key={email.id} value={email.id}>
                                        {email.name} - {email.username}
                                    </option>
                                ))}
                                {(formData.accountType === 'Call' || formData.accountType === 'Text') && communicationChannels.phoneAccounts?.map((phone) => (
                                    <option key={phone.id} value={phone.id}>
                                        {phone.name} - {formatPhoneNumber(phone.phoneNumber)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Communication Originator Section */}
                <div className="console-form-section">
                    <h4 className="console-form-section-title">Communication Originator</h4>
                    <div className="form-grid-2col">
                        <div className="form-group">
                            <label>Client *</label>
                            <div className="originator-select-container">
                                <select
                                    value={formData.client}
                                    onChange={(e) => setFormData({...formData, client: e.target.value})}
                                    required
                                    className="originator-select-field"
                                >
                                    <option value="">
                                        {clients && clients.length > 0 
                                            ? 'Select a client' 
                                            : 'No clients found'}
                                    </option>
                                    {clients.map((cli) => (
                                        <option key={cli.id} value={cli.id}>
                                            {cli.name} - {cli.email}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={onOpenClientModal}
                                    className="originator-add-button"
                                    title="Add new client"
                                >
                                    <FaPlus /> Add
                                </button>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Location *</label>
                            <div className="originator-select-container">
                                <select
                                    value={formData.location}
                                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                                    required
                                    className="originator-select-field"
                                >
                                    <option value="">
                                        {locations && locations.length > 0 
                                            ? 'Select a location' 
                                            : 'No locations found'}
                                    </option>
                                    {locations.map((loc) => (
                                        <option key={loc.id} value={loc.id}>
                                            {loc.name} - {loc.city}, {loc.state}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={onOpenLocationModal}
                                    className="originator-add-button"
                                    title="Add new location"
                                >
                                    <FaPlus /> Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Communication Context Section */}
                <div className="console-form-section">
                    <h4 className="console-form-section-title">Communication Context</h4>
                    
                    <div className="form-group">
                        <label>Context Name *</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            placeholder="Enter a descriptive name for this context"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Greeting *</label>
                        <div className="flow-input-row">
                            <span className="preset-greeting">Hi, this is Sofie</span>
                            <textarea
                                value={formData.greeting}
                                onChange={(e) => setFormData({...formData, greeting: e.target.value})}
                                placeholder="continue greeting message..."
                                rows={3}
                                style={{height:51}}
                                className="flow-input-field textarea-resizable"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Purpose of Communication *</label>
                        <textarea
                            value={formData.purpose}
                            onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                            placeholder="Purpose of this communication"
                            rows={3}
                            className="textarea-resizable"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>General Context *</label>
                        <textarea
                            value={formData.generalContext}
                            onChange={(e) => setFormData({...formData, generalContext: e.target.value})}
                            placeholder="General context information"
                            className="textarea-large"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Specific Context</label>
                        <textarea
                            value={formData.specificContext}
                            onChange={(e) => setFormData({...formData, specificContext: e.target.value})}
                            placeholder="Specific context details"
                            className="textarea-large"
                        />
                    </div>

                    <div className="form-group">
                        <label>Flow of conversation *</label>
                        <div className="flow-input-list">
                            {formData.flow.map((flowItem, index) => (
                                <div key={index} className="flow-input-row">
                                    <input
                                        type="text"
                                        value={flowItem}
                                        onChange={(e) => updateFlowItem(index, e.target.value)}
                                        placeholder={`Flow step ${index + 1}`}
                                        className="flow-input-field"
                                        required={index === 0}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => moveFlowItem(index, 'up')}
                                        disabled={index === 0}
                                        className="flow-action-button"
                                    >
                                        ↑
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => moveFlowItem(index, 'down')}
                                        disabled={index === formData.flow.length - 1}
                                        className="flow-action-button"
                                    >
                                        ↓
                                    </button>
                                    {formData.flow.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeFlowItem(index)}
                                            className="flow-remove-button"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addFlowItem}
                                className="flow-add-button"
                            >
                                <FaPlus /> Add Flow Step
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

const ContextDetails = ({ context, locations, clients, communicationChannels, isEditing, formData, setFormData, onOpenLocationModal, onOpenClientModal }) => {

    useEffect(() => {
        if (context) {
            setFormData({
                id: context.id || '',
                accountType: context.accountType || '',
                accountDirection: context.direction || '',
                account: context.account?.id?.toString() || '',
                name: context.name || '',
                greeting: context.greeting?.replace('Hi, this is Sofie ', '') || '',
                purpose: context.purpose || '',
                generalContext: context.generalContext || '',
                specificContext: context.specificContext || '',
                flow: Array.isArray(context.flow) ? context.flow : (context.flow ? [context.flow] : ['']),
                location: context.locationInformation?.id?.toString() || '',
                client: context.clientInformation?.id?.toString() || ''
            });
        }
    }, [context]);

    const addFlowItem = () => {
        setFormData({...formData, flow: [...formData.flow, '']});
    };

    const removeFlowItem = (index) => {
        const newFlow = formData.flow.filter((_, i) => i !== index);
        setFormData({...formData, flow: newFlow.length > 0 ? newFlow : ['']});
    };

    const updateFlowItem = (index, value) => {
        const newFlow = [...formData.flow];
        newFlow[index] = value;
        setFormData({...formData, flow: newFlow});
    };

    const moveFlowItem = (index, direction) => {
        const newFlow = [...formData.flow];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex >= 0 && targetIndex < newFlow.length) {
            [newFlow[index], newFlow[targetIndex]] = [newFlow[targetIndex], newFlow[index]];
            setFormData({...formData, flow: newFlow});
        }
    };

    return (
        <div>
            <form className="detail-form detail-form-full">
                
                {/* Communication Channel Section */}
                <div className="console-form-section">
                    <h4 className="console-form-section-title">Communication Channel</h4>
                    <div className="form-grid-3col">
                        <div className="form-group">
                            <label>Account Type *</label>
                            <select
                                value={formData.accountType}
                                onChange={(e) => {
                                    setFormData({...formData, accountType: e.target.value, account: ''});
                                }}
                                required
                                disabled={!isEditing}
                            >
                                {!formData.accountType && (
                                    <option value="">
                                        {(communicationChannels?.emailAccounts?.length > 0 || communicationChannels?.phoneAccounts?.length > 0)
                                            ? 'Select type'
                                            : 'No channels - Click "Communication Channels" to add one'}
                                    </option>
                                )}
                                {communicationChannels?.phoneAccounts?.length > 0 && (
                                    <option value="Call">Call</option>
                                )}
                                {communicationChannels?.emailAccounts?.length > 0 && (
                                    <option value="Email">Email</option>
                                )}
                                {communicationChannels?.phoneAccounts?.length > 0 && (
                                    <option value="Text">Text</option>
                                )}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Account Direction *</label>
                            <select
                                value={formData.accountDirection}
                                onChange={(e) => setFormData({...formData, accountDirection: e.target.value})}
                                required
                                disabled={!isEditing}
                            >
                                {!formData.accountDirection && (
                                    <option value="">Select direction</option>
                                )}
                                <option value="Inbound">Inbound</option>
                                <option value="Outbound">Outbound</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Account *</label>
                            <select
                                value={formData.account}
                                onChange={(e) => setFormData({...formData, account: e.target.value})}
                                required
                                disabled={!isEditing || !formData.accountType}
                            >
                                {!formData.account && (
                                    <option value="">
                                        {!formData.accountType 
                                            ? 'Select account type first' 
                                            : 'Select account'}
                                    </option>
                                )}
                                {formData.accountType === 'Email' && communicationChannels?.emailAccounts?.map((email) => (
                                    <option key={email.id} value={email.id}>
                                        {email.name} - {email.username}
                                    </option>
                                ))}
                                {(formData.accountType === 'Call' || formData.accountType === 'Text') && communicationChannels?.phoneAccounts?.map((phone) => (
                                    <option key={phone.id} value={phone.id}>
                                        {phone.name} - {formatPhoneNumber(phone.phoneNumber)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Communication Originator Section */}
                <div className="console-form-section">
                    <h4 className="console-form-section-title">Communication Originator</h4>
                    <div className="form-grid-2col">
                        <div className="form-group">
                            <label>Client *</label>
                            <div className="originator-select-container">
                                <select
                                    value={formData.client}
                                    onChange={(e) => setFormData({...formData, client: e.target.value})}
                                    required
                                    disabled={!isEditing}
                                    className="originator-select-field"
                                >
                                    {!formData.client && (
                                        <option value="">
                                            {clients && clients.length > 0 
                                                ? 'Select a client' 
                                                : 'No clients found'}
                                        </option>
                                    )}
                                    {clients?.map((cli) => (
                                        <option key={cli.id} value={cli.id}>
                                            {cli.name} - {cli.email}
                                        </option>
                                    ))}
                                </select>
                                {isEditing && (
                                    <button
                                        type="button"
                                        onClick={onOpenClientModal}
                                        className="originator-add-button"
                                        title="Add new client"
                                    >
                                        <FaPlus /> Add
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Location *</label>
                            <div className="originator-select-container">
                                <select
                                    value={formData.location}
                                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                                    required
                                    disabled={!isEditing}
                                    className="originator-select-field"
                                >
                                    {!formData.location && (
                                        <option value="">
                                            {locations && locations.length > 0 
                                                ? 'Select a location' 
                                                : 'No locations found'}
                                        </option>
                                    )}
                                    {locations?.map((loc) => (
                                        <option key={loc.id} value={loc.id}>
                                            {loc.name} - {loc.city}, {loc.state}
                                        </option>
                                    ))}
                                </select>
                                {isEditing && (
                                    <button
                                        type="button"
                                        onClick={onOpenLocationModal}
                                        className="originator-add-button"
                                        title="Add new location"
                                    >
                                        <FaPlus /> Add
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Communication Context Section */}
                <div className="console-form-section">
                    <h4 className="console-form-section-title">Communication Context</h4>
                    
                    <div className="form-group">
                        <label>Context Name *</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            placeholder="Enter a descriptive name for this context"
                            required
                            disabled={!isEditing}
                        />
                    </div>

                    <div className="form-group">
                        <label>Greeting *</label>
                        <div className="flow-input-row">
                            <span className="preset-greeting">
                                Hi, this is Sofie
                            </span>
                            <textarea
                                value={formData.greeting}
                                onChange={(e) => setFormData({...formData, greeting: e.target.value})}
                                placeholder="continue greeting message..."
                                rows={3}
                                style={{height:51}}
                                className="flow-input-field textarea-resizable"
                                required
                                disabled={!isEditing}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Purpose of Communication *</label>
                        <textarea
                            value={formData.purpose}
                            onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                            placeholder="Purpose of this communication"
                            rows={3}
                            className="textarea-resizable"
                            required
                            disabled={!isEditing}
                        />
                    </div>

                    <div className="form-group">
                        <label>General Context *</label>
                        <textarea
                            value={formData.generalContext}
                            onChange={(e) => setFormData({...formData, generalContext: e.target.value})}
                            placeholder="General context information"
                            className="textarea-large"
                            required
                            disabled={!isEditing}
                        />
                    </div>

                    <div className="form-group">
                        <label>Specific Context</label>
                        <textarea
                            value={formData.specificContext}
                            onChange={(e) => setFormData({...formData, specificContext: e.target.value})}
                            placeholder="Specific context details"
                            className="textarea-large"
                            disabled={!isEditing}
                        />
                    </div>

                    <div className="form-group">
                        <label>Flow of conversation *</label>
                        <div className="flow-input-list">
                            {formData.flow.map((flowItem, index) => (
                                <div key={index} className="flow-input-row">
                                    <input
                                        type="text"
                                        value={flowItem}
                                        onChange={(e) => updateFlowItem(index, e.target.value)}
                                        placeholder={`Flow step ${index + 1}`}
                                        className="flow-input-field"
                                        required={index === 0}
                                        disabled={!isEditing}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => moveFlowItem(index, 'up')}
                                        disabled={!isEditing || index === 0}
                                        className="flow-action-button"
                                    >
                                        ↑
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => moveFlowItem(index, 'down')}
                                        disabled={!isEditing || index === formData.flow.length - 1}
                                        className="flow-action-button"
                                    >
                                        ↓
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => removeFlowItem(index)}
                                        disabled={!isEditing || formData.flow.length === 1}
                                        className="flow-remove-button-accent"
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            ))}
                            {isEditing && (
                                <button
                                    type="button"
                                    onClick={addFlowItem}
                                    className="flow-add-button"
                                >
                                    <FaPlus /> Add Flow Step
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

const HistoryHome = ({ historyLogs, filters, onFilterChange, onClearFilters }) => {
    // Calculate stats from history logs
    const totalLogs = historyLogs?.length || 0;
    const callLogs = historyLogs?.filter(log => log.accountType?.toLowerCase() === 'call')?.length || 0;
    const textLogs = historyLogs?.filter(log => log.accountType?.toLowerCase() === 'text')?.length || 0;
    const emailLogs = historyLogs?.filter(log => log.accountType?.toLowerCase() === 'email')?.length || 0;
    
    // Calculate total minutes used
    const totalMinutes = historyLogs?.reduce((sum, log) => {
        const minutes = parseInt(log.minutes) || 0;
        return sum + minutes;
    }, 0) || 0;
    
    // Get today's date range
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayLogs = historyLogs?.filter(log => {
        const logDate = new Date(log.start || log.timestamp || log.createdAt);
        return logDate >= today;
    })?.length || 0;

    return (
        <div className="context-home-container">
            {/* Filter Section */}
            <div className="console-form-section-large">
                <div className="filter-header">
                    <label>Filter History Logs</label>
                    <button 
                        className="btn-clear-filters"
                        onClick={onClearFilters}
                    >
                        Clear All Filters
                    </button>
                </div>
                <div className="filter-grid-8col history-filter-grid">
                    <div className="form-group">
                        <label>To / From</label>
                        <input
                            type="text"
                            placeholder="Search to/from..."
                            value={filters.phoneNumber}
                            onChange={(e) => onFilterChange('phoneNumber', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Date From</label>
                        <input
                            type="date"
                            value={filters.dateFrom}
                            onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Date To</label>
                        <input
                            type="date"
                            value={filters.dateTo}
                            onChange={(e) => onFilterChange('dateTo', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Account Type</label>
                        <select
                            value={filters.accountType}
                            onChange={(e) => onFilterChange('accountType', e.target.value)}
                        >
                            <option value="">All Types</option>
                            <option value="call">Call</option>
                            <option value="text">Text</option>
                            <option value="email">Email</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Direction</label>
                        <select
                            value={filters.direction}
                            onChange={(e) => onFilterChange('direction', e.target.value)}
                        >
                            <option value="">All Directions</option>
                            <option value="inbound">Inbound</option>
                            <option value="outbound">Outbound</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Account</label>
                        <select
                            value={filters.account}
                            onChange={(e) => onFilterChange('account', e.target.value)}
                        >
                            <option value="">All Accounts</option>
                            {historyLogs && [...new Set(historyLogs.map(log => log.account?.name).filter(Boolean))].sort().map(accountName => (
                                <option key={accountName} value={accountName}>
                                    {accountName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Context</label>
                        <select
                            value={filters.context}
                            onChange={(e) => onFilterChange('context', e.target.value)}
                        >
                            <option value="">All Contexts</option>
                            {historyLogs && [...new Set(historyLogs.map(log => log.context?.name).filter(Boolean))].sort().map(contextName => (
                                <option key={contextName} value={contextName}>
                                    {contextName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Status</label>
                        <select
                            value={filters.requiresFollowUp}
                            onChange={(e) => onFilterChange('requiresFollowUp', e.target.value)}
                        >
                            <option value="">All</option>
                            <option value="yes">Requires Attention</option>
                            <option value="no">No Attention Needed</option>
                            <option value="done">Done</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Overview Section */}
            <div className="overview-welcome">
                <FaClockRotateLeft className="welcome-icon" />
                <h3>Welcome to History Logs</h3>
                <p>
                    View and filter your interaction history across all communication channels. 
                    Select a log from the left sidebar to view detailed information.
                </p>
                <p>
                    Use the filters above to narrow down your search by date range, account type, direction, follow-up needs, or status.
                </p>
                <div className="overview-stats">
                    <div className="stat-card">
                        <FaClockRotateLeft className="stat-icon" />
                        <h4>Total Logs</h4>
                        <p className="stat-number">
                            {totalLogs}
                        </p>
                        <p className="stat-label">All time interactions</p>
                    </div>
                    <div className="stat-card">
                        <FaCalendarDay className="stat-icon" />
                        <h4>Today's Activity</h4>
                        <p className="stat-number">
                            {todayLogs}
                        </p>
                        <p className="stat-label">Interactions today</p>
                    </div>
                    <div className="stat-card">
                        <FaPhone className="stat-icon" />
                        <h4>Calls</h4>
                        <p className="stat-number">
                            {callLogs}
                        </p>
                        <p className="stat-label">Total call logs</p>
                    </div>
                    <div className="stat-card">
                        <FaComment className="stat-icon" />
                        <h4>Messages</h4>
                        <p className="stat-number">
                            {textLogs}
                        </p>
                        <p className="stat-label">Total text logs</p>
                    </div>
                    <div className="stat-card">
                        <FaEnvelope className="stat-icon" />
                        <h4>Emails</h4>
                        <p className="stat-number">
                            {emailLogs}
                        </p>
                        <p className="stat-label">Total email logs</p>
                    </div>
                    <div className="stat-card">
                        <FaClock className="stat-icon" />
                        <h4>Minutes Used</h4>
                        <p className="stat-number">
                            {totalMinutes}
                        </p>
                        <p className="stat-label">Total duration</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ClientHistory = ({userData, selectedTenant, accessToken}) => {
    const [historyRecords, setHistoryRecords] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [historyDetailView, setHistoryDetailView] = useState('transcript');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterObject, setFilterObject] = useState({});
    const [filters, setFilters] = useState({
        dateFrom: '',
        dateTo: '',
        accountType: '',
        direction: '',
        account: '',
        context: '',
        phoneNumber: '',
        requiresFollowUp: ''
    });
    const [showFollowUpModal, setShowFollowUpModal] = useState(false);
    const [followUpMode, setFollowUpMode] = useState(null); // 'address' | 'requiresAttention'
    const [followUpNotes, setFollowUpNotes] = useState('');
    const [followUpSuccess, setFollowUpSuccess] = useState(null);
    const [followUpError, setFollowUpError] = useState(null);

    const getSummaryText = (summary) => {
        if (summary === null || summary === undefined) return '';
        if (typeof summary === 'string') return summary.trim();
        if (typeof summary === 'object') {
            if (typeof summary.summary === 'string') return summary.summary.trim();
            return JSON.stringify(summary, null, 2);
        }
        return String(summary).trim();
    };

    const getTranscriptText = (transcript) => {
        if (!transcript) return '';
        if (typeof transcript === 'object') return transcript.transcript || '';
        return transcript;
    };

    useEffect(() => {
        if (selectedTenant && accessToken) {
            loadHistoryLogs();
        }
    }, [selectedTenant, accessToken]);

    useEffect(() => {
        if (!selectedRecord) {
            setHistoryDetailView('transcript');
            return;
        }

        const summaryText = getSummaryText(selectedRecord.summary);
        setHistoryDetailView(summaryText ? 'summary' : 'transcript');
    }, [selectedRecord]);

    const loadHistoryLogs = async () => {
        setIsLoading(true);
        setError(null);
        await getHistoryLogs(
            selectedTenant.id,
            filterObject,
            accessToken,
            (data) => {
                // Transform the data to match the structure specified
                const transformedData = data.map(entry => {
                    // Parse method field - it contains just direction like "inbound" or "outbound"
                    const direction = (entry.method || '').toLowerCase();
                    
                    // Determine account type based on available data
                    // If we have phone numbers, it's likely a call (has transcript) or text (no transcript/short)
                    // If we have email fields, it's an email
                    let accountType = '';
                    if (entry.from && entry.to && (entry.from.includes('+') || entry.to.includes('+'))) {
                        // Phone number detected
                        if (entry.transcript) {
                            accountType = 'call';
                        } else {
                            accountType = 'text';
                        }
                    } else if (entry.from && entry.to && (entry.from.includes('@') || entry.to.includes('@'))) {
                        accountType = 'email';
                    } else {
                        // Default to call if can't determine
                        accountType = 'call';
                    }

                    console.log(entry)
                    
                    return {
                        id: entry.id,
                        tenant: entry.tenant,
                        to: entry.to,
                        from: entry.from,
                        start: entry.start,
                        end: entry.end,
                        transcript: entry.transcript,
                        method: entry.method,
                        direction: direction,
                        accountType: accountType,
                        actionsTaken: entry.actionsTaken,
                        integrationsUsed: entry.integrationsUsed,
                        minutes: entry.minutes,
                        period: entry.period,
                        flag: entry.flag,
                        flagReason: entry.flagReason,
                        flagDate: entry.flagDate,
                        flagBy: entry.flagBy,
                        solution: entry.solution,
                        solutionDate: entry.solutionDate,
                        solutionBy: entry.solutionBy,
                        actionTaken: entry.actionTaken,
                        actions: entry.actions,
                        integrations: entry.integrations,
                        context: entry.context,
                        account: entry.account,
                        summary: entry.summary,
                        requiresFollowUp: entry.requiresFollowUp,
                        followedUp: entry.followedUp,
                        followedUpBy: entry.followedUpBy,
                        followedUpDate: entry.followedUpDate,
                        followedUpNotes: entry.followedUpNotes,
                        requestedFollowUp: entry.requestedFollowUp,
                    };
                });
                console.log('History records loaded:', transformedData);
                setHistoryRecords(transformedData);
                setIsLoading(false);
            },
            (err) => {
                setError(err);
                setIsLoading(false);
            }
        );
    };

    const handleSelectRecord = (record) => {
        setSelectedRecord(record);
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const formatDuration = (minutes) => {
        if (!minutes) return 'N/A';
        const hrs = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
    };

    const requiresFollowUp = (record) => {
        const value = record?.requiresFollowUp;
        return value === true || value === 1 || value === '1' || value === 'true';
    };

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };

    const handleClearFilters = () => {
        setFilters({
            dateFrom: '',
            dateTo: '',
            accountType: '',
            direction: '',
            account: '',
            context: '',
            phoneNumber: '',
            requiresFollowUp: ''
        });
    };

    const openFollowUpModal = (mode) => {
        setFollowUpMode(mode);
        setFollowUpNotes('');
        setFollowUpSuccess(null);
        setFollowUpError(null);
        setShowFollowUpModal(true);
    };

    const closeFollowUpModal = () => {
        setShowFollowUpModal(false);
        setFollowUpMode(null);
        setFollowUpNotes('');
        setFollowUpSuccess(null);
        setFollowUpError(null);
    };

    const handleFollowUpSubmit = async () => {
        if (!selectedRecord) return;
        setFollowUpSuccess(null);
        setFollowUpError(null);
        if (followUpMode === 'address') {
            await markHistoryFollowedUp(
                selectedTenant.id,
                selectedRecord.id,
                userData.user.id,
                followUpNotes,
                accessToken,
                async (data) => {
                    setFollowUpSuccess('Record marked as followed up.');
                    // Update the record in the list
                    setHistoryRecords(prev => prev.map(r => r.id === selectedRecord.id ? { ...r, followedUp: true } : r));
                    setSelectedRecord(prev => ({ ...prev, followedUp: true }));
                    // Refresh the history logs to ensure all details are updated
                    await loadHistoryLogs();
                    setTimeout(() => closeFollowUpModal(), 1000);
                },
                (err) => setFollowUpError(err)
            );
        } else {
            await markHistoryRequiringFollowUp(
                selectedTenant.id,
                selectedRecord.id,
                userData.user.id,
                followUpNotes,
                accessToken,
                async (data) => {
                    setFollowUpSuccess('Record marked as requiring attention.');
                    // Update the record in the list
                    setHistoryRecords(prev => prev.map(r => r.id === selectedRecord.id ? { ...r, requiresFollowUp: true, followedUp: false } : r));
                    setSelectedRecord(prev => ({ ...prev, requiresFollowUp: true, followedUp: false }));
                    // Refresh the history logs to ensure all details are updated
                    await loadHistoryLogs();
                    setTimeout(() => closeFollowUpModal(), 1000);
                },
                (err) => setFollowUpError(err)
            );
        }
    };

    const getFilteredRecords = () => {
        if (!historyRecords) return [];
        
        const filtered = historyRecords.filter(record => {
            // Date From filter
            if (filters.dateFrom) {
                const recordDate = new Date(record.start);
                // Create filterDate and set to start of day in local timezone
                const filterDate = new Date(filters.dateFrom + 'T00:00:00');
                if (recordDate < filterDate) return false;
            }
            
            // Date To filter
            if (filters.dateTo) {
                const recordDate = new Date(record.start);
                // Create filterDate and set to end of day in local timezone
                const filterDate = new Date(filters.dateTo + 'T23:59:59.999');
                if (recordDate > filterDate) return false;
            }
            
            // Account Type filter
            if (filters.accountType) {
                const recordType = (record.accountType || '').toLowerCase();
                if (recordType !== filters.accountType.toLowerCase()) {
                    return false;
                }
            }
            
            // Direction filter
            if (filters.direction) {
                const recordDirection = (record.direction || '').toLowerCase();
                if (recordDirection !== filters.direction.toLowerCase()) {
                    return false;
                }
            }
            
            // Account filter
            if (filters.account) {
                const recordAccountName = record.account?.name || '';
                if (recordAccountName !== filters.account) {
                    return false;
                }
            }
            
            // Context filter
            if (filters.context) {
                const recordContextName = record.context?.name || '';
                if (recordContextName !== filters.context) {
                    return false;
                }
            }

            // Requires Follow-Up filter
            if (filters.requiresFollowUp) {
                const recordRequiresFollowUp = requiresFollowUp(record);
                if (filters.requiresFollowUp === 'yes' && !(recordRequiresFollowUp && !record.followedUp)) {
                    return false;
                }
                if (filters.requiresFollowUp === 'no' && recordRequiresFollowUp) {
                    return false;
                }
                if (filters.requiresFollowUp === 'done' && !record.followedUp) {
                    return false;
                }
            }
            
            // Phone Number filter (searches both to and from fields)
            if (filters.phoneNumber) {
                // Strip formatting characters for comparison
                const stripFormatting = (str) => str.replace(/[\s\-()]/g, '');
                const fromNumber = stripFormatting((record.from || '').toLowerCase());
                const toNumber = stripFormatting((record.to || '').toLowerCase());
                const searchValue = stripFormatting(filters.phoneNumber.toLowerCase());
                if (!fromNumber.includes(searchValue) && !toNumber.includes(searchValue)) {
                    return false;
                }
            }
            
            return true;
        });
        return filtered;
    };

    const filteredRecords = getFilteredRecords();

    // Group records by accountType and direction
    const groupedRecords = filteredRecords.reduce((acc, record) => {
        const accountType = (record.context?.accountType || record.accountType || 'call').toLowerCase();
        const direction = (record.context?.direction || record.direction || 'inbound').toLowerCase();
        
        if (!acc[accountType]) {
            acc[accountType] = {};
        }
        if (!acc[accountType][direction]) {
            acc[accountType][direction] = [];
        }
        acc[accountType][direction].push(record);
        return acc;
    }, {});

    // Sort order for account types and directions
    const accountTypeOrder = ['call', 'text', 'email'];
    const directionOrder = ['inbound', 'outbound'];

    return (
        <div className="communication-channels">
            {/* Left Sidebar */}
            <div className="channels-sidebar">
                <div className="sidebar-header">
                    <h3>History Logs</h3>
                    <button 
                        className="add-channel-btn"
                        onClick={loadHistoryLogs}
                        title="Refresh History"
                        style={{ marginLeft: 'auto' }}
                    >
                        <FaRotate />
                    </button>
                </div>
                <div className="sidebar-content">
                    {isLoading ? (
                        <div className="loading-container">
                            <p>Loading history logs...</p>
                        </div>
                    ) : filteredRecords && filteredRecords.length > 0 ? (
                        accountTypeOrder.map(accountType => {
                            if (!groupedRecords[accountType]) return null;
                            
                            // Get the appropriate icon for the account type
                            const getAccountTypeIcon = (type) => {
                                switch(type) {
                                    case 'call': return <FaPhone className="section-header-icon" />;
                                    case 'text': return <FaComment className="section-header-icon" />;
                                    case 'email': return <FaEnvelope className="section-header-icon" />;
                                    default: return <FaClockRotateLeft className="section-header-icon" />;
                                }
                            };
                            
                            return (
                                <div key={accountType} className="channel-section">
                                    <div className="justified-section-header">
                                        {getAccountTypeIcon(accountType)}
                                        <h4>{accountType.charAt(0).toUpperCase() + accountType.slice(1)}</h4>
                                    </div>
                                    
                                    {directionOrder.map(direction => {
                                        const records = groupedRecords[accountType][direction];
                                        if (!records || records.length === 0) return null;
                                        
                                        return (
                                            <div key={direction}>
                                                <div className="direction-subheader">
                                                    {direction.charAt(0).toUpperCase() + direction.slice(1)}
                                                </div>
                                                <div className="channel-list">
                                                    {records.map((record) => (
                                                        <div
                                                            key={record.id}
                                                            className={`channel-item ${selectedRecord?.id === record.id ? 'selected' : ''}`}
                                                            onClick={() => handleSelectRecord(record)}
                                                        >
                                                            <div className={`history-follow-up-symbol ${(() => {
                                                                if (record.followedUp) return 'followed-up';
                                                                return requiresFollowUp(record) ? 'requires-follow-up' : 'no-follow-up';
                                                            })()}`}>
                                                                {(() => {
                                                                    if (record.followedUp) return <FaCheck />;
                                                                    return requiresFollowUp(record) ? <FaCircleExclamation /> : <FaMinus />;
                                                                })()}
                                                            </div>
                                                            <div className="channel-info">
                                                                <p className="channel-name">
                                                                    {record.method || 'Call'} {record.context?.accountType || record.accountType || 'call'} - {record.context?.account?.name || record.account?.name || 'Unknown'}
                                                                </p>
                                                                <p className="channel-detail">
                                                                    {record.direction?.toLowerCase() === 'inbound' 
                                                                        ? `from ${(['Call', 'Text', 'call', 'text'].includes(record.context?.accountType || record.accountType) && record.from ? formatPhoneNumber(record.from) : record.from) || 'Unknown'}` 
                                                                        : `to ${(['Call', 'Text', 'call', 'text'].includes(record.context?.accountType || record.accountType) && record.to ? formatPhoneNumber(record.to) : record.to) || 'Unknown'}`}
                                                                </p>
                                                                <p className="channel-detail">
                                                                    {formatDateTime(record.start)} • {formatDuration(record.minutes)}
                                                                </p>
                                                            </div>
                                                            {record.flag && (
                                                                <FaCircleExclamation className="channel-status-icon status-unverified" />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })
                    ) : (
                        <div className="empty-state">
                            <FaClockRotateLeft className="empty-state-icon" />
                            <p className="empty-state-text">No history logs found</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Right Detail Panel */}
            <div className="channels-detail">
                {selectedRecord ? (
                    <>
                        <div className="detail-toolbar">
                            <div className="detail-toolbar-left">
                                <h3 style={{ margin: 0, color: 'var(--primary-color)' }}>
                                    History Management
                                </h3>
                            </div>
                            <div className="detail-toolbar-right">
                                {requiresFollowUp(selectedRecord) && !selectedRecord.followedUp ? (
                                    <button
                                        className="toolbar-btn toolbar-btn-highlight-edit"
                                        onClick={() => openFollowUpModal('address')}
                                    >
                                        <FaCircleExclamation className="toolbar-btn-icon" />
                                        Addresss Follow-Up
                                    </button>
                                ) : (
                                    <button
                                        className="toolbar-btn"
                                        onClick={() => openFollowUpModal('requiresAttention')}
                                    >
                                        Mark Requiring Attention
                                    </button>
                                )}
                                <button 
                                    className="toolbar-btn toolbar-btn-close"
                                    onClick={() => setSelectedRecord(null)}
                                >
                                    <FaXmark className="toolbar-btn-icon" />
                                    Close
                                </button>
                            </div>
                        </div>
                        <div className="detail-content">
                            {selectedRecord.flag && (
                                <div className="verification-warning">
                                    <FaCircleExclamation className="warning-icon" />
                                    <span><strong>Flagged:</strong> {selectedRecord.flagReason || 'No reason provided'}</span>
                                </div>
                            )}
                            
                            <div className="history-detail-layout">
                                {/* Information Pane */}
                                <div className="history-info-pane">
                                    {selectedRecord.followedUp ? (
                                        <div className="history-follow-up-banner history-follow-up-banner-complete">
                                            <FaCheck className="history-follow-up-banner-icon" />
                                            <span>Marked Complete</span>
                                        </div>
                                    ) : requiresFollowUp(selectedRecord) ? (
                                        <div className="history-follow-up-banner">
                                            <FaCircleExclamation className="history-follow-up-banner-icon" />
                                            <span>Attention Required.</span>
                                        </div>
                                    ) : null}

                                    {/* Follow-Up Information Section */}
                                    {(selectedRecord.followedUpBy || selectedRecord.requestedFollowUp || selectedRecord.followedUpDate || selectedRecord.followedUpNotes) && (
                                        <div className="detail-section">
                                            <h4 className="console-form-section-title">Follow-Up Information</h4>
                                            <div className="detail-form">
                                                
                                                {selectedRecord.requestedFollowUp && (
                                                    <div className="form-group">
                                                        <label>Requested Follow-Up</label>
                                                        <input
                                                            type="text"
                                                            value={typeof selectedRecord.requestedFollowUp === 'object' ? `${selectedRecord.requestedFollowUp.firstName} ${selectedRecord.requestedFollowUp.lastName} (${selectedRecord.requestedFollowUp.email})` : selectedRecord.requestedFollowUp}
                                                            disabled
                                                        />
                                                    </div>
                                                )}
                                                {selectedRecord.followedUpBy && (
                                                    <div className="form-group">
                                                        <label>Followed Up By</label>
                                                        <input
                                                            type="text"
                                                            value={typeof selectedRecord.followedUpBy === 'object' ? `${selectedRecord.followedUpBy.firstName} ${selectedRecord.followedUpBy.lastName} (${selectedRecord.followedUpBy.email})` : selectedRecord.followedUpBy}
                                                            disabled
                                                        />
                                                    </div>
                                                )}
                                                {selectedRecord.followedUpDate && (
                                                    <div className="form-group">
                                                        <label>Followed Up Date</label>
                                                        <input
                                                            type="text"
                                                            value={formatDateTime(selectedRecord.followedUpDate)}
                                                            disabled
                                                        />
                                                    </div>
                                                )}
                                                {selectedRecord.followedUpNotes && (
                                                    <div className="form-group">
                                                        <label>Follow Up Notes</label>
                                                        <textarea
                                                            value={selectedRecord.followedUpNotes}
                                                            disabled
                                                            rows={3}
                                                            className="textarea-resizable"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Communication Channel Section */}
                                    <div className="detail-section">
                                        <h4 className="console-form-section-title">History Details</h4>
                                        <div className="detail-form">
                                            <div className="form-group">
                                                <label>Context Used</label>
                                                <input
                                                    type="text"
                                                    value={selectedRecord.context?.name || 'N/A'}
                                                    disabled
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Type</label>
                                                <input
                                                    type="text"
                                                    value={selectedRecord.context?.accountType || 'N/A'}
                                                    disabled
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Account</label>
                                                <input
                                                    type="text"
                                                    value={selectedRecord.context?.account?.name || selectedRecord.account?.name || 'N/A'}
                                                    disabled
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Method</label>
                                                <input
                                                    type="text"
                                                    value={selectedRecord.method || 'N/A'}
                                                    disabled
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>From</label>
                                                <input
                                                    type="text"
                                                    value={
                                                        selectedRecord.from 
                                                            ? (['Call', 'Text', 'call', 'text'].includes(selectedRecord.context?.accountType || selectedRecord.accountType) 
                                                                ? formatPhoneNumber(selectedRecord.from) 
                                                                : selectedRecord.from)
                                                            : 'N/A'
                                                    }
                                                    disabled
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>To</label>
                                                <input
                                                    type="text"
                                                    value={
                                                        selectedRecord.to 
                                                            ? (['Call', 'Text', 'call', 'text'].includes(selectedRecord.context?.accountType || selectedRecord.accountType) 
                                                                ? formatPhoneNumber(selectedRecord.to) 
                                                                : selectedRecord.to)
                                                            : 'N/A'
                                                    }
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Communication Details Section */}
                                    <div className="detail-section">
                                        <div className="detail-form">
                                            <div className="form-group">
                                                <label>Start Time</label>
                                                <input
                                                    type="text"
                                                    value={formatDateTime(selectedRecord.start)}
                                                    disabled
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>End Time</label>
                                                <input
                                                    type="text"
                                                    value={formatDateTime(selectedRecord.end)}
                                                    disabled
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Duration</label>
                                                <input
                                                    type="text"
                                                    value={formatDuration(selectedRecord.minutes)}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Integrations Used Section */}
                                    <div className="detail-section">
                                        <h4 className="console-form-section-title">Integrations Used</h4>
                                        <div className="detail-form">
                                            {selectedRecord.integrations || selectedRecord.integrationsUsed ? (
                                                <div className="form-group">
                                                    <textarea
                                                        value={selectedRecord.integrations || selectedRecord.integrationsUsed}
                                                        disabled
                                                        rows={3}
                                                        className="textarea-resizable"
                                                    />
                                                </div>
                                            ) : (
                                                <p style={{ color: '#999', fontStyle: 'italic', margin: '0.5rem 0' }}>None used</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Actions Taken Section */}
                                    <div className="detail-section">
                                        <h4 className="console-form-section-title">Actions Taken</h4>
                                        <div className="detail-form">
                                            {selectedRecord.actions || selectedRecord.actionsTaken ? (
                                                <div className="form-group">
                                                    <textarea
                                                        value={selectedRecord.actions || selectedRecord.actionsTaken}
                                                        disabled
                                                        rows={4}
                                                        className="textarea-resizable"
                                                    />
                                                </div>
                                            ) : (
                                                <p style={{ color: '#999', fontStyle: 'italic', margin: '0.5rem 0' }}>None used</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Transcript Pane */}
                                <div className="history-transcript-pane">
                                    <div className="detail-section">
                                        {(() => {
                                            const summaryText = getSummaryText(selectedRecord.summary);
                                            const transcriptText = getTranscriptText(selectedRecord.transcript);
                                            const hasSummary = Boolean(summaryText);

                                            return (
                                                <>
                                                    <div className="history-detail-tabs">
                                                        {hasSummary && (
                                                            <button
                                                                type="button"
                                                                className={`history-detail-tab ${historyDetailView === 'summary' ? 'active' : ''}`}
                                                                onClick={() => setHistoryDetailView('summary')}
                                                            >
                                                                Summary
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className={`history-detail-tab ${historyDetailView === 'transcript' ? 'active' : ''}`}
                                                            onClick={() => setHistoryDetailView('transcript')}
                                                        >
                                                            Transcript
                                                        </button>
                                                    </div>

                                                    {historyDetailView === 'summary' && hasSummary ? (
                                                        <div className="history-summary-container">
                                                            {summaryText}
                                                        </div>
                                                    ) : (
                                                        <div className="transcript-chat-container">
                                                            {(() => {
                                                                if (!transcriptText) return <p>No transcript available</p>;

                                                                const messages = transcriptText
                                                                    .split('\n')
                                                                    .filter(line => line.trim())
                                                                    .map((line, index) => {
                                                                        const match = line.match(/^(assistant|user):\s*(.+)$/);
                                                                        if (match) {
                                                                            return {
                                                                                id: index,
                                                                                role: match[1],
                                                                                message: match[2]
                                                                            };
                                                                        }
                                                                        return null;
                                                                    })
                                                                    .filter(msg => msg !== null);

                                                                return messages.map(msg => (
                                                                    <div
                                                                        key={msg.id}
                                                                        className={`chat-message ${msg.role === 'assistant' ? 'assistant-message' : 'user-message'}`}
                                                                    >
                                                                        <div className="chat-message-content">
                                                                            {msg.message}
                                                                        </div>
                                                                    </div>
                                                                ));
                                                            })()}
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>


                        </div>
                    </>
                ) : (
                    <HistoryHome 
                        historyLogs={filteredRecords}
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onClearFilters={handleClearFilters}
                    />
                )}
            </div>

            {/* Follow-Up / Requires Attention Modal */}
            {showFollowUpModal && (
                <div className="modal-overlay" onClick={closeFollowUpModal}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{followUpMode === 'address' ? 'Address Follow-Up' : 'Mark as Requiring Attention'}</h2>
                            <button className="modal-close" onClick={closeFollowUpModal}>
                                <FaXmark />
                            </button>
                        </div>
                        <div className="modal-form">
                            <div className="form-group">
                                <label>Notes (optional)</label>
                                <textarea
                                    value={followUpNotes}
                                    onChange={(e) => setFollowUpNotes(e.target.value)}
                                    placeholder={followUpMode === 'address' ? 'Add any comments about how this was addressed...' : 'Add any notes about why this requires attention...'}
                                    rows={4}
                                    style={{ width: '100%', padding: '0.75rem', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '1rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                                />
                            </div>
                            {followUpSuccess && (
                                <div style={{ color: 'green', marginBottom: '1rem', fontSize: '0.95rem' }}>
                                    {followUpSuccess}
                                </div>
                            )}
                            {followUpError && (
                                <div style={{ color: 'red', marginBottom: '1rem', fontSize: '0.95rem' }}>
                                    {followUpError}
                                </div>
                            )}
                        </div>
                        <div className="modal-actions">
                            <button className="btn-primary" onClick={handleFollowUpSubmit}>
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ClientCommunicationChannels = ({selectedTenant, accessToken}) => {
    const [communicationLanes, setCommunicationLanes] = useState({ emailAccounts: [], phoneAccounts: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [selectedChannelType, setSelectedChannelType] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    
    const [formData, setFormData] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);

    const validateForm = () => {
        if (!formData) return [];
        
        const errors = [];
        
        if (selectedChannelType === 'email') {
            if (!formData.name || formData.name.trim() === '') {
                errors.push('Account Name');
            }
            if (!formData.smtpServer || formData.smtpServer.trim() === '') {
                errors.push('SMTP Server');
            }
            if (!formData.port || formData.port <= 0) {
                errors.push('Port');
            }
            if (!formData.username || formData.username.trim() === '') {
                errors.push('Username');
            }
            if (!formData.password || formData.password.trim() === '') {
                errors.push('Password');
            }
        } else if (selectedChannelType === 'phone') {
            if (!formData.name || formData.name.trim() === '') {
                errors.push('Account Name');
            }
            if (!formData.phoneNumber || formData.phoneNumber.trim() === '') {
                errors.push('Phone Number');
            }
            if (!formData.provider || formData.provider.trim() === '') {
                errors.push('Provider');
            }
            if (!formData.accountSID || formData.accountSID.trim() === '') {
                errors.push('Account SID');
            }
            if (!formData.authToken || formData.authToken.trim() === '') {
                errors.push('Auth Token');
            }
        }
        
        return errors;
    };

    useEffect(() => {
        if (isEditing && formData) {
            setValidationErrors(validateForm());
        } else {
            setValidationErrors([]);
        }
    }, [formData, isEditing, selectedChannelType]);

    useEffect(() => {
        setIsLoading(true);
        getTenantsCommunicationChannels(selectedTenant.id, accessToken, (data) => {
            setCommunicationLanes(data);
            setIsLoading(false);
        }, (err) => {
            setError(err);
            setIsLoading(false);
        });
    }, [selectedTenant, accessToken]);

    const emailLaneTemplate = {
        name: "",
        type: "email",
        smtpServer: "",
        port: 587,
        useSSL: true,
        username: "",
        password: ""
    };

    const phoneLaneTemplate = {
        name: "",
        type: "phone",
        phoneNumber: "",
        provider: "Twilio",
        accountSID: "",
        authToken: ""
    };

    const handleSelectChannel = (channel, type) => {
        setSelectedChannel(channel);
        setSelectedChannelType(type);
        setFormData(channel);
        setIsEditing(false);
        setStatusMessage(null);
    };

    const handleAddChannel = (type) => {
        const template = type === 'email' ? emailLaneTemplate : phoneLaneTemplate;
        setSelectedChannel(null);
        setSelectedChannelType(type);
        setFormData(template);
        setIsEditing(true);
        setStatusMessage(null);
    };

    const handleEdit = () => {
        setIsEditing(true);
        setStatusMessage(null);
    };

    const handleCancel = () => {
        if (selectedChannel) {
            setFormData(selectedChannel);
            setIsEditing(false);
        } else {
            setSelectedChannel(null);
            setSelectedChannelType(null);
            setFormData(null);
            setIsEditing(false);
        }
        setStatusMessage(null);
    };

    const handleSave = async () => {
        setIsSaving(true);
        setStatusMessage(null);

        if (selectedChannel && selectedChannel.id) {
            // Update existing channel
            await updateCommunicationChannel(
                selectedTenant.id,
                selectedChannel.id,
                selectedChannelType,
                formData,
                accessToken,
                (data) => {
                    setStatusMessage({ type: 'success', text: 'Channel updated successfully!' });
                    setIsEditing(false);
                    // Refresh the list
                    getTenantsCommunicationChannels(selectedTenant.id, accessToken, (data) => {
                        setCommunicationLanes(data);
                        // Update selected channel
                        const updatedChannel = selectedChannelType === 'email' 
                            ? data.emailAccounts.find(ch => ch.id === selectedChannel.id)
                            : data.phoneAccounts.find(ch => ch.id === selectedChannel.id);
                        setSelectedChannel(updatedChannel);
                        setFormData(updatedChannel);
                    }, setError);
                    setIsSaving(false);
                },
                (err) => {
                    setStatusMessage({ type: 'error', text: err });
                    setIsSaving(false);
                }
            );
        } else {
            // Add new channel
            await addCommunicationChannel(
                selectedTenant.id,
                formData,
                accessToken,
                (data) => {
                    setStatusMessage({ type: 'success', text: 'Channel added successfully!' });
                    setIsEditing(false);
                    // Refresh the list
                    getTenantsCommunicationChannels(selectedTenant.id, accessToken, (data) => {
                        setCommunicationLanes(data);
                        // Select the newly added channel
                        const newChannels = selectedChannelType === 'email' ? data.emailAccounts : data.phoneAccounts;
                        const newChannel = newChannels[newChannels.length - 1];
                        setSelectedChannel(newChannel);
                        setFormData(newChannel);
                    }, setError);
                    setIsSaving(false);
                },
                (err) => {
                    setStatusMessage({ type: 'error', text: err });
                    setIsSaving(false);
                }
            );
        }
    };

    const handleDelete = async () => {
        if (!selectedChannel || !selectedChannel.id) return;
        
        if (!window.confirm(`Are you sure you want to delete "${selectedChannel.name}"?`)) return;

        setIsSaving(true);
        await deleteCommunicationChannel(
            selectedTenant.id,
            selectedChannel.id,
            selectedChannelType,
            accessToken,
            (data) => {
                setStatusMessage({ type: 'success', text: 'Channel deleted successfully!' });
                // Refresh the list
                getTenantsCommunicationChannels(selectedTenant.id, accessToken, (data) => {
                    setCommunicationLanes(data);
                    setSelectedChannel(null);
                    setSelectedChannelType(null);
                    setFormData(null);
                    setIsEditing(false);
                }, setError);
                setIsSaving(false);
            },
            (err) => {
                setStatusMessage({ type: 'error', text: err });
                setIsSaving(false);
            }
        );
    };

    const handleTest = async () => {
        if (!selectedChannel || !selectedChannel.id) return;

        setIsSaving(true);
        setStatusMessage(null);
        await testCommunicationChannel(
            selectedTenant.id,
            selectedChannel.id,
            selectedChannelType,
            accessToken,
            (data) => {
                setStatusMessage({ type: 'success', text: 'Test successful! Channel is working correctly.' });
                // Refresh the list to update connection status
                getTenantsCommunicationChannels(selectedTenant.id, accessToken, (data) => {
                    setCommunicationLanes(data);
                    // Update selected channel with fresh data
                    const updatedChannel = selectedChannelType === 'email' 
                        ? data.emailAccounts.find(ch => ch.id === selectedChannel.id)
                        : data.phoneAccounts.find(ch => ch.id === selectedChannel.id);
                    setSelectedChannel(updatedChannel);
                    setFormData(updatedChannel);
                }, setError);
                setIsSaving(false);
            },
            (err) => {
                setStatusMessage({ type: 'error', text: `Test failed: This communication channel is not configured properly.Please verify this information with your provider, then click the edit button to reenter the information and try again.` });
                // Refresh the list even on failure to update connection status
                getTenantsCommunicationChannels(selectedTenant.id, accessToken, (data) => {
                    setCommunicationLanes(data);
                    // Update selected channel with fresh data
                    const updatedChannel = selectedChannelType === 'email' 
                        ? data.emailAccounts.find(ch => ch.id === selectedChannel.id)
                        : data.phoneAccounts.find(ch => ch.id === selectedChannel.id);
                    setSelectedChannel(updatedChannel);
                    setFormData(updatedChannel);
                }, setError);
                setIsSaving(false);
            }
        );
    };

    const handleFormChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    return (
        <div className="communication-channels">
            {/* Left Sidebar */}
            <div className="channels-sidebar">
                <div className="sidebar-header">
                    <h3>Communication Channels</h3>
                </div>
                <div className="sidebar-content">
                    {isLoading ? (
                        <div className="loading-container">
                            <p>Loading communication channels...</p>
                        </div>
                    ) : (
                        <>
                    {/* Email Accounts Section */}
                    <div className="channel-section">
                        <div className="spaced-section-header">
                            <h4 className="channel-section-title">Email Accounts</h4>
                            <button 
                                className="add-channel-btn"
                                onClick={() => handleAddChannel('email')}
                                title="Add Email Account"
                            >
                                <FaPlus />
                            </button>
                        </div>
                        <div className="channel-list">
                            {communicationLanes.emailAccounts && communicationLanes.emailAccounts.length > 0 ? (
                                communicationLanes.emailAccounts.map((channel) => (
                                    <div
                                        key={channel.id}
                                        className={`channel-item communication-channel-item ${selectedChannel?.id === channel.id && selectedChannelType === 'email' ? 'selected' : ''}`}
                                        onClick={() => handleSelectChannel(channel, 'email')}
                                    >
                                        <div className="channel-item-content communication-channel-item-content">
                                            <FaEnvelope className="channel-icon" />
                                            <div className="channel-info">
                                                <p className="channel-name">{channel.name}</p>
                                                <p className="channel-detail">{channel.username || channel.smtpServer}</p>
                                            </div>
                                        </div>
                                        {channel.connected === true ? (
                                            <FaCircleCheck className="channel-status-icon status-verified" />
                                        ) : (
                                            <FaCircleExclamation className="channel-status-icon status-unverified" />
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="empty-state">
                                    <FaEnvelope className="empty-state-icon" />
                                    <p className="empty-state-text">No email accounts configured</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Phone Accounts Section */}
                    <div className="channel-section">
                        <div className="spaced-section-header">
                            <h4 className="channel-section-title">Phone Accounts</h4>
                            <button 
                                className="add-channel-btn"
                                onClick={() => handleAddChannel('phone')}
                                title="Add Phone Account"
                            >
                                <FaPlus />
                            </button>
                        </div>
                        <div className="channel-list">
                            {communicationLanes.phoneAccounts && communicationLanes.phoneAccounts.length > 0 ? (
                                communicationLanes.phoneAccounts.map((channel) => (
                                    <div
                                        key={channel.id}
                                        className={`channel-item communication-channel-item ${selectedChannel?.id === channel.id && selectedChannelType === 'phone' ? 'selected' : ''}`}
                                        onClick={() => handleSelectChannel(channel, 'phone')}
                                    >
                                        <div className="channel-item-content communication-channel-item-content">
                                            <FaPhone className="channel-icon" />
                                            <div className="channel-info">
                                                <p className="channel-name">{channel.name}</p>
                                                <p className="channel-detail">{channel.phoneNumber ? formatPhoneNumber(channel.phoneNumber) : channel.provider}</p>
                                            </div>
                                        </div>
                                        {channel.connected === true ? (
                                            <FaCircleCheck className="channel-status-icon status-verified" />
                                        ) : (
                                            <FaCircleExclamation className="channel-status-icon status-unverified" />
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="empty-state">
                                    <FaPhone className="empty-state-icon" />
                                    <p className="empty-state-text">No phone accounts configured</p>
                                </div>
                            )}
                        </div>
                    </div>
                    </>
                    )}
                </div>
            </div>

            {/* Right Detail Panel */}
            <div className="channels-detail">
                {formData ? (
                    <>
                        <div className="detail-toolbar">
                            <div className="detail-toolbar-left">
                                <h3 style={{ margin: 0, color: 'var(--primary-color)' }}>
                                    {selectedChannel ? (isEditing ? 'Edit Channel' : 'Channel Details') : 'New Channel'}
                                </h3>
                            </div>
                            <div className="detail-toolbar-right">
                                {isEditing ? (
                                    <>
                                        <button 
                                            className="toolbar-btn toolbar-btn-save"
                                            onClick={handleSave}
                                            disabled={isSaving || validationErrors.length > 0}
                                        >
                                            <FaFloppyDisk className="toolbar-btn-icon" />
                                            {isSaving ? 'Saving...' : 'Save'}
                                        </button>
                                        <button 
                                            className="toolbar-btn toolbar-btn-cancel"
                                            onClick={handleCancel}
                                            disabled={isSaving}
                                        >
                                            <FaXmark className="toolbar-btn-icon" />
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button 
                                            className={`toolbar-btn toolbar-btn-edit ${statusMessage?.text?.includes('Test failed: This communication channel is not configured properly') ? 'toolbar-btn-highlight-edit' : ''}`}
                                            onClick={handleEdit}
                                        >
                                            <FaPencil className="toolbar-btn-icon" />
                                            Edit
                                        </button>
                                        <button 
                                            className={`toolbar-btn toolbar-btn-test ${
                                                statusMessage?.text?.includes('Test failed: This communication channel is not configured properly') 
                                                ? '' 
                                                : (selectedChannel && selectedChannel.connected === false ? 'toolbar-btn-unverified' : '')
                                            }`}
                                            onClick={handleTest}
                                            disabled={isSaving}
                                        >
                                            <FaVialCircleCheck className="toolbar-btn-icon" />
                                            {isSaving ? 'Testing...' : 'Test'}
                                        </button>
                                        {selectedChannel && (
                                            <>
                                                <button 
                                                    className={`toolbar-btn toolbar-btn-close ${selectedChannel.connected === true ? 'toolbar-btn-close-validated' : ''}`}
                                                    onClick={() => {
                                                        setSelectedChannel(null);
                                                        setFormData(null);
                                                    }}
                                                >
                                                    <FaXmark className="toolbar-btn-icon" />
                                                    Close
                                                </button>
                                                <button 
                                                    className="toolbar-btn toolbar-btn-delete"
                                                    onClick={handleDelete}
                                                    disabled={isSaving}
                                                >
                                                    <FaTrash className="toolbar-btn-icon" />
                                                    Delete
                                                </button>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="detail-content">
                            {selectedChannel && selectedChannel.connected === true && (
                                <div className="verification-success">
                                    <span>Communication Channel Validated and Ready for Use</span>
                                </div>
                            )}
                            {selectedChannel && selectedChannel.connected !== true && !statusMessage?.text?.includes('Test failed: This communication channel is not configured properly') && (
                                <div className="verification-warning">
                                    <FaCircleExclamation className="warning-icon" />
                                    <span>This communication channel has not yet been verified, click on the <strong>test</strong> button to verify connection</span>
                                </div>
                            )}
                            {statusMessage && (
                                <div className={`status-message ${statusMessage.type}`}>
                                    {statusMessage.text}
                                </div>
                            )}
                            
                            {validationErrors.length > 0 && isEditing && (
                                <div className="validation-warning">
                                    <FaCircleExclamation style={{ fontSize: '1.3rem', color: '#ffc107', marginRight: '1rem', minWidth: '1.3rem' }} />
                                    <div>
                                        <strong>Required Fields Missing</strong>
                                        <p style={{ margin: '0.5rem 0 0 0' }}>
                                            Please fill in the following required fields: <strong>{validationErrors.join(', ')}</strong>
                                        </p>
                                    </div>
                                </div>
                            )}
                            
                            {selectedChannelType === 'email' ? (
                                <>
                                    <div className="detail-form detail-form-two-column">
                                        <div className="form-column">
                                            <div className="form-group">
                                                <label>Account Name</label>
                                                <input
                                                    type="text"
                                                    value={formData.name || ''}
                                                    onChange={(e) => handleFormChange('name', e.target.value)}
                                                    disabled={!isEditing}
                                                    placeholder="e.g., Support Email"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>SMTP Server</label>
                                                <input
                                                    type="text"
                                                    value={formData.smtpServer || ''}
                                                    onChange={(e) => handleFormChange('smtpServer', e.target.value)}
                                                    disabled={!isEditing}
                                                    placeholder="e.g., smtp.gmail.com"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Port</label>
                                                <input
                                                    type="number"
                                                    value={formData.port || ''}
                                                    onChange={(e) => handleFormChange('port', parseInt(e.target.value))}
                                                    disabled={!isEditing}
                                                    placeholder="587"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <div className="checkbox-group">
                                                    <input
                                                        type="checkbox"
                                                        id="useSSL"
                                                        checked={formData.useSSL || false}
                                                        onChange={(e) => handleFormChange('useSSL', e.target.checked)}
                                                        disabled={!isEditing}
                                                    />
                                                    <label htmlFor="useSSL">Use SSL/TLS</label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-column">
                                            <div className="form-group">
                                                <label>Username</label>
                                                <input
                                                    type="text"
                                                    value={formData.username || ''}
                                                    onChange={(e) => handleFormChange('username', e.target.value)}
                                                    disabled={!isEditing}
                                                    placeholder="email@example.com"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Password</label>
                                                <input
                                                    type="password"
                                                    value={formData.password || ''}
                                                    onChange={(e) => handleFormChange('password', e.target.value)}
                                                    disabled={!isEditing}
                                                    placeholder="Enter password"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {(!selectedChannel || statusMessage?.text?.includes('Test failed: This communication channel is not configured properly')) && (
                                        <div className="detail-empty-section" style={{ marginTop: '2rem' }}>
                                            <h4><FaEnvelope style={{ marginRight: '0.5rem' }} /> Setting Up Email Accounts</h4>
                                            <p>To connect an email account, you'll need:</p>
                                            <ul>
                                                <li>SMTP server address and port</li>
                                                <li>Email address (username)</li>
                                                <li>Application password or authentication token</li>
                                            </ul>
                                            <p><strong>Important:</strong> For security reasons, most email providers require application-specific passwords instead of your regular password.</p>
                                            <div className="detail-empty-links">
                                                <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer">
                                                    Get Gmail App Password
                                                </a>
                                                <a href="https://support.microsoft.com/en-us/account-billing/manage-app-passwords-for-two-step-verification-d6dc8c6d-4bf7-4851-ad95-6d07799387e9" target="_blank" rel="noopener noreferrer">
                                                    Get Outlook App Password
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="detail-form">
                                        <div className="form-group">
                                            <label>Account Name</label>
                                            <input
                                                type="text"
                                                value={formData.name || ''}
                                                onChange={(e) => handleFormChange('name', e.target.value)}
                                                disabled={!isEditing}
                                                placeholder="e.g., Main Phone Line"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Phone Number</label>
                                            <input
                                                type="tel"
                                                value={isEditing ? formatPhoneInput(formData.phoneNumber || '') : formatPhoneNumber(formData.phoneNumber || '')}
                                                onChange={(e) => {
                                                    const formatted = formatPhoneInput(e.target.value);
                                                    const parsed = parsePhoneNumber(formatted);
                                                    handleFormChange('phoneNumber', parsed);
                                                }}
                                                disabled={!isEditing}
                                                placeholder="(###) ###-####"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Provider</label>
                                            <input
                                                type="text"
                                                value={formData.provider || ''}
                                                onChange={(e) => handleFormChange('provider', e.target.value)}
                                                disabled={!isEditing}
                                                placeholder="Twilio"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Account SID</label>
                                            <input
                                                type="text"
                                                value={formData.accountSID || ''}
                                                onChange={(e) => handleFormChange('accountSID', e.target.value)}
                                                disabled={!isEditing}
                                                placeholder="Enter Twilio Account SID"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Auth Token</label>
                                            <input
                                                type="password"
                                                value={formData.authToken || ''}
                                                onChange={(e) => handleFormChange('authToken', e.target.value)}
                                                disabled={!isEditing}
                                                placeholder="Enter Auth Token"
                                            />
                                        </div>
                                    </div>
                                    {(!selectedChannel || statusMessage?.text?.includes('Test failed: This communication channel is not configured properly')) && (
                                        <div className="detail-empty-section" style={{ marginTop: '2rem' }}>
                                            <h4><FaPhone style={{ marginRight: '0.5rem' }} /> Setting Up Phone Accounts</h4>
                                            <p>To connect a phone number, you'll need a Twilio account with:</p>
                                            <ul>
                                                <li>Active phone number</li>
                                                <li>Account SID</li>
                                                <li>Auth Token</li>
                                            </ul>
                                            <div className="detail-empty-links">
                                                <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noopener noreferrer">
                                                    Create Twilio Account
                                                </a>
                                                <a href="https://www.twilio.com/docs/usage/tutorials/how-to-use-your-free-trial-account" target="_blank" rel="noopener noreferrer">
                                                    Twilio Setup Guide
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="detail-empty">
                        <FaCircleQuestion className="detail-empty-icon" />
                        <h3 className="detail-empty-title">Getting Started with Communication Channels</h3>
                        <p className="detail-empty-subtitle">Communication channels connect your agent to the outside world via email, call, or text.</p>
                        
                        <div className="detail-empty-instructions">
                            <div className="instruction-steps">
                                <div className="instruction-step">
                                    <div className="step-number">1</div>
                                    <div className="step-title">Create a New Channel</div>
                                    <div className="step-description">Click the + button</div>
                                </div>
                                <div className="instruction-step">
                                    <div className="step-number">2</div>
                                    <div className="step-title">Fill in Required Information</div>
                                    <div className="step-description">Enter account details</div>
                                </div>
                                <div className="instruction-step">
                                    <div className="step-number">3</div>
                                    <div className="step-title">Save Your Channel</div>
                                    <div className="step-description">Click the Save button</div>
                                </div>
                                <div className="instruction-step">
                                    <div className="step-number">4</div>
                                    <div className="step-title">Test the Connection</div>
                                    <div className="step-description">Verify it's working</div>
                                </div>
                            </div>
                        </div>

                        {(communicationLanes.emailAccounts?.some(ch => ch.connected === false) || 
                          communicationLanes.phoneAccounts?.some(ch => ch.connected === false)) && (
                            <div className="detail-empty-warning">
                                <FaCircleExclamation style={{ fontSize: '1.5rem', color: '#ffc107', marginRight: '1rem' }} />
                                <div>
                                    <strong>Action Required: Channel Verification Pending</strong>
                                    <p style={{ margin: '0.5rem 0 0 0' }}>
                                        You have communication channels that have not yet been verified for use. To verify, please select the channel from the sidebar with a <strong>yellow ! icon</strong> and click the <strong>Test</strong> button to confirm connectivity.
                                    </p>
                                </div>
                            </div>
                        )}
                        
                        <div className="detail-empty-sections-container">
                            <div className="detail-empty-section">
                                <h4><FaEnvelope style={{ marginRight: '0.5rem' }} /> Setting Up Email Accounts</h4>
                            <p>To connect an email account, you'll need:</p>
                            <ul>
                                <li>SMTP server address and port</li>
                                <li>Email address (username)</li>
                                <li>Application password or authentication token</li>
                            </ul>
                            <p><strong>Important:</strong> For security reasons, most email providers require application-specific passwords instead of your regular password.</p>
                            <div className="detail-empty-links">
                                <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer">
                                    Get Gmail App Password
                                </a>
                                <a href="https://support.microsoft.com/en-us/account-billing/manage-app-passwords-for-two-step-verification-d6dc8c6d-4bf7-4851-ad95-6d07799387e9" target="_blank" rel="noopener noreferrer">
                                    Get Outlook App Password
                                </a>
                            </div>
                        </div>

                        <div className="detail-empty-section">
                            <h4><FaPhone style={{ marginRight: '0.5rem' }} /> Setting Up Phone Accounts</h4>
                            <p>To connect a phone number, you'll need a Twilio account with:</p>
                            <ul>
                                <li>Active phone number</li>
                                <li>Account SID</li>
                                <li>Auth Token</li>
                            </ul>
                            <p><strong>Important:</strong> Currently Twilio is the only supported phone integration. For other providers, please contact support for assistance.</p>
                            <p><strong>SMS Compliance:</strong> Twilio A2P-10DLC compliance required to enable text message services.</p>
                            <div className="detail-empty-links">
                                <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noopener noreferrer">
                                    Create Twilio Account
                                </a>
                                <a href="https://www.twilio.com/docs/usage/tutorials/how-to-use-your-free-trial-account" target="_blank" rel="noopener noreferrer">
                                    Twilio Setup Guide
                                </a>
                            </div>
                        </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const QueueHome = ({ queueItems, filters, onFilterChange, onClearFilters }) => {
    // Calculate stats from queue items
    const totalItems = queueItems?.length || 0;
    const callItems = queueItems?.filter(item => item.type?.toLowerCase() === 'call')?.length || 0;
    const textItems = queueItems?.filter(item => item.type?.toLowerCase() === 'text')?.length || 0;
    const emailItems = queueItems?.filter(item => item.type?.toLowerCase() === 'email')?.length || 0;
    
    // Count executed vs queued
    const executedItems = queueItems?.filter(item => item.executed)?.length || 0;
    const queuedItems = totalItems - executedItems;
    
    // Get today's date range
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayItems = queueItems?.filter(item => {
        const itemDate = new Date(item.createdTime);
        return itemDate >= today;
    })?.length || 0;

    return (
        <div className="context-home-container">
            {/* Filter Section */}
            <div className="console-form-section-large">
                <div className="filter-header">
                    <label>Filter Queue Items</label>
                    <button 
                        className="btn-clear-filters"
                        onClick={onClearFilters}
                    >
                        Clear All Filters
                    </button>
                </div>
                <div className="filter-grid-7col">
                    <div className="form-group">
                        <label>To</label>
                        <input
                            type="text"
                            placeholder="Search email/phone/name..."
                            value={filters.toContact}
                            onChange={(e) => onFilterChange('toContact', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Executed From</label>
                        <input
                            type="date"
                            value={filters.executedFrom}
                            onChange={(e) => onFilterChange('executedFrom', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Executed To</label>
                        <input
                            type="date"
                            value={filters.executedTo}
                            onChange={(e) => onFilterChange('executedTo', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Account Type</label>
                        <select
                            value={filters.accountType}
                            onChange={(e) => onFilterChange('accountType', e.target.value)}
                        >
                            <option value="">All Types</option>
                            <option value="call">Call</option>
                            <option value="text">Text</option>
                            <option value="email">Email</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Status</label>
                        <select
                            value={filters.status}
                            onChange={(e) => onFilterChange('status', e.target.value)}
                        >
                            <option value="">All Status</option>
                            <option value="queued">Queued</option>
                            <option value="executed">Executed</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Account</label>
                        <select
                            value={filters.account}
                            onChange={(e) => onFilterChange('account', e.target.value)}
                        >
                            <option value="">All Accounts</option>
                            {queueItems && [...new Set(queueItems.map(item => item.account?.name).filter(Boolean))].sort().map(accountName => (
                                <option key={accountName} value={accountName}>
                                    {accountName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Context</label>
                        <select
                            value={filters.context}
                            onChange={(e) => onFilterChange('context', e.target.value)}
                        >
                            <option value="">All Contexts</option>
                            {queueItems && [...new Set(queueItems.map(item => item.context?.name).filter(Boolean))].sort().map(contextName => (
                                <option key={contextName} value={contextName}>
                                    {contextName}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Overview Section */}
            <div className="overview-welcome">
                <MdQueue className="welcome-icon" />
                <h3>Welcome to Communication Queue</h3>
                <p>
                    Manage your outbound communications across all channels. 
                    Create new queued items or import in bulk to streamline your workflow.
                </p>
                <p>
                    Use the filters above to narrow down your search by date range, account type, or status.
                </p>
                <div className="overview-stats">
                    <div className="stat-card">
                        <MdQueue className="stat-icon" />
                        <h4>Total Items</h4>
                        <p className="stat-number">
                            {totalItems}
                        </p>
                        <p className="stat-label">All queue items</p>
                    </div>
                    <div className="stat-card">
                        <FaCalendarDay className="stat-icon" />
                        <h4>Today's Items</h4>
                        <p className="stat-number">
                            {todayItems}
                        </p>
                        <p className="stat-label">Created today</p>
                    </div>
                    <div className="stat-card">
                        <MdQueue className="stat-icon" />
                        <h4>Queued</h4>
                        <p className="stat-number">
                            {queuedItems}
                        </p>
                        <p className="stat-label">Pending execution</p>
                    </div>
                    <div className="stat-card">
                        <FaCircleCheck className="stat-icon" />
                        <h4>Executed</h4>
                        <p className="stat-number">
                            {executedItems}
                        </p>
                        <p className="stat-label">Completed items</p>
                    </div>
                    <div className="stat-card">
                        <FaPhone className="stat-icon" />
                        <h4>Calls</h4>
                        <p className="stat-number">
                            {callItems}
                        </p>
                        <p className="stat-label">Total call items</p>
                    </div>
                    <div className="stat-card">
                        <FaComment className="stat-icon" />
                        <h4>Messages</h4>
                        <p className="stat-number">
                            {textItems}
                        </p>
                        <p className="stat-label">Total text items</p>
                    </div>
                    <div className="stat-card">
                        <FaEnvelope className="stat-icon" />
                        <h4>Emails</h4>
                        <p className="stat-number">
                            {emailItems}
                        </p>
                        <p className="stat-label">Total email items</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ClientCommunicationQueue = ({selectedTenant, userData, accessToken}) => {
    const [queueData, setQueueData] = useState({ phone: [], email: [] });
    const [contexts, setContexts] = useState([]);
    const [selectedQueueItem, setSelectedQueueItem] = useState(null);
    const [queueHistory, setQueueHistory] = useState(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [showNewQueueItem, setShowNewQueueItem] = useState(false);
    const [showBulkImport, setShowBulkImport] = useState(false);
    const [isLoading, setIsLoading] = useState(true); 
    const [pendingItems, setPendingItems] = useState([]);
    const [editingPendingId, setEditingPendingId] = useState(null);
    const [filters, setFilters] = useState({
        toContact: '',
        executedFrom: '',
        executedTo: '',
        accountType: '',
        status: '',
        account: '',
        context: ''
    });
    const [newItemFormData, setNewItemFormData] = useState({
        communicationType: '',
        account: '',
        context: '',
        toNumber: '',
        toEmail: '',
        callDetails: '',
        personContacted: ''
    });

    useEffect(() => {
        if (selectedTenant && accessToken) {
            loadQueueData();
            loadContexts();
        }
    }, [selectedTenant, accessToken]);

    const loadQueueData = async () => {
        setIsLoading(true);
        await getCommunicationQueue(
            selectedTenant.id,
            accessToken,
            (data) => {
                console.log('Communication queue fetched:', data);
                setQueueData(data);
                setIsLoading(false);
            },
            (err) => {
                console.error('Error loading queue:', err);
                setIsLoading(false);
            }
        );
    };

    const loadContexts = async () => {
        await getAllContext(
            selectedTenant.id,
            accessToken,
            (data) => {
                setContexts(data.context || []);
            },
            (err) => {
                console.error('Error loading contexts:', err);
            }
        );
    };

    const handleSelectQueueItem = async (item) => {
        if (item.isPending) {
            // Edit pending item
            handleEditPendingItem(item);
        } else {
            // View existing queue item
            setSelectedQueueItem(item);
            setShowNewQueueItem(false);
            setShowBulkImport(false);
            setEditingPendingId(null);
            
            // Load history for this queue item
            if (item.id) {
                setIsLoadingHistory(true);
                await getCommunicationQueueHistory(
                    selectedTenant.id,
                    item.id,
                    accessToken,
                    (data) => {
                        console.log('Queue item history fetched:', data);
                        setQueueHistory(data);
                        setIsLoadingHistory(false);
                    },
                    (err) => {
                        console.error('Error loading queue history:', err);
                        setQueueHistory(null);
                        setIsLoadingHistory(false);
                    }
                );
            }
        }
    };

    const handleEditPendingItem = (item) => {
        setEditingPendingId(item.id);
        setShowNewQueueItem(true);
        setShowBulkImport(false);
        setSelectedQueueItem(null);
        setNewItemFormData({
            communicationType: item.type,
            account: item.account,
            context: item.context,
            toNumber: item.toNumber || '',
            toEmail: item.toEmail || '',
            callDetails: item.callDetails || '',
            personContacted: item.personContacted || ''
        });
    };

    const handleNewQueueItem = () => {
        setShowNewQueueItem(true);
        setShowBulkImport(false);
        setSelectedQueueItem(null);
        setEditingPendingId(null);
        setNewItemFormData({
            communicationType: '',
            account: '',
            context: '',
            toNumber: '',
            toEmail: '',
            callDetails: '',
            personContacted: ''
        });
    };

    const handleBulkImport = () => {
        setShowBulkImport(true);
        setShowNewQueueItem(false);
        setSelectedQueueItem(null);
    };

    const handleCloseQueueItem = () => {
        setSelectedQueueItem(null);
        setQueueHistory(null);
        setIsLoadingHistory(false);
    };

    const handleAddPendingItem = () => {
        if (!isNewItemFormValid()) return;

        if (editingPendingId) {
            // Update existing pending item
            setPendingItems(pendingItems.map(item => 
                item.id === editingPendingId
                    ? {
                        ...item,
                        type: newItemFormData.communicationType,
                        account: newItemFormData.account,
                        context: newItemFormData.context,
                        toNumber: newItemFormData.toNumber,
                        toEmail: newItemFormData.toEmail,
                        callDetails: newItemFormData.callDetails,
                        personContacted: newItemFormData.personContacted
                    }
                    : item
            ));
            setEditingPendingId(null);
        } else {
            // Add new pending item
            const pendingItem = {
                id: `pending-${Date.now()}`,
                type: newItemFormData.communicationType,
                account: newItemFormData.account,
                context: newItemFormData.context,
                toNumber: newItemFormData.toNumber,
                toEmail: newItemFormData.toEmail,
                callDetails: newItemFormData.callDetails,
                personContacted: newItemFormData.personContacted,
                isPending: true
            };

            setPendingItems([...pendingItems, pendingItem]);
            
            // Close the new queue item window and show queue home
            setShowNewQueueItem(false);
            setSelectedQueueItem(null);
            setEditingPendingId(null);
        }
        
        // Reset form but keep communication type and account selected
        setNewItemFormData({
            ...newItemFormData,
            toNumber: '',
            toEmail: '',
            callDetails: '',
            personContacted: '',
            context: ''
        });
    };

    const handleSubmitQueue = async () => {
        if (pendingItems.length === 0) return;

        const queueArray = pendingItems.map(item => ({
            type: item.type,
            account: item.account,
            context: item.context,
            toNumber: item.toNumber,
            toEmail: item.toEmail,
            callDetails: item.callDetails,
            personContacted: item.personContacted
        }));

        await addToCommunicationQueue(
            selectedTenant.id,
            userData.user.id,
            queueArray,
            accessToken,
            (data) => {
                console.log('Queue items added successfully');
                setPendingItems([]);
                loadQueueData();
                setShowNewQueueItem(false);
            },
            (err) => {
                console.error('Error adding to queue:', err);
            }
        );
    };

    const handleRemovePendingItem = (itemId) => {
        setPendingItems(pendingItems.filter(item => item.id !== itemId));
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        await formatBulkQueueData(
            selectedTenant.id,
            accessToken,
            file,
            (data) => {
                console.log('Bulk data formatted:', data);
                // We'll handle this next
            },
            (err) => {
                console.error('Error formatting bulk data:', err);
            }
        );
    };

    const isNewItemFormValid = () => {
        if (!newItemFormData.communicationType || !newItemFormData.account || !newItemFormData.context) {
            return false;
        }
        
        if (newItemFormData.communicationType === 'Email') {
            return newItemFormData.toEmail.trim() !== '';
        } else {
            return newItemFormData.toNumber.trim() !== '';
        }
    };

    // Get accounts based on selected communication type
    const getAvailableAccounts = () => {
        if (!newItemFormData.communicationType) return [];
        
        return contexts
            .filter(ctx => ctx.accountType === newItemFormData.communicationType)
            .map(ctx => ctx.account)
            .filter((account, index, self) => 
                index === self.findIndex(a => a.id === account.id)
            );
    };

    // Get outbound contexts for selected account
    const getOutboundContexts = () => {
        if (!newItemFormData.account) return [];
        
        return contexts.filter(ctx => 
            ctx.direction === 'Outbound' &&
            ctx.accountType === newItemFormData.communicationType &&
            String(ctx.account?.id) === String(newItemFormData.account)
        );
    };

    // Filter handling functions
    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };

    const handleClearFilters = () => {
        setFilters({
            toContact: '',
            executedFrom: '',
            executedTo: '',
            accountType: '',
            status: '',
            account: '',
            context: ''
        });
    };

    // Get all items combined
    const getAllQueueItems = () => {
        const phoneItems = queueData.phone || [];
        const emailItems = queueData.email || [];
        return [...phoneItems, ...emailItems];
    };

    // Apply filters to queue items
    const getFilteredQueueItems = () => {
        const allItems = getAllQueueItems();
        
        return allItems.filter(item => {
            // To Contact filter (searches toNumber, toEmail, and personContacted)
            if (filters.toContact) {
                const stripFormatting = (str) => (str || '').replace(/[\s\-()]/g, '').toLowerCase();
                const toNumber = stripFormatting(item.toNumber);
                const toEmail = (item.toEmail || '').toLowerCase();
                const personContacted = (item.personContacted || '').toLowerCase();
                const searchValue = stripFormatting(filters.toContact);
                if (!toNumber.includes(searchValue) && !toEmail.includes(searchValue) && !personContacted.includes(searchValue)) {
                    return false;
                }
            }
            
            // Executed From filter
            if (filters.executedFrom && item.executed) {
                const itemDate = new Date(item.executedTime);
                const filterDate = new Date(filters.executedFrom + 'T00:00:00');
                if (itemDate < filterDate) return false;
            }
            
            // Executed To filter
            if (filters.executedTo && item.executed) {
                const itemDate = new Date(item.executedTime);
                const filterDate = new Date(filters.executedTo + 'T23:59:59.999');
                if (itemDate > filterDate) return false;
            }
            
            // Account Type filter
            if (filters.accountType) {
                const itemType = (item.type || '').toLowerCase();
                if (itemType !== filters.accountType.toLowerCase()) {
                    return false;
                }
            }
            
            // Status filter
            if (filters.status) {
                if (filters.status === 'queued' && item.executed) return false;
                if (filters.status === 'executed' && !item.executed) return false;
            }
            
            // Account filter
            if (filters.account) {
                const itemAccountName = item.account?.name || '';
                if (itemAccountName !== filters.account) {
                    return false;
                }
            }
            
            // Context filter
            if (filters.context) {
                const itemContextName = item.context?.name || '';
                if (itemContextName !== filters.context) {
                    return false;
                }
            }
            
            return true;
        });
    };

    // Separate queued and executed items (with filters applied)
    const getQueuedItems = () => {
        const filteredItems = getFilteredQueueItems();
        const queued = filteredItems.filter(item => !item.executed);
        return [...queued, ...pendingItems];
    };

    const getExecutedItems = () => {
        const filteredItems = getFilteredQueueItems();
        return filteredItems.filter(item => item.executed);
    };

    return (
        <div className="communication-channels">
            {/* Left Sidebar */}
            <div className="channels-sidebar">
                <div className="sidebar-header">
                    <h3>Outbound Queue</h3>
                    <button 
                        className="add-channel-btn"
                        onClick={loadQueueData}
                        title="Refresh Queue"
                        style={{ marginLeft: 'auto' }}
                    >
                        <FaRotate />
                    </button>
                </div>
                <div className="sidebar-content">
                    {isLoading ? (
                        <div className="loading-container">
                            <p>Loading communication queue...</p>
                        </div>
                    ) : (
                        <>
                    {/* Queued Communications */}
                    <div className="channel-section">
                        <div className="justified-section-header">
                            <h4>
                                <MdQueue className="section-header-icon" />
                                Queued
                            </h4>
                        </div>
                        <div className="channel-list">
                            {getQueuedItems().length === 0 ? (
                                <div className="empty-state-small">
                                    No items in queue
                                </div>
                            ) : (
                                getQueuedItems().map((item) => (
                                    <div
                                        key={item.id}
                                        className={`channel-item ${selectedQueueItem?.id === item.id || (item.isPending && editingPendingId === item.id) ? 'selected' : ''} ${item.isPending ? 'warning-item' : ''}`}
                                        onClick={() => handleSelectQueueItem(item)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {item.context?.name && (
                                            <div className="channel-context-name">
                                                {item.context.name}
                                            </div>
                                        )}
                                        <div className="channel-item-content">
                                            {item.type === 'Call' || item.type === 'call' ? (
                                                <FaPhone className="channel-icon" />
                                            ) : item.type === 'Text' || item.type === 'text' ? (
                                                <FaComment className="channel-icon" />
                                            ) : (
                                                <FaEnvelope className="channel-icon" />
                                            )}
                                            <div className="channel-info">
                                                {item.personContacted && (
                                                    <p className="channel-person">
                                                        {item.personContacted}
                                                    </p>
                                                )}
                                                <p className="channel-name">
                                                    {item.toNumber ? formatPhoneNumber(item.toNumber) : item.toEmail}
                                                </p>
                                                <p className="channel-detail">
                                                    {item.isPending ? 'Pending Submit' : `Created: ${new Date(item.createdTime).toLocaleString()}`}
                                                </p>
                                            </div>
                                            {item.isPending && (
                                                <FaCircleExclamation className="channel-status-icon warning-icon" />
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Executed Communications */}
                    <div className="channel-section">
                        <div className="justified-section-header">
                            <h4>
                                <FaCircleCheck className="section-header-icon" />
                                Executed
                            </h4>
                        </div>
                        <div className="channel-list">
                            {getExecutedItems().length === 0 ? (
                                <div className="empty-state-small">
                                    No executed items
                                </div>
                            ) : (
                                getExecutedItems().map((item) => (
                                    <div
                                        key={item.id}
                                        className={`channel-item ${selectedQueueItem?.id === item.id ? 'selected' : ''}`}
                                        onClick={() => handleSelectQueueItem(item)}
                                    >
                                        {item.context?.name && (
                                            <div className="channel-context-name">
                                                {item.context.name}
                                            </div>
                                        )}
                                        <div className="channel-item-content">
                                            {item.type === 'Call' || item.type === 'call' ? (
                                                <FaPhone className="channel-icon" />
                                            ) : item.type === 'Text' || item.type === 'text' ? (
                                                <FaComment className="channel-icon" />
                                            ) : (
                                                <FaEnvelope className="channel-icon" />
                                            )}
                                            <div className="channel-info">
                                                {item.personContacted && (
                                                    <p className="channel-person">
                                                        {item.personContacted}
                                                    </p>
                                                )}
                                                <p className="channel-name">
                                                    {item.toNumber ? formatPhoneNumber(item.toNumber) : item.toEmail}
                                                </p>
                                                <p className="channel-detail">
                                                    Executed: {new Date(item.executedTime).toLocaleString()}
                                                </p>
                                            </div>
                                            <FaCircleCheck className="channel-status-icon status-verified" />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    </>
                    )}
                </div>
            </div>

            {/* Right Detail Panel */}
            <div className="channels-detail">
                <div className="detail-toolbar">
                    <div className="detail-toolbar-left">
                        <h3 className="detail-toolbar-title">
                            {showNewQueueItem ? 'New Queued Item' : showBulkImport ? 'Bulk Import' : selectedQueueItem ? 'Queue Item Details' : 'Queue Management'}
                        </h3>
                    </div>
                    <div className="detail-toolbar-right">
                        {showNewQueueItem ? (
                            <>
                                <button 
                                    className="toolbar-btn"
                                    onClick={() => {
                                        setShowNewQueueItem(false);
                                        setEditingPendingId(null);
                                        setNewItemFormData({
                                            communicationType: '',
                                            account: '',
                                            context: '',
                                            toNumber: '',
                                            toEmail: '',
                                            callDetails: ''
                                        });
                                    }}
                                >
                                    <FaXmark className="toolbar-btn-icon" />
                                    Cancel
                                </button>
                                {editingPendingId && (
                                    <button 
                                        className="toolbar-btn toolbar-btn-remove"
                                        onClick={() => {
                                            setPendingItems(pendingItems.filter(item => item.id !== editingPendingId));
                                            setShowNewQueueItem(false);
                                            setEditingPendingId(null);
                                            setNewItemFormData({
                                                communicationType: '',
                                                account: '',
                                                context: '',
                                                toNumber: '',
                                                toEmail: '',
                                                callDetails: ''
                                            });
                                        }}
                                    >
                                        <FaTrash className="toolbar-btn-icon" />
                                        Delete Pending Item
                                    </button>
                                )}
                                <button 
                                    className="toolbar-btn toolbar-btn-save"
                                    onClick={handleAddPendingItem}
                                    disabled={!isNewItemFormValid()}
                                >
                                    {editingPendingId ? (
                                        <>
                                            <FaFloppyDisk className="toolbar-btn-icon" />
                                            Update Pending
                                        </>
                                    ) : (
                                        <>
                                            <FaPlus className="toolbar-btn-icon" />
                                            Add to Pending
                                        </>
                                    )}
                                </button>
                            </>
                        ) : showBulkImport ? (
                            <button 
                                className="toolbar-btn"
                                onClick={() => setShowBulkImport(false)}
                            >
                                <FaXmark className="toolbar-btn-icon" />
                                Cancel
                            </button>
                        ) : selectedQueueItem ? (
                            <button 
                                className="toolbar-btn toolbar-btn-close"
                                onClick={handleCloseQueueItem}
                            >
                                <FaXmark className="toolbar-btn-icon" />
                                Close
                            </button>
                        ) : (
                            <>
                                <button 
                                    className="toolbar-btn"
                                    onClick={handleNewQueueItem}
                                >
                                    <FaPlus className="toolbar-btn-icon" />
                                    New Queued Item
                                </button>
                                <button 
                                    className="toolbar-btn"
                                    onClick={handleBulkImport}
                                >
                                    <FaFileLines className="toolbar-btn-icon" />
                                    Bulk Import Queued Items
                                </button>
                                {pendingItems.length > 0 && (
                                    <button 
                                        className="toolbar-btn toolbar-btn-save"
                                        onClick={handleSubmitQueue}
                                    >
                                        <FaFloppyDisk className="toolbar-btn-icon" />
                                        Submit Queue ({pendingItems.length})
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="detail-content">
                    {showNewQueueItem && (
                        <div>
                            {pendingItems.length > 0 && !editingPendingId && (
                                <div className="verification-warning" style={{ marginBottom: '1.5rem' }}>
                                    <FaCircleExclamation className="warning-icon" />
                                    <span>You have {pendingItems.length} pending item(s). Close this record and click "Submit Queue" to execute them.</span>
                                </div>
                            )}
                            {editingPendingId && (
                                <div className="verification-warning" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, rgba(32, 126, 137, 0.1) 0%, rgba(128, 0, 128, 0.1) 100%)', borderLeft: '4px solid var(--primary-color)' }}>
                                    <FaPencil className="warning-icon" style={{ color: 'var(--primary-color)' }} />
                                    <span>Editing pending item. Click "Update Pending" to save changes or "Delete Pending Item" to remove.</span>
                                </div>
                            )}
                            
                            <form className="detail-form detail-form-full">
                                <div className="console-form-section">
                                    <h4 className="console-form-section-title">Communication Details</h4>
                                    
                                    {newItemFormData.account && getOutboundContexts().length === 0 && (
                                        <div className="verification-warning" style={{ marginBottom: '1rem' }}>
                                            <FaCircleExclamation className="warning-icon" />
                                            <span>No outbound contexts found for this account. Please create an outbound context before adding items to the queue.</span>
                                        </div>
                                    )}

                                    <div className="form-grid-2col">
                                        <div className="form-group">
                                            <label>Communication Type *</label>
                                            <select
                                                value={newItemFormData.communicationType}
                                                onChange={(e) => {
                                                    setNewItemFormData({
                                                        communicationType: e.target.value,
                                                        account: '',
                                                        context: '',
                                                        toNumber: '',
                                                        toEmail: '',
                                                        callDetails: ''
                                                    });
                                                }}
                                            >
                                                <option value="">Select type</option>
                                                <option value="Call">Call</option>
                                                <option value="Text">Text</option>
                                                <option value="Email">Email</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Account *</label>
                                            <select
                                                value={newItemFormData.account}
                                                onChange={(e) => {
                                                    setNewItemFormData({
                                                        ...newItemFormData,
                                                        account: e.target.value,
                                                        context: ''
                                                    });
                                                }}
                                                disabled={!newItemFormData.communicationType}
                                            >
                                                <option value="">
                                                    {!newItemFormData.communicationType 
                                                        ? 'Select type first'
                                                        : getAvailableAccounts().length === 0
                                                        ? 'No accounts available'
                                                        : 'Select account'}
                                                </option>
                                                {getAvailableAccounts().map((account) => (
                                                    <option key={account.id} value={account.id}>
                                                        {account.name || account.number || account.username}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Context *</label>
                                            <select
                                                value={newItemFormData.context}
                                                onChange={(e) => {
                                                    setNewItemFormData({
                                                        ...newItemFormData,
                                                        context: e.target.value
                                                    });
                                                }}
                                                disabled={!newItemFormData.account}
                                            >
                                                <option value="">
                                                    {!newItemFormData.account
                                                        ? 'Select account first'
                                                        : getOutboundContexts().length === 0
                                                        ? 'No outbound contexts'
                                                        : 'Select context'}
                                                </option>
                                                {getOutboundContexts().map((context) => (
                                                    <option key={context.id} value={context.id}>
                                                        {context.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {newItemFormData.communicationType && (newItemFormData.communicationType === 'Call' || newItemFormData.communicationType === 'Text') && (
                                            <div className="form-group">
                                                <label>To Number *</label>
                                                <input
                                                    type="tel"
                                                    value={formatPhoneInput(newItemFormData.toNumber)}
                                                    onChange={(e) => {
                                                        const formatted = formatPhoneInput(e.target.value);
                                                        const parsed = parsePhoneNumber(formatted);
                                                        setNewItemFormData({...newItemFormData, toNumber: parsed});
                                                    }}
                                                    placeholder="(###) ###-####"
                                                />
                                            </div>
                                        )}
                                        {newItemFormData.communicationType === 'Email' && (
                                            <div className="form-group">
                                                <label>To Email *</label>
                                                <input
                                                    type="email"
                                                    value={newItemFormData.toEmail}
                                                    onChange={(e) => setNewItemFormData({...newItemFormData, toEmail: e.target.value})}
                                                    placeholder="Email address"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {newItemFormData.communicationType && (newItemFormData.communicationType === 'Call' || newItemFormData.communicationType === 'Text') && (
                                    <div className="console-form-section">
                                        <h4 className="console-form-section-title">Call Details</h4>
                                        <div className="form-group">
                                            <label>Person Contacted (Optional)</label>
                                            <input
                                                type="text"
                                                value={newItemFormData.personContacted}
                                                onChange={(e) => setNewItemFormData({...newItemFormData, personContacted: e.target.value})}
                                                placeholder="Name of person being contacted (e.g., John Smith)"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Additional Information</label>
                                            <textarea
                                                value={newItemFormData.callDetails}
                                                onChange={(e) => setNewItemFormData({...newItemFormData, callDetails: e.target.value})}
                                                placeholder="Enter any additional details or context for this communication...This can include the name of the person being contacted or any other relevant information that can help provide context to the recipient. This is added to the information already provided in the selected context."
                                                style={{ minHeight: '400px', width: '100%' }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </form>
                        </div>
                    )}

                    {showBulkImport && (
                        <div>
                            <div className="console-form-section">
                                <h4 className="console-form-section-title">Select File for Bulk Import</h4>
                                <div className="form-group">
                                    <input
                                        type="file"
                                        onChange={handleFileSelect}
                                        accept=".csv,.xlsx,.xls"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedQueueItem && !showNewQueueItem && !showBulkImport && (
                        <div className="detail-form detail-form-full">
                            {/* Queue Status Overview */}
                            <div className="console-form-section">
                                <h4 className="console-form-section-title">Queue Status</h4>
                                <div className="form-grid-2col">
                                    <div className="form-group">
                                        <label>Communication Type</label>
                                        <input type="text" value={selectedQueueItem.type} disabled />
                                    </div>
                                    <div className="form-group">
                                        <label>Status</label>
                                        <input 
                                            type="text" 
                                            value={selectedQueueItem.executed ? 'Executed' : 'Queued'} 
                                            disabled 
                                            style={{ 
                                                fontWeight: 'bold',
                                                color: selectedQueueItem.executed ? 'var(--success-color)' : 'var(--warning-color)'
                                            }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Created Time</label>
                                        <input type="text" value={new Date(selectedQueueItem.createdTime).toLocaleString()} disabled />
                                    </div>
                                    {selectedQueueItem.executed && (
                                        <>
                                            <div className="form-group">
                                                <label>Executed Time</label>
                                                <input type="text" value={new Date(selectedQueueItem.executedTime).toLocaleString()} disabled />
                                            </div>
                                            {selectedQueueItem.resolution && (
                                                <>
                                                    <div className="form-group">
                                                        <label>Resolution</label>
                                                        <input 
                                                            type="text" 
                                                            value={selectedQueueItem.resolution} 
                                                            disabled 
                                                            style={{ 
                                                                fontWeight: 'bold',
                                                                color: selectedQueueItem.resolution === 'success' ? 'var(--success-color)' : 'var(--error-color)'
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Premium Request</label>
                                                        <input type="text" value={selectedQueueItem.premiumRequest ? 'Yes' : 'No'} disabled />
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Contact Details */}
                            <div className="console-form-section">
                                <h4 className="console-form-section-title">Contact Details</h4>
                                <div className="form-grid-2col">
                                    {selectedQueueItem.personContacted && (
                                        <div className="form-group">
                                            <label>Person Contacted</label>
                                            <input type="text" value={selectedQueueItem.personContacted} disabled />
                                        </div>
                                    )}
                                    <div className="form-group">
                                        <label>{selectedQueueItem.type === 'Email' ? 'Email Address' : 'Phone Number'}</label>
                                        <input 
                                            type="text" 
                                            value={
                                                selectedQueueItem.toNumber 
                                                    ? formatPhoneNumber(selectedQueueItem.toNumber) 
                                                    : selectedQueueItem.toEmail
                                            } 
                                            disabled 
                                        />
                                    </div>
                                    {selectedQueueItem.account && (
                                        <>
                                            <div className="form-group">
                                                <label>Account Used</label>
                                                <input type="text" value={selectedQueueItem.account.name} disabled />
                                            </div>
                                            <div className="form-group">
                                                <label>Account Number</label>
                                                <input 
                                                    type="text" 
                                                    value={selectedQueueItem.account.number ? formatPhoneNumber(selectedQueueItem.account.number) : selectedQueueItem.account.username || 'N/A'} 
                                                    disabled 
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Context Information */}
                            {selectedQueueItem.context && (
                                <div className="console-form-section">
                                    <h4 className="console-form-section-title">Context Information</h4>
                                    <div className="form-grid-2col">
                                        <div className="form-group">
                                            <label>Context Name</label>
                                            <input type="text" value={selectedQueueItem.context.name} disabled />
                                        </div>
                                        <div className="form-group">
                                            <label>Direction</label>
                                            <input type="text" value={selectedQueueItem.context.direction} disabled />
                                        </div>
                                        {selectedQueueItem.context.clientInformation && (
                                            <>
                                                <div className="form-group">
                                                    <label>Client</label>
                                                    <input type="text" value={selectedQueueItem.context.clientInformation.name} disabled />
                                                </div>
                                                <div className="form-group">
                                                    <label>Client Email</label>
                                                    <input type="text" value={selectedQueueItem.context.clientInformation.email} disabled />
                                                </div>
                                            </>
                                        )}
                                        {selectedQueueItem.context.locationInformation && (
                                            <>
                                                <div className="form-group">
                                                    <label>Location</label>
                                                    <input type="text" value={selectedQueueItem.context.locationInformation.name} disabled />
                                                </div>
                                                <div className="form-group">
                                                    <label>Location Address</label>
                                                    <input 
                                                        type="text" 
                                                        value={`${selectedQueueItem.context.locationInformation.city}, ${selectedQueueItem.context.locationInformation.state}`} 
                                                        disabled 
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    {selectedQueueItem.context.purpose && (
                                        <div className="form-group">
                                            <label>Purpose</label>
                                            <textarea value={selectedQueueItem.context.purpose} disabled rows="2" className="textarea-resizable" />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Communication Details */}
                            {selectedQueueItem.callDetails && (
                                <div className="console-form-section">
                                    <h4 className="console-form-section-title">Communication Details</h4>
                                    <div className="form-group">
                                        <label>Additional Information</label>
                                        <textarea value={selectedQueueItem.callDetails} disabled rows="6" className="textarea-resizable" />
                                    </div>
                                </div>
                            )}

                            {/* History Section */}
                            {selectedQueueItem.executed && (
                                <div className="console-form-section">
                                    <h4 className="console-form-section-title">Execution History</h4>
                                    {isLoadingHistory ? (
                                        <div className="loading-container" style={{ padding: '2rem', textAlign: 'center' }}>
                                            <p>Loading history...</p>
                                        </div>
                                    ) : queueHistory ? (
                                        <div className="history-detail-layout">
                                            {/* Information Pane */}
                                            <div className="history-info-pane">
                                                {/* Communication Details */}
                                                <div className="detail-section">
                                                    <h4 className="console-form-section-title">Communication Details</h4>
                                                    <div className="detail-form">
                                                        <div className="form-group">
                                                            <label>From</label>
                                                            <input
                                                                type="text"
                                                                value={queueHistory.from ? formatPhoneNumber(queueHistory.from) : 'N/A'}
                                                                disabled
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>To</label>
                                                            <input
                                                                type="text"
                                                                value={queueHistory.to ? formatPhoneNumber(queueHistory.to) : 'N/A'}
                                                                disabled
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Start Time</label>
                                                            <input
                                                                type="text"
                                                                value={queueHistory.start ? new Date(queueHistory.start).toLocaleString() : 'N/A'}
                                                                disabled
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>End Time</label>
                                                            <input
                                                                type="text"
                                                                value={queueHistory.end ? new Date(queueHistory.end).toLocaleString() : 'N/A'}
                                                                disabled
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Duration</label>
                                                            <input
                                                                type="text"
                                                                value={queueHistory.minutes ? `${queueHistory.minutes} minute(s)` : 'N/A'}
                                                                disabled
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Method</label>
                                                            <input
                                                                type="text"
                                                                value={queueHistory.method || 'N/A'}
                                                                disabled
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Integrations and Actions */}
                                                {(queueHistory.integrations || queueHistory.actions) && (
                                                    <>
                                                        {queueHistory.integrations && (
                                                            <div className="detail-section">
                                                                <h4 className="console-form-section-title">Integrations Used</h4>
                                                                <div className="detail-form">
                                                                    <div className="form-group">
                                                                        <textarea
                                                                            value={queueHistory.integrations}
                                                                            disabled
                                                                            rows={3}
                                                                            className="textarea-resizable"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {queueHistory.actions && (
                                                            <div className="detail-section">
                                                                <h4 className="console-form-section-title">Actions Taken</h4>
                                                                <div className="detail-form">
                                                                    <div className="form-group">
                                                                        <textarea
                                                                            value={queueHistory.actions}
                                                                            disabled
                                                                            rows={4}
                                                                            className="textarea-resizable"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>

                                            {/* Transcript Pane */}
                                            {queueHistory.transcript && (
                                                <div className="history-transcript-pane">
                                                    <div className="detail-section">
                                                        <h4 className="console-form-section-title">Transcript</h4>
                                                        <div className="transcript-container">
                                                            <pre className="transcript-text">
                                                                {queueHistory.transcript.transcript || queueHistory.transcript}
                                                            </pre>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div style={{ padding: '1rem', color: '#999', fontStyle: 'italic' }}>
                                            No history available for this queue item.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {!showNewQueueItem && !showBulkImport && !selectedQueueItem && (
                        <QueueHome 
                            queueItems={getAllQueueItems()}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onClearFilters={handleClearFilters}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const ClientIntegrations = ({ selectedTenant, accessToken }) => {
    const [allIntegrations, setAllIntegrations] = useState([]);
    const [configuredIntegrations, setConfiguredIntegrations] = useState([]);
    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [selectedConfiguredIntegration, setSelectedConfiguredIntegration] = useState(null);
    const [selectedSetupIndex, setSelectedSetupIndex] = useState(0);
    const [setupFormData, setSetupFormData] = useState({});
    const [setupOptions, setSetupOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isValidatingEntry, setIsValidatingEntry] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [setupValidationStatus, setSetupValidationStatus] = useState({
        type: 'idle',
        message: ''
    });
    const [validatedSetupObject, setValidatedSetupObject] = useState(null);
    const [enabledActionState, setEnabledActionState] = useState({});
    const [enabledActionOptions, setEnabledActionOptions] = useState({});

    const normalizeRequiredEntry = (requiredEntry) => {
        if (Array.isArray(requiredEntry)) return requiredEntry;
        if (requiredEntry && typeof requiredEntry === 'object') {
            return Object.values(requiredEntry);
        }
        return [];
    };

    const normalizeEnabledActions = (enabledActions) => {
        if (enabledActions && typeof enabledActions === 'object') {
            return enabledActions;
        }
        return {};
    };

    const normalizeIntegrationDefinition = (integration, key) => ({
        ...integration,
        key: key || integration?.key || '',
        requiredEntry: normalizeRequiredEntry(integration?.requiredEntry),
        enabledActions: normalizeEnabledActions(integration?.enabledActions)
    });

    const getConfiguredIntegrationId = (configured) => (
        configured?.integration?.id || configured?.integrationId || configured?.integration
    );

    const normalizeConfiguredIntegration = (configured) => {
        const integrationData = configured?.integrationData && typeof configured.integrationData === 'object'
            ? configured.integrationData
            : {};

        return {
            ...configured,
            ...integrationData,
            enabledActions: integrationData.enabledActions || configured?.enabledActions || {},
            setupType: integrationData.setupType || configured?.setupType || ''
        };
    };

    useEffect(() => {
        if (selectedTenant?.id && accessToken) {
            loadIntegrations();
        }
    }, [selectedTenant, accessToken]);

    useEffect(() => {
        const setupEntry = selectedIntegration?.requiredEntry?.[selectedSetupIndex];
        setSetupValidationStatus({ type: 'idle', message: '' });
        setValidatedSetupObject(null);
        if (!setupEntry) {
            setSetupFormData({});
            setSetupOptions([]);
            return;
        }

        if (setupEntry.type === 'input') {
            const nextFormData = {};
            const fields = setupEntry.fields || {};

            Object.entries(fields).forEach(([fieldName, fieldConfig]) => {
                const existingValue = selectedConfiguredIntegration?.[fieldName];
                if (existingValue !== undefined && existingValue !== null) {
                    nextFormData[fieldName] = existingValue;
                    return;
                }

                if (fieldConfig.type === 'checkbox') {
                    nextFormData[fieldName] = false;
                } else {
                    nextFormData[fieldName] = '';
                }
            });

            setSetupFormData(nextFormData);
            setSetupOptions([]);

            if (selectedConfiguredIntegration?.id && selectedConfiguredIntegration?.setupType === 'input') {
                setSetupValidationStatus({
                    type: 'success',
                    message: 'Loaded from existing integration configuration.'
                });

                if (selectedConfiguredIntegration?.createdObject && typeof selectedConfiguredIntegration.createdObject === 'object') {
                    setValidatedSetupObject(selectedConfiguredIntegration.createdObject);
                }
            }

            return;
        }

        if (setupEntry.type === 'select' || setupEntry.type === 'selectAccount') {
            loadSelectOptionsFromEndpoint(setupEntry);
        }
    }, [selectedIntegration, selectedSetupIndex, selectedConfiguredIntegration]);

    useEffect(() => {
        const actionConfigs = selectedIntegration?.enabledActions || {};
        const configuredActions = selectedConfiguredIntegration?.enabledActions || {};
        const nextActionState = {};

        Object.entries(actionConfigs).forEach(([actionKey, actionConfig]) => {
            const configuredValue = configuredActions[actionKey];
            const configuredEnabled = typeof configuredValue === 'object'
                ? configuredValue.enabled
                : configuredValue;
            const configuredSelectedOptions = typeof configuredValue === 'object'
                ? configuredValue.selectedOptions
                : [];
            const configuredSelectedOption = typeof configuredValue === 'object'
                ? configuredValue.selectedOption
                : '';

            nextActionState[actionKey] = {
                enabled: configuredEnabled !== undefined
                    ? Boolean(configuredEnabled)
                    : Boolean(actionConfig?.default),
                selectedOptions: Array.isArray(configuredSelectedOptions)
                    ? configuredSelectedOptions.map((value) => String(value))
                    : [],
                selectedOption: configuredSelectedOption ? String(configuredSelectedOption) : ''
            };
        });

        setEnabledActionState(nextActionState);
    }, [selectedIntegration, selectedConfiguredIntegration]);

    useEffect(() => {
        const loadEnabledActionOptions = async () => {
            const actionConfigs = selectedIntegration?.enabledActions || {};
            const nextOptions = {};

            await Promise.all(Object.entries(actionConfigs).map(async ([actionKey, actionConfig]) => {
                if (!actionConfig?.options || !selectedTenant?.id || !accessToken) {
                    return;
                }

                const endpoint = resolveApiEndpoint(actionConfig.options, actionConfig.params || { tenantId: ':tenantId' });
                if (!endpoint) {
                    return;
                }

                try {
                    const response = await fetch(endpoint, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${accessToken}`
                        }
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to load ${actionConfig.label || actionKey} options (${response.status})`);
                    }

                    const data = await response.json();
                    nextOptions[actionKey] = normalizeSelectOptions(data, '${name} ${email}');
                } catch (err) {
                    setError(`Error: ${err.message}`);
                }
            }));

            setEnabledActionOptions(nextOptions);
        };

        loadEnabledActionOptions();
    }, [selectedIntegration, selectedTenant, accessToken]);

    const resolveParamValue = (value) => {
        if (value === ':tenantId') {
            return selectedTenant?.id;
        }

        if (typeof value === 'string' && value.startsWith(':')) {
            if (value === ':tenantId') {
                return selectedTenant?.id;
            }
            return undefined;
        }

        return value;
    };

    const resolveApiEndpoint = (apiEndpoint, params = {}) => {
        if (!apiEndpoint) return '';

        let resolved = String(apiEndpoint).trim();

        resolved = resolved.replaceAll('${process.env.CONSOLE_URL}', `${API_URL}console`);

        if (resolved.startsWith('/')) {
            resolved = `${API_URL}console${resolved}`;
        }

        const resolvedParams = {};
        Object.entries(params || {}).forEach(([key, value]) => {
            const resolvedValue = resolveParamValue(value);
            if (resolvedValue !== undefined && resolvedValue !== null && String(resolvedValue).trim() !== '') {
                resolvedParams[key] = resolvedValue;
            }
        });

        const usedParamKeys = new Set();

        Object.entries(resolvedParams).forEach(([key, value]) => {
            const token = `:${key}`;
            if (resolved.includes(token)) {
                resolved = resolved.replaceAll(token, encodeURIComponent(String(value)));
                usedParamKeys.add(key);
            }
        });

        const queryParams = Object.entries(resolvedParams).filter(([key]) => !usedParamKeys.has(key));
        if (queryParams.length > 0) {
            const queryString = queryParams
                .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
                .join('&');
            resolved += resolved.includes('?') ? `&${queryString}` : `?${queryString}`;
        }

        return resolved;
    };

    const getValueByPath = (source, path) => {
        if (!source || !path) return undefined;
        return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), source);
    };

    const renderDisplayMapping = (item, displayMapping) => {
        if (!displayMapping || typeof displayMapping !== 'string') return '';

        const rendered = displayMapping.replace(/\$\{([^}]+)\}/g, (_, keyPath) => {
            const value = getValueByPath(item, keyPath.trim());
            return value === undefined || value === null ? '' : String(value);
        });

        return rendered.replace(/\s+/g, ' ').trim();
    };

    const buildMappedLabel = (item, displayMapping) => {
        let mappedLabel = renderDisplayMapping(item, displayMapping);
        if (!mappedLabel) return '';

        if (item?.name && mappedLabel.startsWith(`${item.name} `)) {
            const trailing = mappedLabel.slice(item.name.length).trim();
            mappedLabel = trailing ? `${item.name} - ${trailing}` : item.name;
        }

        const includesUsernameTemplate = typeof displayMapping === 'string' && displayMapping.includes('${username}');
        if (includesUsernameTemplate || !item?.username) {
            return mappedLabel;
        }

        return `${mappedLabel} - ${item.username}`.replace(/\s+/g, ' ').trim();
    };

    const buildLabelFromStructure = (item, optionsStructure) => {
        if (!optionsStructure || typeof optionsStructure !== 'object' || !item || typeof item !== 'object') {
            return '';
        }

        const preferredKeys = ['name', 'provider', 'email', 'username'];
        const structureKeys = Object.keys(optionsStructure);
        const orderedKeys = [
            ...preferredKeys.filter((key) => structureKeys.includes(key)),
            ...structureKeys.filter((key) => !preferredKeys.includes(key))
        ];

        const values = orderedKeys
            .map((key) => item[key])
            .filter((value) => value !== undefined && value !== null && String(value).trim() !== '');

        if (values.length === 0) {
            return '';
        }

        return values.slice(0, 3).join(' - ');
    };

    const normalizeSelectOptions = (data, displayMapping, optionsStructure) => {
        if (Array.isArray(data)) {
            return data.map((item, index) => {
                const value = item?.id ?? item?.value ?? index;
                const mappedLabel = buildMappedLabel(item, displayMapping);
                const structuredLabel = buildLabelFromStructure(item, optionsStructure);
                const label = mappedLabel || structuredLabel || item?.label ||
                    item?.name ||
                    item?.email ||
                    item?.username ||
                    item?.number ||
                    item?.phoneNumber ||
                    `Option ${index + 1}`;

                return {
                    value,
                    label,
                    raw: item
                };
            });
        }

        if (data && typeof data === 'object') {
            const flattened = [];

            Object.entries(data).forEach(([groupKey, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((item, index) => {
                        const itemValue = item?.id ?? `${groupKey}-${index}`;
                        const mappedLabel = buildMappedLabel(item, displayMapping);
                        const structuredLabel = buildLabelFromStructure(item, optionsStructure);
                        const baseLabel = mappedLabel || structuredLabel || item?.label ||
                            item?.name ||
                            item?.email ||
                            item?.username ||
                            item?.number ||
                            item?.phoneNumber ||
                            `${groupKey} ${index + 1}`;

                        flattened.push({
                            value: itemValue,
                            label: `${groupKey}: ${baseLabel}`,
                            raw: item,
                            group: groupKey
                        });
                    });
                }
            });

            return flattened;
        }

        return [];
    };

    const loadSelectOptionsFromEndpoint = async (setupEntry) => {
        if (!selectedTenant?.id || !accessToken) return;

        const endpoint = resolveApiEndpoint(setupEntry?.options || setupEntry?.apiEndpoint, setupEntry?.params);
        if (!endpoint) {
            setSetupOptions([]);
            return;
        }

        try {
            const response = await fetch(endpoint, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to load select options (${response.status})`);
            }

            const data = await response.json();
            const normalizedOptions = normalizeSelectOptions(data, setupEntry?.displayMapping, setupEntry?.optionsStructure);
            setSetupOptions(normalizedOptions);

            const configuredSelectedId = selectedConfiguredIntegration?.selectedOptionId ||
                selectedConfiguredIntegration?.selectedAccountId ||
                selectedConfiguredIntegration?.accountId ||
                selectedConfiguredIntegration?.account?.id ||
                '';

            const configuredSelectedIds = selectedConfiguredIntegration?.selectedOptionIds ||
                selectedConfiguredIntegration?.selectedAccountIds ||
                [];

            setSetupFormData({
                selectedOptionId: configuredSelectedId ? String(configuredSelectedId) : '',
                selectedOptionIds: Array.isArray(configuredSelectedIds)
                    ? configuredSelectedIds.map((value) => String(value))
                    : []
            });
        } catch (err) {
            setSetupOptions([]);
            setError(`Error: ${err.message}`);
        }
    };

    const loadIntegrations = async () => {
        setIsLoading(true);
        setError(null);
        setSuccess('');

        let availableIntegrations = [];
        let hasError = false;

        await getAllIntegrations(
            accessToken,
            (data) => {
                const source = data?.integrationList && typeof data.integrationList === 'object'
                    ? data.integrationList
                    : data;

                const normalizedIntegrations = (Array.isArray(source)
                    ? source.map((integration, index) => normalizeIntegrationDefinition(
                        integration,
                        integration?.key || `integration-${integration?.id || index}`
                    ))
                    : Object.entries(source || {}).map(([key, integration]) => (
                        normalizeIntegrationDefinition(integration, key)
                    )))
                    .filter((integration) => integration && (integration.id !== undefined || integration.name))
                    .sort((a, b) => (a.id || 0) - (b.id || 0));
                availableIntegrations = normalizedIntegrations;
                setAllIntegrations(normalizedIntegrations);
            },
            (err) => {
                hasError = true;
                setError(err);
            }
        );

        if (!selectedTenant?.id || hasError) {
            setIsLoading(false);
            return;
        }

        await getIntegrations(
            selectedTenant.id,
            accessToken,
            (data) => {
                setConfiguredIntegrations(Array.isArray(data) ? data : []);

                if (selectedIntegration) {
                    const stillExists = availableIntegrations.some(
                        (integration) => String(integration.id) === String(selectedIntegration.id)
                    );
                    if (!stillExists) {
                        setSelectedIntegration(null);
                        setSelectedConfiguredIntegration(null);
                    }
                }
            },
            (err) => {
                hasError = true;
                setError(err);
            }
        );

        setIsLoading(false);
    };

    const integrationsById = allIntegrations.reduce((acc, integration) => {
        acc[String(integration.id)] = integration;
        return acc;
    }, {});

    const getActiveSetupEntry = () => selectedIntegration?.requiredEntry?.[selectedSetupIndex] || null;
    const isEditingExistingIntegration = Boolean(selectedConfiguredIntegration?.id);

    const isSetupComplete = () => {
        const setupEntry = getActiveSetupEntry();
        if (!setupEntry) return true;

        if (setupEntry.type === 'select' || setupEntry.type === 'selectAccount') {
            if (setupEntry.optionsType === 'multi-select') {
                return Array.isArray(setupFormData.selectedOptionIds) && setupFormData.selectedOptionIds.length > 0;
            }
            return Boolean(setupFormData.selectedOptionId);
        }

        if (setupEntry.type === 'input') {
            return setupValidationStatus.type === 'success';
        }

        return true;
    };

    const handleSelectAvailableIntegration = (integration) => {
        setSelectedIntegration(integration);
        setSelectedConfiguredIntegration(null);
        setSelectedSetupIndex(0);
        setError(null);
        setSuccess('');
    };

    const handleSelectConfiguredIntegration = (configured) => {
        const configuredIntegrationId = getConfiguredIntegrationId(configured);
        const normalizedConfigured = normalizeConfiguredIntegration(configured);

        const matchedIntegration = integrationsById[String(configuredIntegrationId)] || {
            id: configuredIntegrationId,
            key: `integration-${configuredIntegrationId}`,
            name: `Integration #${configuredIntegrationId}`,
            description: 'Details unavailable for this integration.',
            symbol: '🔌',
            requiredEntry: []
        };

        const configuredSetupType = normalizedConfigured?.setupType;
        const matchedSetupIndex = Array.isArray(matchedIntegration.requiredEntry)
            ? matchedIntegration.requiredEntry.findIndex((entry) => {
                if (configuredSetupType === 'select') {
                    return entry?.type === 'select' || entry?.type === 'selectAccount';
                }
                return entry?.type === configuredSetupType;
            })
            : -1;

        setSelectedIntegration(matchedIntegration);
        setSelectedConfiguredIntegration(normalizedConfigured);
        setSelectedSetupIndex(matchedSetupIndex >= 0 ? matchedSetupIndex : 0);
        setError(null);
        setSuccess('');
    };

    const handleSetupFieldChange = (fieldName, value) => {
        if (isEditingExistingIntegration) {
            return;
        }

        setSetupValidationStatus({ type: 'idle', message: '' });
        setValidatedSetupObject(null);
        setSetupFormData((prev) => ({
            ...prev,
            [fieldName]: value
        }));
    };

    const handleValidateInputSetup = async () => {
        const setupEntry = getActiveSetupEntry();
        if (!setupEntry || setupEntry.type !== 'input') return;
        if (isEditingExistingIntegration) return;

        setError(null);
        setSuccess('');

        if (!isSetupValid()) {
            setSetupValidationStatus({
                type: 'error',
                message: 'Please complete all required fields before validating.'
            });
            return;
        }

        const endpoint = resolveApiEndpoint(setupEntry.apiEndpoint, setupEntry.params);
        if (!endpoint) {
            setSetupValidationStatus({
                type: 'error',
                message: 'No validation endpoint is configured for this setup option.'
            });
            return;
        }

        setIsValidatingEntry(true);

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(setupFormData)
            });

            let responseBody = null;
            try {
                responseBody = await response.json();
            } catch (_) {
                responseBody = null;
            }

            if (!response.ok) {
                const errorMessage = responseBody?.message || responseBody?.error || `Validation failed (${response.status}).`;
                setSetupValidationStatus({
                    type: 'error',
                    message: errorMessage
                });
                setValidatedSetupObject(null);
                return;
            }

            const successMessage = responseBody?.message || 'Input validated successfully.';
            setSetupValidationStatus({
                type: 'success',
                message: successMessage
            });

            const createdObject = responseBody?.data && typeof responseBody.data === 'object'
                ? responseBody.data
                : (responseBody && typeof responseBody === 'object' ? responseBody : null);

            if (createdObject && (createdObject.id !== undefined || createdObject._id !== undefined)) {
                setValidatedSetupObject(createdObject);
            } else {
                setValidatedSetupObject(null);
            }
        } catch (err) {
            setSetupValidationStatus({
                type: 'error',
                message: err.message || 'Unable to validate setup input.'
            });
            setValidatedSetupObject(null);
        } finally {
            setIsValidatingEntry(false);
        }
    };

    const handleEnabledActionToggle = (actionKey, isEnabled) => {
        setEnabledActionState((prev) => ({
            ...prev,
            [actionKey]: {
                ...(prev[actionKey] || {}),
                enabled: isEnabled
            }
        }));
    };

    const handleEnabledActionOptionChange = (actionKey, value, isMultiSelect) => {
        setEnabledActionState((prev) => {
            const current = prev[actionKey] || {};
            return {
                ...prev,
                [actionKey]: {
                    ...current,
                    selectedOptions: isMultiSelect ? value : (current.selectedOptions || []),
                    selectedOption: !isMultiSelect ? value : (current.selectedOption || '')
                }
            };
        });
    };

    const isSetupValid = () => {
        const setupEntry = getActiveSetupEntry();
        if (!setupEntry) return true;

        if (setupEntry.type === 'select' || setupEntry.type === 'selectAccount') {
            if (setupEntry.optionsType === 'multi-select') {
                return Array.isArray(setupFormData.selectedOptionIds) && setupFormData.selectedOptionIds.length > 0;
            }
            return Boolean(setupFormData.selectedOptionId);
        }

        if (setupEntry.type === 'input') {
            const fields = setupEntry.fields || {};
            return Object.entries(fields).every(([fieldName, fieldConfig]) => {
                if (fieldConfig.type === 'checkbox') return true;
                return String(setupFormData[fieldName] ?? '').trim() !== '';
            });
        }

        return true;
    };

    const isEnabledActionsValid = () => {
        const enabledActionsConfig = selectedIntegration?.enabledActions || {};

        return Object.entries(enabledActionsConfig).every(([actionKey, actionConfig]) => {
            const actionState = enabledActionState[actionKey] || {};
            const isActionEnabled = actionState.enabled !== undefined
                ? Boolean(actionState.enabled)
                : Boolean(actionConfig?.default);

            if (!isActionEnabled) {
                return true;
            }

            if (actionConfig.optionsType === 'multi-select') {
                return Array.isArray(actionState.selectedOptions) && actionState.selectedOptions.length > 0;
            }

            if (actionConfig.optionsType === 'select') {
                return Boolean(actionState.selectedOption);
            }

            return true;
        });
    };

    const buildPayloadFromSetup = () => {
        if (!selectedIntegration) return null;

        const setupEntry = getActiveSetupEntry();
        const setupType = setupEntry?.type || 'none';
        let payloadBody = {};

        if (setupType === 'select' || setupType === 'selectAccount') {
            if (setupEntry?.optionsType === 'multi-select') {
                const selectedIds = Array.isArray(setupFormData.selectedOptionIds)
                    ? setupFormData.selectedOptionIds
                    : [];

                const selectedOptions = setupOptions.filter((option) => selectedIds.includes(String(option.value)));

                if (selectedOptions.length === 0) {
                    return null;
                }

                payloadBody = {
                    selectedOptionIds: selectedOptions.map((option) => option.value),
                    selectedOptionLabels: selectedOptions.map((option) => option.label),
                    selectedOptions: selectedOptions.map((option) => option.raw || null)
                };
            } else {
            const selectedOption = setupOptions.find(
                (option) => String(option.value) === String(setupFormData.selectedOptionId)
            );

            if (!selectedOption) {
                return null;
            }

            payloadBody = {
                selectedOptionId: selectedOption.value,
                selectedOptionLabel: selectedOption.label,
                selectedOptionGroup: selectedOption.group || null,
                selectedOption: selectedOption.raw || null
            };
            }
        } else if (setupType === 'input') {
            const fields = setupEntry.fields || {};
            payloadBody = Object.keys(fields).reduce((acc, fieldName) => {
                const fieldConfig = fields[fieldName];
                let fieldValue = setupFormData[fieldName];

                if (fieldConfig.type === 'number' && fieldValue !== '') {
                    const parsedValue = Number(fieldValue);
                    fieldValue = Number.isNaN(parsedValue) ? fieldValue : parsedValue;
                }

                if (fieldConfig.type === 'checkbox') {
                    fieldValue = Boolean(fieldValue);
                }

                acc[fieldName] = fieldValue;
                return acc;
            }, {});

            if (validatedSetupObject) {
                payloadBody.createdObject = validatedSetupObject;
                payloadBody.createdObjectId = validatedSetupObject.id ?? validatedSetupObject._id;
            }
        }

        const integrationPayload = {
            integration: selectedIntegration.id,
            setupType,
            ...payloadBody
        };

        const enabledActionsConfig = selectedIntegration.enabledActions || {};
        const enabledActionsPayload = Object.entries(enabledActionsConfig).reduce((acc, [actionKey, actionConfig]) => {
            const actionState = enabledActionState[actionKey] || {};
            const actionEnabled = actionState.enabled !== undefined
                ? Boolean(actionState.enabled)
                : Boolean(actionConfig?.default);

            acc[actionKey] = {
                enabled: actionEnabled
            };

            if (actionConfig.optionsType === 'multi-select') {
                acc[actionKey].selectedOptions = Array.isArray(actionState.selectedOptions)
                    ? actionState.selectedOptions
                    : [];
            }

            if (actionConfig.optionsType === 'select') {
                acc[actionKey].selectedOption = actionState.selectedOption || '';
            }

            return acc;
        }, {});

        if (Object.keys(enabledActionsPayload).length > 0) {
            integrationPayload.enabledActions = enabledActionsPayload;
        }

        if (selectedConfiguredIntegration?.id) {
            integrationPayload.id = selectedConfiguredIntegration.id;
        }

        return integrationPayload;
    };

    const handleSaveIntegration = async () => {
        setError(null);
        setSuccess('');

        if (!isSetupValid()) {
            setError('Please complete all required setup fields before saving this integration.');
            return;
        }

        if (!isEnabledActionsValid()) {
            setError('Please select at least one option for each enabled multi-select action before saving.');
            return;
        }

        const payload = buildPayloadFromSetup();
        if (!payload) {
            setError('Unable to build integration payload from the selected setup.');
            return;
        }

        setIsSaving(true);
        const isUpdate = Boolean(payload.id);

        if (isUpdate) {
            await updateIntegrations(
                selectedTenant.id,
                payload,
                accessToken,
                async () => {
                    setSuccess('Integration updated successfully.');
                    await loadIntegrations();
                },
                (err) => {
                    setError(err);
                }
            );
        } else {
            await addIntegration(
                selectedTenant.id,
                payload,
                accessToken,
                async () => {
                    setSuccess('Integration created successfully.');
                    await loadIntegrations();
                },
                (err) => {
                    setError(err);
                }
            );
        }

        setIsSaving(false);
    };

    const configuredWithDetails = configuredIntegrations
        .map((configured) => ({
            ...configured,
            details: integrationsById[String(getConfiguredIntegrationId(configured))] || null
        }))
        .sort((a, b) => {
            const aName = a.details?.name || '';
            const bName = b.details?.name || '';
            return aName.localeCompare(bName);
        });

    const configuredIntegrationIds = new Set(
        configuredIntegrations
            .map((configured) => getConfiguredIntegrationId(configured))
            .filter((integrationId) => integrationId !== undefined && integrationId !== null)
            .map((integrationId) => String(integrationId))
    );

    const availableIntegrationsForCreate = allIntegrations.filter(
        (integration) => !configuredIntegrationIds.has(String(integration.id))
    );

    return (
        <div className="communication-channels">
            <div className="channels-sidebar">
                <div className="sidebar-header">
                    <h3>My Integrations</h3>
                </div>

                <div className="sidebar-content">
                    {isLoading ? (
                        <div className="loading-container">
                            <p>Loading integrations...</p>
                        </div>
                    ) : configuredWithDetails.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-icon">🔌</div>
                            <p className="empty-state-text">No integrations are configured yet.</p>
                        </div>
                    ) : (
                        <div className="channel-list">
                            {configuredWithDetails.map((configured) => {
                                const integration = configured.details;
                                const integrationId = integration?.id || getConfiguredIntegrationId(configured);
                                const configuredRecordId = configured?.id || `${integrationId}-${configured?.integrationData?.name || configured?.name || 'configured'}`;
                                const configuredName = configured?.integrationData?.name || configured?.name || '';
                                const configuredDisplayName = configured?.integrationData?.displayName || '';
                                const integrationDisplayName = configuredName && configuredDisplayName
                                    ? `${configuredName} - ${configuredDisplayName}`
                                    : (configuredName || configuredDisplayName || 'Configured Integration');
                                const isIntegrationActive = configured?.integrationData?.active === true;
                                return (
                                    <div
                                        key={configuredRecordId}
                                        className={`channel-item ${selectedConfiguredIntegration && String(selectedConfiguredIntegration.id) === String(configured.id) ? 'selected' : ''}`}
                                        onClick={() => handleSelectConfiguredIntegration(configured)}
                                    >
                                        <div className="channel-item-content">
                                            <div className="channel-icon integration-channel-icon">
                                                <IntegrationSymbol symbol={integration?.symbol} logo={integration?.logo} />
                                            </div>
                                            <div className="channel-info">
                                                <p className="channel-name">{integration?.name || `Integration #${integrationId}`}</p>
                                                <p className="channel-detail">{integrationDisplayName}</p>
                                            </div>
                                            <div className="channel-status-icon">
                                                {isIntegrationActive
                                                    ? <FaCircleCheck className="integration-status-active" />
                                                    : <FaMinus className="integration-status-inactive" />}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className="channels-detail">
                <div className="detail-toolbar">
                    <div className="detail-toolbar-left">
                        <h3 className="detail-toolbar-title">
                            {selectedIntegration ? 'Integration Details' : 'Available Integrations'}
                        </h3>
                    </div>
                    <div className="detail-toolbar-right">
                        {selectedIntegration && (
                            <button
                                className="toolbar-btn toolbar-btn-save"
                                onClick={handleSaveIntegration}
                                disabled={!isSetupValid() || !isSetupComplete() || !isEnabledActionsValid() || isSaving}
                            >
                                {selectedConfiguredIntegration?.id ? <FaFloppyDisk className="toolbar-btn-icon" /> : <FaPlus className="toolbar-btn-icon" />}
                                {isSaving
                                    ? 'Saving...'
                                    : selectedConfiguredIntegration?.id
                                        ? 'Update Integration'
                                        : 'Add Integration'}
                            </button>
                        )}
                        <button
                            className="toolbar-btn"
                            onClick={() => {
                                setSelectedIntegration(null);
                                setSelectedConfiguredIntegration(null);
                                setSelectedSetupIndex(0);
                                setSetupValidationStatus({ type: 'idle', message: '' });
                                setEnabledActionState({});
                                setEnabledActionOptions({});
                            }}
                            disabled={!selectedIntegration}
                        >
                            <FaXmark className="toolbar-btn-icon" />
                            Close
                        </button>
                    </div>
                </div>

                <div className="detail-content">
                    {error && (
                        <div className="status-message error">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="status-message success">
                            {success}
                        </div>
                    )}

                    {selectedIntegration ? (
                        <div className="detail-form detail-form-full">
                            <div className="detail-section">
                                <div className="integration-readonly-card">
                                    <div className="integration-readonly-symbol">
                                        <IntegrationSymbol symbol={selectedIntegration.symbol} logo={selectedIntegration.logo} />
                                    </div>
                                    <p className="integration-readonly-name">{selectedIntegration.name || 'Unnamed Integration'}</p>
                                    <p className="integration-readonly-description">{selectedIntegration.description || 'No description available.'}</p>
                                </div>

                                <div className="integration-instructions-section">
                                    <h4 className="console-form-section-title">Instructions</h4>
                                    <p>{selectedIntegration.instructions || 'Follow the setup steps below to configure this integration.'}</p>
                                </div>

                                {Array.isArray(selectedIntegration.requiredEntry) && selectedIntegration.requiredEntry.length > 0 && (
                                    <>
                                        <div className="integration-entry-section">
                                            <h4 className="console-form-section-title">Setup Method</h4>
                                            <div className="integration-setup-choice-buttons">
                                                {selectedIntegration.requiredEntry.map((entry, index) => (
                                                    <button
                                                        key={`${selectedIntegration.id}-setup-${index}`}
                                                        type="button"
                                                        onClick={() => setSelectedSetupIndex(index)}
                                                        className={`integration-choice-btn ${selectedSetupIndex === index ? 'active' : ''}`}
                                                        disabled={isEditingExistingIntegration}
                                                    >
                                                        {entry.label || `Setup Option ${index + 1}`}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {(getActiveSetupEntry()?.type === 'select' || getActiveSetupEntry()?.type === 'selectAccount') && (
                                            <div className="form-group integration-entry-fields">
                                                <label>{getActiveSetupEntry()?.label || 'Select Account'}</label>
                                                {getActiveSetupEntry()?.optionsType === 'multi-select' ? (
                                                    <div className="integration-multi-checkbox-list">
                                                        {setupOptions.map((option) => {
                                                            const optionValue = String(option.value);
                                                            const selectedValues = setupFormData.selectedOptionIds || [];
                                                            const isChecked = selectedValues.includes(optionValue);

                                                            return (
                                                                <label
                                                                    key={`${option.group || 'option'}-${option.value}`}
                                                                    className="integration-multi-checkbox-item"
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        disabled={isEditingExistingIntegration}
                                                                        onChange={(e) => {
                                                                            const nextValues = e.target.checked
                                                                                ? [...selectedValues, optionValue]
                                                                                : selectedValues.filter((value) => value !== optionValue);
                                                                            handleSetupFieldChange('selectedOptionIds', nextValues);
                                                                        }}
                                                                    />
                                                                    <span>{option.label}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <select
                                                        value={setupFormData.selectedOptionId || ''}
                                                        disabled={isEditingExistingIntegration}
                                                        onChange={(e) => handleSetupFieldChange('selectedOptionId', e.target.value)}
                                                    >
                                                        <option value="">Choose an option</option>
                                                        {setupOptions.map((option) => (
                                                            <option key={`${option.group || 'option'}-${option.value}`} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        )}

                                        {getActiveSetupEntry()?.type === 'input' && (
                                            <div className="integration-entry-fields">
                                                <div className="detail-form-three-column">
                                                    {Object.entries(getActiveSetupEntry()?.fields || {}).map(([fieldName, fieldConfig]) => (
                                                        <div className="form-group" key={`${selectedIntegration.id}-${fieldName}`}>
                                                            <label>{fieldConfig.label || fieldName}</label>

                                                            {fieldConfig.type === 'checkbox' ? (
                                                                <div className="checkbox-group">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={Boolean(setupFormData[fieldName])}
                                                                        disabled={isEditingExistingIntegration}
                                                                        onChange={(e) => handleSetupFieldChange(fieldName, e.target.checked)}
                                                                    />
                                                                    <label>{fieldConfig.label || fieldName}</label>
                                                                </div>
                                                            ) : (
                                                                <input
                                                                    type={fieldConfig.type || 'text'}
                                                                    value={setupFormData[fieldName] ?? ''}
                                                                    disabled={isEditingExistingIntegration}
                                                                    onChange={(e) => handleSetupFieldChange(fieldName, e.target.value)}
                                                                    placeholder={fieldConfig.placeholder || ''}
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="integration-validate-row">
                                                    <button
                                                        type="button"
                                                        className="toolbar-btn toolbar-btn-save integration-validate-btn"
                                                        onClick={handleValidateInputSetup}
                                                        disabled={isEditingExistingIntegration || !isSetupValid() || isValidatingEntry}
                                                    >
                                                        {isValidatingEntry ? 'Validating...' : 'Validate Entry'}
                                                    </button>
                                                </div>

                                                {setupValidationStatus.type === 'success' && (
                                                    <div className="status-message success integration-validation-message">
                                                        {setupValidationStatus.message}
                                                    </div>
                                                )}

                                                {setupValidationStatus.type === 'error' && (
                                                    <div className="status-message error integration-validation-message">
                                                        {setupValidationStatus.message}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {isSetupComplete() && Object.keys(selectedIntegration.enabledActions || {}).length > 0 && (
                                            <div className="integration-enabled-actions-section">
                                                <h4 className="console-form-section-title">Enabled Actions</h4>

                                                {Object.entries(selectedIntegration.enabledActions || {}).map(([actionKey, actionConfig]) => {
                                                    const actionState = enabledActionState[actionKey] || {
                                                        enabled: Boolean(actionConfig.default),
                                                        selectedOptions: [],
                                                        selectedOption: ''
                                                    };
                                                    const actionOptions = enabledActionOptions[actionKey] || [];

                                                    return (
                                                        <div className="integration-action-card" key={`${selectedIntegration.id}-${actionKey}`}>
                                                            <div className="form-group-checkbox">
                                                                <label>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={Boolean(actionState.enabled)}
                                                                        onChange={(e) => handleEnabledActionToggle(actionKey, e.target.checked)}
                                                                    />
                                                                    <span>{actionConfig.label || actionKey}</span>
                                                                </label>
                                                            </div>

                                                            {actionConfig.description && (
                                                                <p className="integration-action-description">{actionConfig.description}</p>
                                                            )}

                                                            {Boolean(actionState.enabled) && actionConfig.optionsType === 'multi-select' && actionOptions.length > 0 && (
                                                                <div className="form-group">
                                                                    <label>Select Options</label>
                                                                    <div className="integration-multi-checkbox-list">
                                                                        {actionOptions.map((option) => {
                                                                            const optionValue = String(option.value);
                                                                            const selectedValues = actionState.selectedOptions || [];
                                                                            const isChecked = selectedValues.includes(optionValue);

                                                                            return (
                                                                                <label
                                                                                    key={`${actionKey}-${option.value}`}
                                                                                    className="integration-multi-checkbox-item"
                                                                                >
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={isChecked}
                                                                                        onChange={(e) => {
                                                                                            const nextValues = e.target.checked
                                                                                                ? [...selectedValues, optionValue]
                                                                                                : selectedValues.filter((value) => value !== optionValue);
                                                                                            handleEnabledActionOptionChange(actionKey, nextValues, true);
                                                                                        }}
                                                                                    />
                                                                                    <span>{option.label}</span>
                                                                                </label>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                    {(actionState.selectedOptions || []).length === 0 && (
                                                                        <p className="integration-action-required-note">Select at least one option.</p>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {Boolean(actionState.enabled) && actionConfig.optionsType === 'select' && actionOptions.length > 0 && (
                                                                <div className="form-group">
                                                                    <label>Select Option</label>
                                                                    <select
                                                                        value={actionState.selectedOption || ''}
                                                                        onChange={(e) => handleEnabledActionOptionChange(actionKey, e.target.value, false)}
                                                                    >
                                                                        <option value="">Choose an option</option>
                                                                        {actionOptions.map((option) => (
                                                                            <option key={`${actionKey}-${option.value}`} value={String(option.value)}>
                                                                                {option.label}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </>
                                )}

                                {Array.isArray(selectedIntegration.requiredEntry) && selectedIntegration.requiredEntry.length === 0 && (
                                    <div className="status-message error">
                                        This integration does not include setup options in the response. Expected requiredEntry with a select and/or input definition.
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <IntegrationsHome
                            integrations={availableIntegrationsForCreate}
                            onSelectIntegration={handleSelectAvailableIntegration}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const ClientBilling = () => {
    return (
        <div className="billing-content">
            <h2>Billing Information</h2>
            <p>View and manage your billing information here.</p>
        </div>
    );
};

const SupportComponent = () => {
    return (
        <div className="support-content">
            <h2>Support</h2>
            <p>Contact support or access help resources here.</p>
        </div>
    )
};

const ClientUserManagement = ({selectedTenant, accessToken}) => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditingUser, setIsEditingUser] = useState(false);
    const [formData, setFormData] = useState(null);
    const [emailCheckResult, setEmailCheckResult] = useState(null);
    const [showUserExistsWarning, setShowUserExistsWarning] = useState(false);
    const [userExistsConfirmed, setUserExistsConfirmed] = useState(false);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [emailAlreadyInTenant, setEmailAlreadyInTenant] = useState(false);
    const [createError, setCreateError] = useState(null);
    const [updateError, setUpdateError] = useState(null);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = () => {
        setIsLoading(true);
        getTenantUsers(selectedTenant.id, accessToken, (data) => {
            setUsers(data);
            setIsLoading(false);
        }, (error) => {
            console.error(error);
            setIsLoading(false);
        });
    };

    const handleSelectUser = (user) => {
        setSelectedUser(user);
        setShowCreateUser(false);
        setIsEditingUser(false);
        setFormData(null);
        setEmailCheckResult(null);
        setShowUserExistsWarning(false);
        setUserExistsConfirmed(false);
        setEmailAlreadyInTenant(false);
        setCreateError(null);
        setUpdateError(null);
    };

    const handleEditUser = () => {
        setIsEditingUser(true);
        setUpdateError(null);
        setFormData({
            permissions: {
                communicationChannels: selectedUser.permissions?.communicationChannels || false,
                communicationQueue: selectedUser.permissions?.communicationQueue || false,
                context: selectedUser.permissions?.context || false,
                history: selectedUser.permissions?.history || false,
                integrations: selectedUser.permissions?.integrations || false,
                billing: selectedUser.permissions?.billing || false,
                userManagement: selectedUser.permissions?.userManagement || false
            }
        });
    };

    const handleCancelEdit = () => {
        setIsEditingUser(false);
        setFormData(null);
        setUpdateError(null);
    };

    const handleUpdateUser = () => {
        if (!selectedUser?.permissions?.id) {
            setUpdateError('Unable to update: Permissions ID not found.');
            return;
        }

        setUpdateError(null);
        
        // Convert camelCase to snake_case for backend
        const permissionsData = {
            id: selectedUser.permissions.id,
            history: formData.permissions.history,
            context: formData.permissions.context,
            user_management: formData.permissions.userManagement,
            communication_channels: formData.permissions.communicationChannels,
            communication_queue: formData.permissions.communicationQueue,
            integrations: formData.permissions.integrations,
            billing: formData.permissions.billing
        };

        updateUserPermissions(
            accessToken,
            selectedUser.tenantId,
            permissionsData,
            (data) => {
                console.log('Permissions updated:', data);
                setIsEditingUser(false);
                setFormData(null);
                setUpdateError(null);
                loadUsers();
                // Update the selectedUser with new permissions
                setSelectedUser({
                    ...selectedUser,
                    permissions: {
                        ...selectedUser.permissions,
                        ...formData.permissions
                    }
                });
            },
            (error) => {
                console.error('Failed to update permissions:', error);
                setUpdateError(error);
            }
        );
    };

    const handleRemoveUser = () => {
        if (!selectedUser) return;

        // Check if this is the last user with User Management privilege
        if (selectedUser.permissions?.userManagement) {
            const otherAdmins = users.filter(user => 
                user.id !== selectedUser.id && 
                user.permissions?.userManagement === true
            );
            
            if (otherAdmins.length === 0) {
                setUpdateError('Cannot remove this user: They are the only one with User Management privileges.');
                return;
            }
        }

        const confirmMessage = `Are you sure you want to remove ${selectedUser.firstName} ${selectedUser.lastName} from this tenant?`;
        if (!window.confirm(confirmMessage)) {
            return;
        }

        setUpdateError(null);
        removeUser(
            accessToken,
            selectedTenant.id,
            selectedUser.id,
            (data) => {
                console.log('User removed:', data);
                setSelectedUser(null);
                setIsEditingUser(false);
                setFormData(null);
                setUpdateError(null);
                loadUsers();
            },
            (error) => {
                console.error('Failed to remove user:', error);
                setUpdateError(error);
            }
        );
    };

    const handleCreateUserClick = () => {
        setShowCreateUser(true);
        setSelectedUser(null);
        setIsEditingUser(false);
        setEmailCheckResult(null);
        setShowUserExistsWarning(false);
        setUserExistsConfirmed(false);
        setEmailAlreadyInTenant(false);
        setCreateError(null);
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            permissions: {
                communicationChannels: false,
                communicationQueue: false,
                context: false,
                history: false,
                integrations: false,
                billing: false,
                userManagement: false
            }
        });
    };

    const handleCancelCreate = () => {
        setShowCreateUser(false);
        setFormData(null);
        setEmailCheckResult(null);
        setShowUserExistsWarning(false);
        setUserExistsConfirmed(false);
        setEmailAlreadyInTenant(false);
        setCreateError(null);
    };

    const handleEmailCheck = (email) => {
        if (!email || !email.includes('@')) {
            setEmailCheckResult(null);
            setShowUserExistsWarning(false);
            setEmailAlreadyInTenant(false);
            return;
        }

        // Check if email already exists in current tenant's user list
        const existingUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());
        if (existingUser) {
            setEmailAlreadyInTenant(true);
            setEmailCheckResult(null);
            setShowUserExistsWarning(false);
            setUserExistsConfirmed(false);
            return;
        }

        setEmailAlreadyInTenant(false);
        setIsCheckingEmail(true);
        checkUserEmail(
            email,
            accessToken,
            (userData) => {
                // User exists (200 response)
                setEmailCheckResult(userData);
                setShowUserExistsWarning(true);
                setUserExistsConfirmed(false);
                setIsCheckingEmail(false);
            },
            (error) => {
                // User doesn't exist (404 or other error)
                setEmailCheckResult(null);
                setShowUserExistsWarning(false);
                setUserExistsConfirmed(false);
                setIsCheckingEmail(false);
            }
        );
    };

    const handleConfirmAddExistingUser = () => {
        if (emailCheckResult) {
            setFormData({
                ...formData,
                firstName: emailCheckResult.firstName || '',
                lastName: emailCheckResult.lastName || '',
                phone: emailCheckResult.phone || ''
            });
            setUserExistsConfirmed(true);
            setShowUserExistsWarning(false);
        }
    };

    const handleFormChange = (field, value) => {
        if (field.startsWith('permissions.')) {
            const permissionKey = field.split('.')[1];
            setFormData({
                ...formData,
                permissions: {
                    ...formData.permissions,
                    [permissionKey]: value
                }
            });
        } else {
            setFormData({
                ...formData,
                [field]: value
            });
            
            // Check email when it changes
            if (field === 'email' && value) {
                handleEmailCheck(value);
            }
        }
    };

    const handleCreateUser = () => {
        setCreateError(null);
        createUser(
            accessToken,
            selectedTenant.id,
            formData,
            (data) => {
                console.log('User created:', data);
                setShowCreateUser(false);
                setFormData(null);
                setEmailCheckResult(null);
                setShowUserExistsWarning(false);
                setUserExistsConfirmed(false);
                setEmailAlreadyInTenant(false);
                setCreateError(null);
                loadUsers();
            },
            (error) => {
                console.error('Failed to create user:', error);
                setCreateError(error);
            }
        );
    };

    const isFormValid = () => {
        if (!formData) return false;
        
        // When editing, only validate permissions
        if (isEditingUser) {
            // Don't allow removing last admin
            if (isLastUserManagementAdmin()) return false;
            
            const hasPermission = Object.values(formData.permissions).some(perm => perm === true);
            return hasPermission;
        }
        
        // When creating, validate all fields
        // Check if email is valid
        if (!formData.email || !formData.email.includes('@')) return false;
        
        // Check if email already exists in tenant
        if (emailAlreadyInTenant) return false;
        
        // Check if basic info is filled
        if (!formData.firstName || !formData.lastName) return false;
        
        // Check if at least one permission is selected
        const hasPermission = Object.values(formData.permissions).some(perm => perm === true);
        if (!hasPermission) return false;
        
        // If user exists and hasn't been confirmed, don't allow creation
        if (showUserExistsWarning && !userExistsConfirmed) return false;
        
        return true;
    };

    const hasValidEmail = () => {
        return formData?.email && formData.email.includes('@') && !showUserExistsWarning && !emailAlreadyInTenant;
    };

    const isLastUserManagementAdmin = () => {
        if (!isEditingUser || !selectedUser || !formData) return false;
        
        // Check if current user has userManagement enabled
        const currentUserHasPermission = selectedUser.permissions?.userManagement;
        if (!currentUserHasPermission) return false;
        
        // Check if trying to remove userManagement
        const tryingToRemove = !formData.permissions.userManagement;
        if (!tryingToRemove) return false;
        
        // Count how many other users have userManagement
        const otherAdmins = users.filter(user => 
            user.id !== selectedUser.id && 
            user.permissions?.userManagement === true
        );
        
        return otherAdmins.length === 0;
    };

    const isOnlyUserManagementAdmin = () => {
        if (!selectedUser) return false;
        
        // Check if current user has userManagement enabled
        const currentUserHasPermission = selectedUser.permissions?.userManagement;
        if (!currentUserHasPermission) return false;
        
        // Count how many other users have userManagement
        const otherAdmins = users.filter(user => 
            user.id !== selectedUser.id && 
            user.permissions?.userManagement === true
        );
        
        return otherAdmins.length === 0;
    };

    return (
        <div className="communication-channels">
            {/* Left Sidebar */}
            <div className="channels-sidebar">
                <div className="sidebar-header">
                    <h3>Users</h3>
                </div>
                <div className="sidebar-content">
                    {isLoading ? (
                        <div className="loading-container">
                            <p>Loading users...</p>
                        </div>
                    ) : (
                    <div className="channel-section">
                        <div className="channel-list">
                            {users && users.length > 0 ? (
                                users.map((user) => (
                                    <div
                                        key={user.id}
                                        className={`channel-item user-channel-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                                        onClick={() => handleSelectUser(user)}
                                    >
                                        <div className="channel-item-content user-channel-item-content">
                                            <FaUser className="channel-icon" />
                                            <div className="channel-info">
                                                <p className="channel-name">
                                                    {user.firstName} {user.lastName}
                                                </p>
                                                <p className="channel-detail">
                                                    {user.email}
                                                </p>
                                                {user.phone && (
                                                    <p className="channel-detail">
                                                        {formatPhoneNumber(user.phone)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {user.emailVerifiedOn ? (
                                            <FaCircleCheck className="channel-status-icon status-verified" />
                                        ) : (
                                            <FaCircleExclamation className="channel-status-icon status-unverified" />
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="empty-state">
                                    <FaUser className="empty-state-icon" />
                                    <p className="empty-state-text">No users found</p>
                                </div>
                            )}
                        </div>
                    </div>
                    )}
                </div>
            </div>

            {/* Right Detail Panel */}
            <div className="channels-detail">
                <div className="detail-toolbar">
                    <div className="detail-toolbar-left">
                        <h3 style={{ margin: 0, color: 'var(--primary-color)' }}>
                            {showCreateUser ? 'Add New User' : selectedUser ? 'User Details' : 'User Management'}
                        </h3>
                    </div>
                    <div className="detail-toolbar-right">
                        {showCreateUser ? (
                            <>
                                <button 
                                    className="toolbar-btn"
                                    onClick={handleCancelCreate}
                                >
                                    <FaXmark className="toolbar-btn-icon" />
                                    Cancel
                                </button>
                                <button 
                                    className="toolbar-btn toolbar-btn-save"
                                    onClick={handleCreateUser}
                                    disabled={!isFormValid()}
                                >
                                    <FaFloppyDisk className="toolbar-btn-icon" />
                                    Add User
                                </button>
                            </>
                        ) : selectedUser ? (
                            <>
                                <button 
                                    className="toolbar-btn"
                                    onClick={() => {
                                        setSelectedUser(null);
                                        setIsEditingUser(false);
                                        setFormData(null);
                                        setUpdateError(null);
                                    }}
                                >
                                    <FaXmark className="toolbar-btn-icon" />
                                    Close
                                </button>
                                {isEditingUser ? (
                                    <>
                                        <button 
                                            className="toolbar-btn"
                                            onClick={handleCancelEdit}
                                        >
                                            <FaXmark className="toolbar-btn-icon" />
                                            Cancel Edit
                                        </button>
                                        <button 
                                            className="toolbar-btn toolbar-btn-save"
                                            onClick={handleUpdateUser}
                                            disabled={!isFormValid()}
                                        >
                                            <FaFloppyDisk className="toolbar-btn-icon" />
                                            Update User
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button 
                                            className="toolbar-btn toolbar-btn-highlight-edit"
                                            onClick={handleEditUser}
                                        >
                                            <FaPencil className="toolbar-btn-icon" />
                                            Edit
                                        </button>
                                        {!isOnlyUserManagementAdmin() && (
                                            <button 
                                                className="toolbar-btn toolbar-btn-delete"
                                                onClick={handleRemoveUser}
                                            >
                                                <FaTrash className="toolbar-btn-icon" />
                                                Remove User
                                            </button>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <button 
                                className="toolbar-btn toolbar-btn-save"
                                onClick={handleCreateUserClick}
                            >
                                <FaPlus className="toolbar-btn-icon" />
                                Add User
                            </button>
                        )}
                    </div>
                </div>

                {/* Error Banner */}
                {createError && showCreateUser && (
                    <div style={{
                        padding: '1rem 1.5rem',
                        background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.2) 0%, rgba(220, 53, 69, 0.1) 100%)',
                        borderBottom: '2px solid #dc3545',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        color: '#721c24'
                    }}>
                        <FaCircleExclamation style={{ fontSize: '1.3rem', color: '#dc3545', minWidth: '1.3rem' }} />
                        <div style={{ flex: 1 }}>
                            <strong>Error Creating User:</strong> {createError}
                        </div>
                        <button 
                            onClick={() => setCreateError(null)}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#dc3545',
                                cursor: 'pointer',
                                fontSize: '1.2rem',
                                padding: '0.25rem'
                            }}
                        >
                            <FaXmark />
                        </button>
                    </div>
                )}

                {/* Update Error Banner */}
                {updateError && selectedUser && isEditingUser && (
                    <div style={{
                        padding: '1rem 1.5rem',
                        background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.2) 0%, rgba(220, 53, 69, 0.1) 100%)',
                        borderBottom: '2px solid #dc3545',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        color: '#721c24'
                    }}>
                        <FaCircleExclamation style={{ fontSize: '1.3rem', color: '#dc3545', minWidth: '1.3rem' }} />
                        <div style={{ flex: 1 }}>
                            <strong>Error Updating Permissions:</strong> {updateError}
                        </div>
                        <button 
                            onClick={() => setUpdateError(null)}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#dc3545',
                                cursor: 'pointer',
                                fontSize: '1.2rem',
                                padding: '0.25rem'
                            }}
                        >
                            <FaXmark />
                        </button>
                    </div>
                )}

                <div className="detail-content">
                    {showCreateUser ? (
                        <>
                            {/* Add User Form - 2 Column Layout */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'start' }}>
                                {/* Left Column - User Information */}
                                <div className="detail-section">
                                    <h4 style={{ color: 'var(--primary-color)', marginBottom: '1rem', fontSize: '1.1rem' }}>User Information</h4>
                                    <div className="detail-form">
                                        {/* Row 1: Email and Phone */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div className="form-group">
                                                <label>Email Address *</label>
                                                <input
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={(e) => handleFormChange('email', e.target.value)}
                                                    placeholder="Enter email address"
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Phone Number</label>
                                                <input
                                                    type="tel"
                                                    value={formatPhoneInput(formData.phone || '')}
                                                    onChange={(e) => {
                                                        const formatted = formatPhoneInput(e.target.value);
                                                        const parsed = parsePhoneNumber(formatted);
                                                        handleFormChange('phone', parsed);
                                                    }}
                                                    placeholder="(###) ###-####"
                                                    disabled={!hasValidEmail() || userExistsConfirmed}
                                                />
                                            </div>
                                        </div>

                                        {/* User Already in Tenant Warning */}
                                        {emailAlreadyInTenant && (
                                            <div className="user-exists-warning" style={{ 
                                                background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.2) 0%, rgba(220, 53, 69, 0.1) 100%)',
                                                borderColor: '#dc3545'
                                            }}>
                                                <FaCircleExclamation className="warning-icon" style={{ color: '#dc3545' }} />
                                                <div style={{ flex: 1 }}>
                                                    <strong>User Already in Tenant:</strong> A user with email <strong>{formData.email}</strong> already exists in your tenant. You cannot add duplicate users.
                                                </div>
                                            </div>
                                        )}

                                        {/* User Exists Warning Banner */}
                                        {showUserExistsWarning && emailCheckResult && !emailAlreadyInTenant && (
                                            <div className="user-exists-warning">
                                                <FaCircleExclamation className="warning-icon" />
                                                <div style={{ flex: 1 }}>
                                                    <strong>User Already Exists:</strong> A user with email <strong>{formData.email}</strong> already exists in the system.
                                                    <div style={{ marginTop: '0.5rem' }}>
                                                        Would you like to add this user to your tenant?
                                                    </div>
                                                </div>
                                                <button 
                                                    className="toolbar-btn toolbar-btn-save"
                                                    onClick={handleConfirmAddExistingUser}
                                                    style={{ marginLeft: '1rem' }}
                                                >
                                                    <FaCircleCheck className="toolbar-btn-icon" />
                                                    Yes, Add User
                                                </button>
                                            </div>
                                        )}

                                        {/* Row 2: First and Last Name */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div className="form-group">
                                                <label>First Name *</label>
                                                <input
                                                    type="text"
                                                    value={formData.firstName}
                                                    onChange={(e) => handleFormChange('firstName', e.target.value)}
                                                    placeholder="Enter first name"
                                                    disabled={!hasValidEmail() || userExistsConfirmed}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Last Name *</label>
                                                <input
                                                    type="text"
                                                    value={formData.lastName}
                                                    onChange={(e) => handleFormChange('lastName', e.target.value)}
                                                    placeholder="Enter last name"
                                                    disabled={!hasValidEmail() || userExistsConfirmed}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column - Permissions */}
                                <div className="detail-section">
                                    <h4 style={{ color: 'var(--primary-color)', marginBottom: '1rem', fontSize: '1.1rem' }}>
                                        Permissions *
                                    </h4>
                                    <p style={{ color: 'var(--secondary-color)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                                        Select at least one permission for this user.
                                    </p>
                                    <div className="detail-form">
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.permissions.communicationChannels}
                                                    onChange={(e) => handleFormChange('permissions.communicationChannels', e.target.checked)}
                                                    disabled={!hasValidEmail() && !userExistsConfirmed}
                                                />
                                                <span>Communication Channels</span>
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.permissions.communicationQueue}
                                                    onChange={(e) => handleFormChange('permissions.communicationQueue', e.target.checked)}
                                                    disabled={!hasValidEmail() && !userExistsConfirmed}
                                                />
                                                <span>Communication Queue</span>
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.permissions.context}
                                                    onChange={(e) => handleFormChange('permissions.context', e.target.checked)}
                                                    disabled={!hasValidEmail() && !userExistsConfirmed}
                                                />
                                                <span>Context</span>
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.permissions.history}
                                                    onChange={(e) => handleFormChange('permissions.history', e.target.checked)}
                                                    disabled={!hasValidEmail() && !userExistsConfirmed}
                                                />
                                                <span>History</span>
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.permissions.integrations}
                                                    onChange={(e) => handleFormChange('permissions.integrations', e.target.checked)}
                                                    disabled={!hasValidEmail() && !userExistsConfirmed}
                                                />
                                                <span>Integrations</span>
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.permissions.billing}
                                                    onChange={(e) => handleFormChange('permissions.billing', e.target.checked)}
                                                    disabled={!hasValidEmail() && !userExistsConfirmed}
                                                />
                                                <span>Billing</span>
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.permissions.userManagement}
                                                    onChange={(e) => handleFormChange('permissions.userManagement', e.target.checked)}
                                                    disabled={!hasValidEmail() && !userExistsConfirmed}
                                                />
                                                <span>User Management</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : selectedUser ? (
                        <>
                            {/* User Details View - 2 Column Layout */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'start' }}>
                                {/* Left Column - User Information */}
                                <div className="detail-section">
                                    <h4 style={{ color: 'var(--primary-color)', marginBottom: '1rem', fontSize: '1.1rem' }}>User Information</h4>
                                    <div className="detail-form">
                                        {!selectedUser.emailVerifiedOn && (
                                            <div className="verification-warning" style={{ marginBottom: '1rem' }}>
                                                <FaCircleExclamation className="warning-icon" />
                                                <span><strong>Unverified:</strong> This user has not verified their email address.</span>
                                            </div>
                                        )}
                                        
                                        {/* Row 1: Email and Phone */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div className="form-group">
                                                <label>Email</label>
                                                <input
                                                    type="email"
                                                    value={selectedUser.email || ''}
                                                    disabled
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Phone</label>
                                                <input
                                                    type="tel"
                                                    value={formatPhoneNumber(selectedUser.phone || '')}
                                                    disabled
                                                />
                                            </div>
                                        </div>

                                        {/* Row 2: First and Last Name */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div className="form-group">
                                                <label>First Name</label>
                                                <input
                                                    type="text"
                                                    value={selectedUser.firstName || ''}
                                                    disabled
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Last Name</label>
                                                <input
                                                    type="text"
                                                    value={selectedUser.lastName || ''}
                                                    disabled
                                                />
                                            </div>
                                        </div>

                                        {selectedUser.emailVerifiedOn && (
                                            <div className="form-group">
                                                <label>Email Verified On</label>
                                                <input
                                                    type="text"
                                                    value={new Date(selectedUser.emailVerifiedOn).toLocaleDateString()}
                                                    disabled
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Right Column - Permissions */}
                                <div className="detail-section">
                                    <h4 style={{ color: 'var(--primary-color)', marginBottom: '1rem', fontSize: '1.1rem' }}>Permissions</h4>
                                    
                                    {/* Warning: Cannot remove last admin */}
                                    {isEditingUser && isLastUserManagementAdmin() && (
                                        <div style={{
                                            padding: '0.75rem 1rem',
                                            background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.15) 0%, rgba(220, 53, 69, 0.05) 100%)',
                                            border: '2px solid #dc3545',
                                            borderRadius: '6px',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '0.75rem',
                                            marginBottom: '1rem',
                                            color: '#721c24'
                                        }}>
                                            <FaCircleExclamation style={{ fontSize: '1.1rem', color: '#dc3545', marginTop: '0.1rem', minWidth: '1.1rem' }} />
                                            <div style={{ flex: 1, fontSize: '0.9rem' }}>
                                                <strong>Cannot Remove User Management:</strong> This user is the only one with User Management privileges. At least one user must have this permission to manage users.
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="detail-form">
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={isEditingUser ? formData?.permissions?.communicationChannels || false : selectedUser.permissions?.communicationChannels || false}
                                                    onChange={(e) => isEditingUser && handleFormChange('permissions.communicationChannels', e.target.checked)}
                                                    disabled={!isEditingUser}
                                                />
                                                <span>Communication Channels</span>
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={isEditingUser ? formData?.permissions?.communicationQueue || false : selectedUser.permissions?.communicationQueue || false}
                                                    onChange={(e) => isEditingUser && handleFormChange('permissions.communicationQueue', e.target.checked)}
                                                    disabled={!isEditingUser}
                                                />
                                                <span>Communication Queue</span>
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={isEditingUser ? formData?.permissions?.context || false : selectedUser.permissions?.context || false}
                                                    onChange={(e) => isEditingUser && handleFormChange('permissions.context', e.target.checked)}
                                                    disabled={!isEditingUser}
                                                />
                                                <span>Context</span>
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={isEditingUser ? formData?.permissions?.history || false : selectedUser.permissions?.history || false}
                                                    onChange={(e) => isEditingUser && handleFormChange('permissions.history', e.target.checked)}
                                                    disabled={!isEditingUser}
                                                />
                                                <span>History</span>
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={isEditingUser ? formData?.permissions?.integrations || false : selectedUser.permissions?.integrations || false}
                                                    onChange={(e) => isEditingUser && handleFormChange('permissions.integrations', e.target.checked)}
                                                    disabled={!isEditingUser}
                                                />
                                                <span>Integrations</span>
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={isEditingUser ? formData?.permissions?.billing || false : selectedUser.permissions?.billing || false}
                                                    onChange={(e) => isEditingUser && handleFormChange('permissions.billing', e.target.checked)}
                                                    disabled={!isEditingUser}
                                                />
                                                <span>Billing</span>
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={isEditingUser ? formData?.permissions?.userManagement || false : selectedUser.permissions?.userManagement || false}
                                                    onChange={(e) => isEditingUser && handleFormChange('permissions.userManagement', e.target.checked)}
                                                    disabled={!isEditingUser}
                                                />
                                                <span>User Management</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="channels-detail-empty">
                            <FaUser className="empty-icon" />
                            <h3>No User Selected</h3>
                            <p>Select a user from the list to view details or create a new user.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ClientConsoleContent = ({selectedTenant, userData, menuSelection, accessToken}) => {
    const clientCommunicationChannelsProps = {
        selectedTenant,
        accessToken
    };

    const clientHistoryProps = {
        userData,
        selectedTenant,
        accessToken
    };

    const clientContextProps = {
        selectedTenant,
        accessToken
    };

    const clientUserManagementProps = {
        selectedTenant,
        accessToken
    };

    const clientCommunicationQueueProps = {
        selectedTenant,
        userData,
        accessToken
    }

    const clientIntegrationsProps = {
        selectedTenant,
        accessToken
    };

    return (
        <div className="console-content">
            {menuSelection === 'home' && <ClientHome />}
            {menuSelection === 'communicationChannels' && <ClientCommunicationChannels {...clientCommunicationChannelsProps} />}
            {menuSelection === 'context' && <ClientContext {...clientContextProps} />}
            {menuSelection === 'history' && <ClientHistory {...clientHistoryProps} />}
            {menuSelection === 'communicationQueue' && <ClientCommunicationQueue {...clientCommunicationQueueProps} />}
            {menuSelection === 'integrations' && <ClientIntegrations {...clientIntegrationsProps} />}
            {menuSelection === 'billing' && <ClientBilling />}
            {menuSelection === 'userManagement' && <ClientUserManagement {...clientUserManagementProps} />}
            {menuSelection === 'support' && <SupportComponent />}
        </div>
    );
};

const ClientConsoleDisplayWindow = ({userData, accessToken, logoutUser}) => {
    const [selectedTenant, setSelectedTenant] = useState(userData?.tenants?.[0] || '');
    const [menuOptions, setMenuOptions] = useState([]);
    const [menuSelection, setMenuSelection] = useState('home');
    const [showAccountSettings, setShowAccountSettings] = useState(false);

    useEffect(() => {
        if (userData?.tenants?.length > 0 && !selectedTenant) {
            setSelectedTenant(userData.tenants[0]);
        }
    }, [userData, selectedTenant]);

    useEffect(() => {
        if(selectedTenant.permissions){
            let menuOptions = [{selection:'home', displayName:'Home'}];
            if(selectedTenant.permissions.communicationChannels) menuOptions.push({selection:'communicationChannels', displayName:'Channels'});
            if(selectedTenant.permissions.context) menuOptions.push({selection:'context', displayName:'Context'});
            if(selectedTenant.permissions.communicationQueue) menuOptions.push({selection:'communicationQueue', displayName:'Outbound Queue'});
            if(selectedTenant.permissions.history) menuOptions.push({selection:'history', displayName:'History'});
            if(selectedTenant.permissions.integrations) menuOptions.push({selection:'integrations', displayName:'Integrations'});
            if(selectedTenant.permissions.billing) menuOptions.push({selection:'billing', displayName:'Billing'});
            if(selectedTenant.permissions.userManagement) menuOptions.push({selection:'userManagement', displayName:'User Management'});
            menuOptions.push({selection:'support', displayName:'Support'});
            setMenuOptions(menuOptions);
        };
    }, [selectedTenant]);

    const consoleHeaderProps = {
        userData,
        selectedTenant,
        setSelectedTenant,
        onOpenAccountSettings: () => setShowAccountSettings(true),
        logoutUser
    };

    const menubarProps = {
        selectedTenant,
        menuOptions,
        menuSelection,
        setMenuSelection
    };

    const contentProps = {
        userData,
        selectedTenant,
        menuSelection,
        accessToken
    };

    return (
        <div className="console-display-window">
            <ClientConsoleHeader {...consoleHeaderProps } />
            <div className="console-body">
                <ClientConsoleMenubar {...menubarProps} />
                <ClientConsoleContent {...contentProps} />
            </div>
            {showAccountSettings && (
                <AccountSettingsModal 
                    userData={userData}
                    onClose={() => setShowAccountSettings(false)}
                />
            )}
        </div>
    );
};

const ClientConsole = () => {
    const [ userData, setUserData ] = useState(null);
    const [ userError, setUserError ] = useState(null);
    const [ accessToken, setApiToken ] = useState(null);

    useEffect(() => {
    if(localStorage['apiToken'] && localStorage['userID'] && !userData){
        console.log(`Attempting auto-login with stored credentials`,localStorage['apiToken'], localStorage['userID']);
        const accessToken = localStorage.getItem('apiToken')
        const userID = localStorage.getItem('userID');
        autoLoginUser(userID, accessToken, setLoginData, setUserError);
    }
    },[]);

    const logoutUser = () => {
        localStorage.removeItem('apiToken');
        localStorage.removeItem('userID');
        setUserData(null);
    };

    const setLoginData = (data) => {
        setUserData(data);
        setApiToken(data.user.accessToken);
        setUserError(null);
        localStorage.setItem('apiToken', data.user.accessToken);
        localStorage.setItem('userID', data.user.id);
    };

    const handleUserLogin = (credentials) => {
        loginUser(credentials, setLoginData, setUserError);
    };

    const handleGoogleLogin = async (googleCredential) => {
        try {
            const response = await ClientInstanceService.googleLogin({ credential: googleCredential });
            if(response.status >= 200 && response.status < 300) {
                setUserData(response.data);
            } else {
                setUserError(`Error: Received status code ${response.status}`);
            }
        } catch(error) {
            setUserError(`Error: ${error.message}`);
        }
    };

    const clientDisplayWindowProps = {
        userData,
        accessToken,
        logoutUser
    };

    const clientLoginProps = {
        handleUserLogin,
        handleGoogleLogin,
        userError,
    };

    return (
        <div className="client-console">
            { userData ? 
                <ClientConsoleDisplayWindow {...clientDisplayWindowProps} />
            : 
                <ClientLogin {...clientLoginProps} />
            }
        </div>
    );
};

export default ClientConsole;